// High-Lift multifidelity V5 surface (FXC-9009 / blown-lift × Tailor).
//
// Harry's #5 ("ADB-Gen with this model") + #6 ("Tailor next runs folded in"):
// a two-pane workbench that puts the NATIVE high-lift foil design tool beside
// the existing ADB explorer.
//
//   LEFT  — the native foil: <FoilGeometryCanvas> drawn over a <FlowVisLayer>
//           (geometry on top, LIC flow behind) + <FoilControls>, all driven by
//           useFoilDesign. One spine ▶ Solve forks the live point; the solve
//           panel proposes the Tailor next-N queue.
//   RIGHT — <AdbExplorerSurface {...props}/> verbatim (it owns the ADB grid +
//           Tailor fit + recommendations + residual/raw evidence modes). Above
//           it a compact trust summary from fetchHighliftTrustMap
//           (alpha_deg × flap_deploy_deg).
//
// SOLVE → GRID REFRESH SEAM: AdbExplorerSurface owns its own react-query under
// the key ["v5-adb-explorer", projectId, branch] (see ThreadV5AdbExplorer.tsx).
// After a fork lands we invalidate exactly that key via the shared QueryClient,
// so the explorer refetches the grid without us reaching into its internals or
// reimplementing the fusion. The forked cases ingest asynchronously (case_poller
// pulls CL/CD into the run nodes ~1 min each), so we invalidate immediately AND
// schedule a couple of delayed invalidations to pick up the ingested rows.

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import {
  fetchHighliftFlowMesh,
  fetchHighliftPriors,
  fetchHighliftSolveProgress,
  listHighliftGeometries,
  persistWebGpuRun,
  saveHighliftGeometry,
  solveHighliftRuns,
  type SavedGeometry,
  type SolvePoint,
  type SolveProgress,
  type SolveResponse,
} from "../../api/highlift";
import {
  fetchHighliftRecommendations,
  fetchHighliftTrustMap,
  registerHighliftComposite,
  type RegisterResponse,
} from "../../api/highliftComposite";
import {
  controlHighliftCampaign,
  createHighliftCampaign,
  fetchHighliftCampaign,
  listHighliftCampaigns,
  type CampaignControlAction,
  type CampaignDetail,
} from "../../api/highliftCampaign";
import type { CompositeClassification, RunRecommendation, TrustMapResponse } from "../../api/composite";
import { FlowVisLayer, type FlowMesh } from "../highlift/foil/FlowVisLayer";
import { ConvergencePlayer } from "../highlift/ConvergencePlayer";
import { HighliftForceArrows, type ElementForceArrow } from "../highlift/foil/HighliftForceArrows";
import { useHighliftTheme, type HighliftPalette } from "../highlift/foil/useHighliftTheme";
import { useTheme } from "../../context/ThemeContext";
import { FoilGeometryCanvas, type FoilGeometryCanvasHandle } from "../highlift/foil/FoilGeometryCanvas";
import { useFoilDesign, type FoilSetters } from "../highlift/foil/useFoilDesign";
import { FoilPicker } from "../highlift/foil/FoilPicker";
import { KNOB_REGISTRY, KNOB_GROUPS, knobBounds } from "../highlift/foil/knobRegistry";
import { loadNN } from "../highlift/flowfield-nn/nnInfer";
import { buildSweepInputs, buildConfiguration, blowingOf, meshBoundaryCheck, elementsOf, FOIL_VIEW, letterbox, zoomLevel, isHomeView, framePoints, isDefaultCollectionId, mainCoordsDigest, gapMetrics as computeGapMetrics, addPlainFlap as addPlainFlapWithOpts, conditionFoil, DEFAULT_MAIN_ELEMENT_ID, DEFAULT_HIGH_LIFT_AIRFOIL } from "../highlift/geometry";
import type { AirfoilCoords, CoveCutouts, HighLiftAirfoil, V2, ViewRect, ElementKind } from "../highlift/geometry";
import { animateMotionAxis } from "../highlift/geometry/motionGapCheck";
import { sectionHingeXc, type SlottedSection } from "../highlift/geometry/slottedSection";
import { clearAppliedSeed, consumeSectionSeed, normalizeTwoBodySection, readAppliedSeed, writeAppliedSeed, type SectionSeed, type TwoBodySection } from "../highlift/foil/sectionSeed";
import { createWebGpuSolver, type WebGpuSolveInput, type WebGpuSession } from "../highlift/webgpu-solver/backend";
import { buildWebGpuRunRequest, webgpuLineageSlug } from "../highlift/webgpu-solver/webgpuPersist";
import { refineEnabled, refineSecondOrder, flowMeshToVtk, type RefineResult } from "../highlift/webgpu-solver/refineClient";
import { ransV100Enabled, ransV100BaseUrl, buildRansV100Contours, solveRansV100, fetchRansV100Flow, fetchRansV100FrameSteps, type RansV100Result } from "../highlift/ransV100Client";
import { buildRansV100RunRequest, ransV100LineageSlug } from "../highlift/ransV100Persist";
// HighliftDockLayout + TileBoard are RETIRED from the render tree by the redesign IA
// (the stage spine replaces the draggable dock + tile board). The DockPanel/DockSide/
// DockControls + WorkbenchTile TYPES are still used by the retained data definitions
// (designPanels / panelHomes / dataTiles) + the dockControlsRef the spine's Save/Reset
// chips drive, so we keep the type imports; the components themselves are not rendered.
import { ShapePanel, controlSurfaceFlapSpec, type ControlSurface } from "../highlift/workbench/ShapePanel";
import { InverseDesignSection } from "../highlift/foil/InverseDesignSection";
import { RiggingTable } from "../highlift/workbench/RiggingTable";
import { startHighliftTour, TutorialButton, emptyTourLive, type HighliftTourState } from "../highlift/tour/highliftTour";
import { AxisTable, type FillCampaignSpec } from "../highlift/workbench/AxisTable";
import type { SweepLiveValues } from "../highlift/workbench/campaignMath";
import { ExploreBoard } from "../highlift/workbench/ExploreBoard";
import { WhyTerminal, type WhyPoint } from "../highlift/workbench/WhyTerminal";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { boardAxisSpecs, normalizeLanes, solveFocusToSelectedPoint, EXPLORE_PRESETS, type ExploreCardId } from "../highlift/workbench/exploreBoard";
import { campaignReplaySteps, type ReplayStep } from "../highlift/workbench/campaignMath";
import {
  patchStudyCursor,
  useEnsureStudyCursor,
  useStudyCursorValue,
  type StudySelectedPoint,
} from "../adaptive-sampling/surfaces/useSliceState";
import { TrustSurfacePanel, type SurfaceAxisOption } from "./TrustSurfacePanel";
import { deriveClMax, type PolarSeries, type ClMaxMarker } from "../highlift/foil/PolarPlot";
import { fetchRunTablePaginated, type RunTableData } from "../../api/runTable";
import { WingViewer3D } from "./WingViewer3D";
import type { WingPlanform } from "../../api/wing";
import type { SurfaceProps } from "./ThreadV5Surfaces";

/** Output the high-lift composite is registered + trusted/recommended on. */
const HIGHLIFT_QUANTITY = "CL";
/**
 * Selectable response-surface axes for the Tailor trust surface, sourced from the design knob
 * registry (FXC-9009 geometry-breadth tranche). Every value is a canonical composite identity axis
 * the backend accepts; the user can put any of them — including a per-element geometry shape DOF —
 * on the x/y of the trust surface. `reynolds` is excluded: it is a held condition pinned on the
 * `re_chord_millions` axis, not a plot axis (selecting the registry key would 400).
 */
const TRUST_AXIS_OPTIONS: SurfaceAxisOption[] = KNOB_REGISTRY
  .filter((k) => k.key !== "reynolds")
  .map((k) => ({ value: k.key, label: k.label, group: k.group }));
/** Default size of the Tailor proposal the queue requests. */
const SOLVE_NEXT_BUDGET = 6;
/** Bounds for the editable N stepper. */
const QUEUE_N_MIN = 1;
const QUEUE_N_MAX = 12;
/** RHS-map overrides for the High-Lift surface: the physical-model comparison
 *  (RANS vs Flexfoil) PLUS the engine's signature confidence-weighted fusion.
 *  `rom` is the flexfoil prior (inviscid at M>=0.15); `cfd` is the 2D RANS.
 *
 *  FXC-9009 Phase 4.11: the fused layer is now ON so the RHS shows the Tailor
 *  confidence-weighted blend (the dashed Fused curve — where the models agree it
 *  tracks them, where they disagree it ghosts toward the more-confident source),
 *  not just the bare physical lines. GP stays OFF here: this surface compares the
 *  two REAL physical models, and the honest engine label (FXC-9009 Phase 4) is
 *  single-fidelity GP rather than co-kriging, so a "GP" line would add a third,
 *  non-physical curve without earning its keep on this demo view. */
/** Delayed grid refetches (ms) after a fork so async case ingestion shows up. */
const POST_SOLVE_REFRESH_DELAYS = [4000, 15000, 45000];
/** Fill-campaign poll cadence (ms) while a campaign runs — fast enough that batches feel
 *  live, slow enough not to load the single-worker backend (FXC-9009 fill-campaign). */
const CAMPAIGN_POLL_MS = 4000;

type SolveState = "idle" | "solving" | "done" | "error";

/** Redesign IA stage spine: Rig (section rigging) → Flow (tailor-read flow vis) →
 *  Push (tailor-target/queue) → Trust (ADB / trust surface / polars / lineage) →
 *  Span (loft to 3D). The Trust + Span stages are the data-facing ones (mode='data'). */
type HlwStage = "rig" | "explore";

/** A Tailor-proposed run sitting in the editable queue, with its rationale. */
type QueueStatus = "queued" | "solving" | "done" | "failed";
interface QueuedPoint {
  id: string;
  alpha_deg: number;
  mach: number;
  reynolds: number;
  temperature?: number;
  flap_deploy_deg: number;
  /** Tailor acquisition score + why this point was proposed. */
  score: number;
  purpose: string;
  reasons: string[];
  classification?: CompositeClassification;
  status: QueueStatus;
  note?: string;
}

let QUEUE_SEQ = 0;

/** Map a Tailor recommendation → an editable queue row (falling back to the live
 *  flow conditions for any axis the acquisition didn't pin). */
function recToQueued(
  rec: RunRecommendation,
  fallback: { mach: number; reynolds: number; temperature: number; flap_deploy_deg: number },
): QueuedPoint {
  const cond = rec.condition;
  const num = (k: string, d: number) => {
    const v = Number(cond[k]);
    return Number.isFinite(v) ? v : d;
  };
  const alpha = Number(cond.alpha_deg ?? cond.alpha);
  return {
    id: `q${++QUEUE_SEQ}`,
    alpha_deg: Number.isFinite(alpha) ? alpha : 0,
    mach: num("mach", fallback.mach),
    reynolds: num("reynolds", fallback.reynolds),
    temperature: fallback.temperature,
    flap_deploy_deg: num("flap_deploy_deg", fallback.flap_deploy_deg),
    score: typeof rec.score === "number" ? rec.score : 0,
    purpose: rec.purpose ?? "envelope",
    reasons: Array.isArray(rec.reasons) ? rec.reasons : [],
    classification: rec.classification,
    status: "queued",
  };
}

/** Build the `design` record the register/solve calls seed the section from.
 *  The server flattens this shape for provenance / mesh hashes, so keep the
 *  live high-lift geometry under the same keys as `HighLiftAirfoil`. */
function designPayload(config: HighLiftAirfoil): Record<string, unknown> {
  const { main, design, propeller } = config;
  return {
    main: {
      chord: main.chord,
      bluntThickness: main.bluntThickness,
    },
    design,
    propeller,
  };
}

/** Linear interp of a tabulated (ascending-x, LE→TE) foil surface at chordwise x (clamped). */
function surfaceYAt(surface: V2[], x: number): number {
  if (surface.length === 0) return 0;
  const first = surface[0]!;
  const last = surface[surface.length - 1]!;
  if (x <= first[0]) return first[1];
  if (x >= last[0]) return last[1];
  for (let i = 1; i < surface.length; i++) {
    const a = surface[i - 1]!;
    const b = surface[i]!;
    if (x <= b[0]) return a[1] + ((x - a[0]) / (b[0] - a[0] || 1)) * (b[1] - a[1]);
  }
  return last[1];
}

/**
 * Degenerate "sealed at the blunt TE" cove for a BARE single-element design (the aircraft
 * clean-foil seed intake): all three cove cuts coincide at the aftmost station whose local
 * thickness still hosts a real back wall (>= 3x the blunt-TE thickness). `buildMainWing` then
 * renders the FULL foil, blunt-truncated there exactly like every other element's TE treatment
 * — no visible cove, no aft cutout — while staying an ordinary {@link CoveCutouts} the user can
 * immediately drag forward (or supersede with a plain flap, which re-cuts the main with its own
 * cove). Coincident cuts are the proven sealed-flap configuration (`coveFromHinge(x, 0)`).
 * Exported for the seed-intake geometry test (sectionSeed.test.ts).
 */
export function sealedTeCove(coords: AirfoilCoords, bluntThickness: number): CoveCutouts {
  const bt = Math.max(bluntThickness, 1e-4);
  let cut = 0.9; // pathological fallback: a foil thinner than 3·bt everywhere aft of mid-chord
  for (let i = 800; i >= 0; i--) {
    const x = 0.5 + (0.5 * i) / 800;
    if (surfaceYAt(coords.upper, x) - surfaceYAt(coords.lower, x) >= 3 * bt) {
      cut = x;
      break;
    }
  }
  return { coveX: cut, upperCutX: cut, lowerCutX: cut };
}

// ---------------------------------------------------------------------------
// Aircraft CS seed → design builders (the three seed modes)
// ---------------------------------------------------------------------------

/** The ways a CS-seeded section can present in the workbench (mode control in the Library panel,
 *  next to the seeded-from note): `aileron` = the DEFAULT "as designed" build (the clean aircraft
 *  foil + a plain-flap aileron from the seed rig), `clean` = the bare uncut foil only, `highlift` =
 *  the clean aircraft section carrying the native multi-element high-lift system (cove + vane +
 *  slotted flaperon + propeller), `twobody` = the READ-ONLY exact cut contours over the hero (Solve
 *  stays parametric). */
export type CsSeedMode = "aileron" | "clean" | "highlift" | "twobody";

/**
 * The plain-flap (aileron) options a seed's rig maps to — ONE home for the maths both the
 * as-designed build and the v1 no-cleanFoil fallback share (do not re-derive):
 *   - hinge x/c: the publisher's exact rig value, else recovered from the section's own contours,
 *     else the plain-flap default — clamped to the band the flaperon cut handles allow.
 *   - deflection: the seed's own (TE-down positive), non-finite ⇒ 0.
 *   - cove depth: the rig slot gap (TRUE mm → model units via unitMm) in chords; absent ⇒ the
 *     factory default (the caller omits the key).
 * Exported for the seed-intake tests (sectionSeed.test.ts).
 */
export function seedFlapOpts(section: SlottedSection): {
  hingeX: number;
  deflectionDeg: number;
  coveDepth?: number;
  gap?: number;
  concentricNose: boolean;
} {
  const hingeXc = section.rig?.xc ?? sectionHingeXc(section) ?? 0.7;
  const hingeX = Math.min(0.92, Math.max(0.5, Number.isFinite(hingeXc) ? hingeXc : 0.7));
  // Rig slot width in the section's own model units (true mm / unitMm), for the flap GAP below.
  const gapMm = section.rig?.gapMm;
  const unitMm = section.rig?.unitMm;
  const gapModel =
    typeof gapMm === "number" && Number.isFinite(gapMm)
      ? gapMm / (typeof unitMm === "number" && Number.isFinite(unitMm) && unitMm > 0 ? unitMm : 1)
      : undefined;
  // Cove depth = the CHORDWISE reach of the cove past the hinge, measured from the section's
  // EXACT cove-carved wing contour: its aftmost point is the upper cove lip. (The earlier
  // mapping used the rig's slot GAP here - a few mm of SLOT WIDTH is ~0.4%c, and a near-zero
  // cove leaves the parametric flap's slope-matched nose spline nowhere to nest: it bulged
  // out of the profile. Slot width and cove depth are different quantities; the exact contour
  // carries the real one.)
  // Cove depth measured from the EXACT wing contour's cove lip. NOTE: for the concentric aileron the
  // main cove geometry is the hinge-concentric SPIRAL (built from the section's own split radii — the
  // nearest-surface distances from the hinge), so it is ALWAYS a generous rounded pocket by
  // construction, deep enough to nest the round nose (the "deep cove, not a slit" the studio picture
  // shows). `coveDepth` here therefore no longer drives the concentric pocket shape — it is retained
  // only as the legacy hint (clamped to the parametric band) and is inert on the concentric path. The
  // clamp is raised to 0.18c so a genuinely deep measured cove is allowed rather than pinned at 0.1c.
  let coveDepth: number | undefined;
  const wing = section.elements?.find((e) => e.id === "wing")?.contour;
  if (wing && wing.length >= 8 && Number.isFinite(section.chord) && section.chord > 0) {
    let xMin = Infinity;
    let xMax = -Infinity;
    for (const p of wing) {
      const x = p?.[0];
      if (!Number.isFinite(x)) continue;
      if (x < xMin) xMin = x;
      if (x > xMax) xMax = x;
    }
    if (xMax > xMin) {
      const lipXc = (xMax - xMin) / section.chord;
      if (Number.isFinite(lipXc) && lipXc > hingeX) {
        coveDepth = Math.min(0.18, Math.max(0.015, lipXc - hingeX));
      }
    }
  }
  // Slot width (main -> flap clearance) IS the rig's designed gap. This is a DIFFERENT quantity
  // from the cove depth above: gapMm is how far the flap nose sits off the main cove wall, so it
  // maps onto the flap's `gap`, opening a real slot instead of burying the nose in the main (the
  // zero-gap / 5%-overlap "collision" the sealed default produced). Clamped to a sane slot band.
  const gapC =
    gapModel !== undefined && section.chord > 0
      ? Math.min(0.03, Math.max(0, gapModel / section.chord))
      : undefined;
  // The concentric aileron's slot clearance is the cove SPIRAL radius (r + gap) minus the flap-nose
  // spiral radius (r), i.e. exactly `gap` at every deflection. Floor it at a small positive value so a
  // rig with no published slot still opens a real, constant clearance (a zero-gap concentric cove
  // would touch the nose everywhere).
  const CONCENTRIC_GAP_MIN = 0.01;
  const gapConcentric = Math.max(CONCENTRIC_GAP_MIN, gapC ?? 0);
  return {
    hingeX,
    deflectionDeg: Number.isFinite(section.deflectionDeg) ? section.deflectionDeg : 0,
    // Legacy cove-depth hint (inert on the concentric path — the spiral cove is fit from the section).
    ...(coveDepth !== undefined ? { coveDepth } : {}),
    // The concentric slot gap (rig slot width if published, else the concentric floor).
    gap: gapConcentric,
    // HINGE-CONCENTRIC nose: the seeded aircraft aileron is the internally-hinged sealed aileron —
    // spiral nose + concentric spiral cove keep clearance constant at every deflection.
    concentricNose: true,
  };
}

/**
 * Build the seeded design draft for a PARAMETRIC seed mode from the stashed {@link SectionSeed}.
 * Pure — clones `base`, never mutates the seed — so a mode switch can rebuild from the same seed
 * any number of times (the mode control re-invokes this).
 *
 *   - `clean`: the bare single-element design — the aircraft's own clean section as the main via
 *     `conditionFoil`, sealed at the blunt TE, NO devices, blowing an explicit [].
 *   - `aileron` (the DEFAULT "as designed"): the same bare clean-foil design PLUS the plain-flap
 *     aileron the seed rig describes ({@link seedFlapOpts}), added via `addPlainFlap` with the
 *     CONDITIONED coords passed explicitly (the draft is built fully before any setter runs —
 *     `setMainCoords` is a queued update, so the flap must never read coords through state).
 *   - `highlift`: the aircraft's clean section carrying the DEFAULT native high-lift system (cove +
 *     vane + slotted flaperon + deploy track/motion + propeller). Clones `DEFAULT_HIGH_LIFT_AIRFOIL`
 *     (NOT `base` — guarantees the native devices even from an edited buffer) and swaps in only the
 *     seeded main coords + physical chord, KEEPING the whole device stack. `buildConfiguration`
 *     re-derives the flaperon + cove from the current main coords and the vane is absolute, so the
 *     stack rides onto the aircraft's own section (the FoilPicker replace-foil path proves the same).
 *
 * Returns `null` when the seed carries no usable cleanFoil (old publishers / degenerate stations)
 * — the caller falls back to the v1 plain-flap-on-incumbent mapping, unchanged.
 * The `coords` in the result are the conditioned foil for the follow-up `setMainCoords` re-assert.
 */
export function buildSeedDraft(
  seed: SectionSeed,
  mode: "aileron" | "clean" | "highlift",
  base: HighLiftAirfoil,
): { draft: HighLiftAirfoil; coords: AirfoilCoords } | null {
  const clean = seed.cleanFoil;
  if (!clean || !Array.isArray(clean.coords) || clean.coords.length < 16) return null;
  let coords: AirfoilCoords;
  try {
    // conditionFoil validates + canonicalizes (throws on a degenerate loop → v1 fallback).
    coords = conditionFoil(clean.coords.map((p) => [p[0], p[1]] as V2));
  } catch {
    return null;
  }
  const section = seed.section;
  // `highlift` clones the DEFAULT config (not `base`) so the native cove/vane/flaperon/deployTrack/
  // motion/blowing come through even when the live buffer was edited; the others clone `base` and
  // strip down to the bare clean foil (+ optional plain flap) below.
  const draft = structuredClone(mode === "highlift" ? DEFAULT_HIGH_LIFT_AIRFOIL : base);
  if (mode === "highlift") {
    // Only the DEVICE STACK comes from the default — everything the user set that is not a
    // device carries over from the live buffer like the sibling modes: flight/flow conditions
    // (freestreamKnots / densityAltitudeM / tempOffsetK — the default would silently reset Re
    // and Mach), knob constraints, the blunt-TE thickness, and the physical chord (the seed's
    // own scale below still wins when the rig carries unitMm).
    draft.operation.freestreamKnots = base.operation.freestreamKnots;
    draft.operation.densityAltitudeM = base.operation.densityAltitudeM;
    draft.operation.tempOffsetK = base.operation.tempOffsetK;
    draft.main.bluntThickness = base.main.bluntThickness;
    draft.main.chord = base.main.chord;
    if (base.constraints) draft.constraints = structuredClone(base.constraints);
  }
  // Physical chord (m) from the section's own units when the rig carries the scale
  // (section.chord is in model units; unitMm converts to true mm) — keeps Re honest.
  const unitMm = section.rig?.unitMm;
  const chordM =
    typeof unitMm === "number" && Number.isFinite(unitMm) && unitMm > 0 &&
    Number.isFinite(section.chord) && section.chord > 0
      ? (section.chord * unitMm) / 1000
      : null;
  if (chordM != null) draft.main.chord = chordM;
  // The conditioned clean section lands in the draft DIRECTLY (deep-copied, same shape the raw setter
  // writes) so any downstream builder + the single setConfig see the conditioned foil, never the
  // incumbent one.
  draft.main.coords = {
    upper: coords.upper.map((p) => [p[0], p[1]] as V2),
    lower: coords.lower.map((p) => [p[0], p[1]] as V2),
  };
  if (mode === "highlift") {
    // KEEP the native device stack — nothing to strip. buildConfiguration re-derives the flaperon +
    // cove from the current main coords (the vane is absolute), so the whole system rides onto the
    // aircraft's own section. The stowed operation + default propeller/blowing carry through.
    return { draft, coords };
  }
  // BARE single-element base: just the main, sealed at the blunt TE. Blowing is an explicit []
  // (the supported "no sources" state) and the operation is stowed.
  draft.design.cove = sealedTeCove(coords, draft.main.bluntThickness);
  draft.design.elements = [
    { kind: "main-cove", id: DEFAULT_MAIN_ELEMENT_ID, label: "Main", cove: draft.design.cove },
  ];
  draft.design.blowing = [];
  draft.operation.deployAngleDeg = 0;
  draft.operation.flaperonAngleDeg = 0;
  draft.operation.spoilerAngleDeg = 0;
  if (mode === "aileron") {
    // "As designed": the seed rig's plain-flap aileron ON the clean-foil design. The flap carries
    // its OWN cove; the builder re-cuts the main against it (the sealed-TE main cove stays inert).
    const flap = addPlainFlapWithOpts(draft.design, coords, seedFlapOpts(section));
    // Park the legacy deploy pivot ON the flap hinge - the cloned default left it at the factory
    // position, a marker floating in empty space under the seeded foil.
    draft.design.deployHinge = { ...draft.design.deployHinge, pivot: [flap.hingeX, flap.hingeY ?? 0] };
  }
  return { draft, coords };
}

/**
 * The READ-ONLY exact two-body overlay (seed mode `twobody`): the seed's EXACT cut contours (wing
 * box with its carved cove + the DEFLECTED control surface), drawn over the hero in the SAME
 * letterboxed chord frame the canvas + LIC field share, with the slot health measured from the
 * exact contours themselves ({@link normalizeTwoBodySection}). Deliberately NOT a solvable config:
 * resampling the cut into parametric elements would fake one, so the parametric buffer underneath
 * is left untouched and Solve keeps running it — the banner says exactly that.
 */
function CsTwoBodyOverlay({
  body,
  section,
  view,
  width,
  height,
  palette,
}: {
  body: TwoBodySection;
  section: SlottedSection;
  view: ViewRect;
  width: number;
  height: number;
  palette: HighliftPalette;
}) {
  const { s, ox, oy } = letterbox(view, width, height);
  // world → pixel (y-flip, matching FoilGeometryCanvas world()).
  const toPx = (p: [number, number]): [number, number] => [
    ox + s * (p[0] - view.xmin),
    height - (oy + s * (p[1] - view.ymin)),
  ];
  const path = (pts: [number, number][]): string =>
    pts
      .map((p, i) => {
        const [x, y] = toPx(p);
        return `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
      })
      .join(" ") + " Z";
  const m = body.metrics;
  const hinge = body.hinge ? toPx(body.hinge) : null;
  const metricsLine = m
    ? `gap ${(m.gap * 100).toFixed(2)}%c · overlap ${(m.overlap * 100).toFixed(2)}%c · exit ${(m.exitWidth * 100).toFixed(2)}%c · convergence ${m.convergence.toFixed(2)}`
    : "slot metrics unavailable (degenerate contours)";
  return (
    <>
      <svg
        width={width}
        height={height}
        style={{ position: "absolute", inset: 0, zIndex: 3, pointerEvents: "none" }}
        aria-label="exact two-body cut section (read-only)"
        data-testid="hlw-twobody-overlay"
      >
        <path d={path(body.wing)} fill={palette.mainFace} fillOpacity={0.55} stroke={palette.mainEdge} strokeWidth={1.6} />
        <path d={path(body.cs)} fill={palette.flapFace} fillOpacity={0.55} stroke={palette.flapEdge} strokeWidth={1.6} />
        {hinge ? (
          <g>
            <circle cx={hinge[0]} cy={hinge[1]} r={4} fill="none" stroke={palette.flaperonHinge} strokeWidth={1.4} />
            <line x1={hinge[0] - 7} y1={hinge[1]} x2={hinge[0] + 7} y2={hinge[1]} stroke={palette.flaperonHinge} strokeWidth={1} />
            <line x1={hinge[0]} y1={hinge[1] - 7} x2={hinge[0]} y2={hinge[1] + 7} stroke={palette.flaperonHinge} strokeWidth={1} />
          </g>
        ) : null}
      </svg>
      {/* Honesty banner: what this view is (the exact import, fixed geometry) and what Solve
          actually runs (the parametric aileron / clean-foil modes). */}
      <div
        style={{
          position: "absolute", top: 12, left: "50%", transform: "translateX(-50%)", zIndex: 4,
          display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
          background: `color-mix(in srgb, ${palette.surface} 88%, transparent)`,
          border: `1px solid ${palette.line}`, borderRadius: 8, padding: "6px 14px",
          pointerEvents: "none", maxWidth: "88%",
        }}
      >
        <span style={{ fontFamily: MONO_STACK, fontSize: 11, color: palette.ink }}>
          {`two-body (exact) · ${section.name} at ${Number.isFinite(section.deflectionDeg) ? section.deflectionDeg.toFixed(0) : "0"} deg${body.flipped ? " · flipped to aero-up" : ""}`}
        </span>
        <span style={{ fontFamily: MONO_STACK, fontSize: 10, color: palette.ink3 }}>{metricsLine}</span>
        <span style={{ fontFamily: MONO_STACK, fontSize: 10, color: palette.amber }}>
          read-only exact import - Solve runs the parametric aileron / clean foil modes
        </span>
      </div>
    </>
  );
}

/** Coerce a flattened run-table scalar to a finite number, else null. */
function rowNum(row: Record<string, unknown>, ...keys: string[]): number | null {
  for (const k of keys) {
    const v = Number(row[k]);
    if (Number.isFinite(v)) return v;
  }
  return null;
}

/** Adapt the (already-fetched) run-table rows → PolarPlot series. Each run node's
 *  value_json is flattened to row keys by api/runTable.ts, so the high-lift solve
 *  outputs (alpha/CL/CD/CM from rans_provenance.run_value_json) land as scalars.
 *  Rows are grouped by their provenance `source` (the solved-points provenance;
 *  the high-lift demo's runs are real Flow360 RANS, so they collapse to one
 *  "RANS" series). The client-solve backends (WebGPU in-browser, RANS — V100 local)
 *  persist through `/highlift/webgpu-run` with the SAME provenance `source` and
 *  `fidelity_level='rans'` — only `metadata.solver` diverges, and that tag is not
 *  flattened onto the row — so their points deliberately fold into the same "RANS"
 *  series here (a RANS-grade solve, just computed on the client/box). Rows missing
 *  CL are dropped (a freshly-forked cloud run is
 *  PENDING with null CL until case_poller fills it), and each group is sorted by
 *  α so the lift curve / drag polar trace cleanly. Returns [] when no run carries
 *  CL yet — the caller shows an honest empty state, never fabricated points. */
function runTableToPolarSeries(
  data: RunTableData | null | undefined,
  colors: { rans: string; other: string },
): PolarSeries[] {
  if (!data || !Array.isArray(data.rows) || data.rows.length === 0) return [];
  const groups = new Map<string, PolarSeries["points"]>();
  for (const row of data.rows) {
    const cl = rowNum(row, "CL", "cl", "Cl");
    if (cl == null) continue; // PENDING / no coefficient yet
    const alpha = rowNum(row, "alpha", "alpha_deg");
    if (alpha == null) continue;
    const cd = rowNum(row, "CD", "cd", "Cd") ?? NaN;
    const cm = rowNum(row, "CM", "cm", "Cm") ?? NaN;
    const rawSource = typeof row.source === "string" && row.source ? row.source : "RANS";
    // Normalise the common provenance sources to a short, human label.
    const label = /flow360|local|rans|cfd/i.test(rawSource) ? "RANS" : rawSource;
    const pts = groups.get(label) ?? [];
    const nodeId = typeof row._node_id === "string" ? row._node_id : undefined;
    pts.push({ alpha, CL: cl, CD: cd, CM: cm, nodeId });
    groups.set(label, pts);
  }
  const series: PolarSeries[] = [];
  for (const [label, pts] of groups) {
    pts.sort((a, b) => a.alpha - b.alpha);
    series.push({ label, color: label === "RANS" ? colors.rans : colors.other, points: pts });
  }
  return series;
}


/** Classification → trust colour, matching the composite trust palette (themed). */
function classColors(palette: HighliftPalette): Record<CompositeClassification, string> {
  return {
    ok: palette.classOk,
    low_confidence: palette.classLow,
    conflicting: palette.classConflict,
    extrapolation: palette.classExtrap,
    no_data: palette.classNoData,
  };
}
/** Swatch colour for an element kind (element list + section-tree dots). One place for all kinds. */
function elementSwatch(kind: ElementKind, palette: HighliftPalette): string {
  switch (kind) {
    case "main-cove":
      return palette.mainEdge;
    case "vane-bspline":
      return palette.vaneEdge;
    case "plain-slat":
      return palette.blue;
    case "slat":
      return palette.slatEdge;
    case "krueger":
      return palette.kruegerEdge;
    case "flaperon-derived":
    case "plain-flap":
      return palette.flapEdge;
  }
}
/**
 * Droop-nose / variable-camber LE control (FXC-9009 LE-device tranche). NOT an element — a morph of
 * the main contour. One slider for the droop angle (deg, positive = nose down) on the (first)
 * main-cove element; 0 ⇒ no droop (the default config stays byte-identical).
 */
function DroopNoseControl({
  config,
  setters,
  palette,
  mono,
}: {
  config: HighLiftAirfoil;
  setters: FoilSetters;
  palette: HighliftPalette;
  mono: CSSProperties;
}) {
  const main = elementsOf(config).find((e) => e.kind === "main-cove");
  const droopDeg = main?.kind === "main-cove" ? main.droopDeg ?? 0 : 0;
  const num: CSSProperties = {
    ...mono,
    width: 56,
    background: palette.surface2,
    border: `1px solid ${palette.line}`,
    color: palette.ink,
    borderRadius: 4,
    padding: "2px 4px",
    fontSize: 11,
    textAlign: "right",
  };
  return (
    <div
      style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, padding: "0 1px" }}
      title="Droop nose / variable-camber LE: morph the main's nose down (a camber deflection, not a split). 0 = off."
    >
      <span style={{ flex: 1, fontSize: 11, color: palette.ink3 }}>Droop nose (deg)</span>
      <input
        type="number"
        aria-label="droop nose angle"
        step={0.5}
        value={Number(droopDeg.toFixed(1))}
        onChange={(e) => setters.setMainDroop("droopDeg", Number(e.target.value))}
        style={num}
      />
    </div>
  );
}
const CLASS_LABELS: Record<CompositeClassification, string> = {
  ok: "ok",
  low_confidence: "low conf",
  conflicting: "conflicting",
  extrapolation: "extrapolation",
  no_data: "no data",
};
const CLASS_ORDER: CompositeClassification[] = ["ok", "low_confidence", "conflicting", "extrapolation", "no_data"];

/** Tally trust-map cells by classification (ok / low_confidence / conflicting / …). */
function trustMix(trust: TrustMapResponse | null): { mix: Record<CompositeClassification, number>; total: number } {
  const mix: Record<CompositeClassification, number> = {
    ok: 0,
    low_confidence: 0,
    conflicting: 0,
    extrapolation: 0,
    no_data: 0,
  };
  if (!trust) return { mix, total: 0 };
  for (const cell of trust.cells) mix[cell.classification] += 1;
  return { mix, total: trust.cells.length };
}

// Thread-v5 brand type: TWK Everett (UI) + JetBrains Mono (numeric/code). Matches the design handoff.
const FONT_STACK = "'TWK Everett', 'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif";
const MONO_STACK = "'JetBrains Mono', 'SFMono-Regular', Menlo, monospace";

/** Build the themed inline styles for this surface from the live palette (thread-v5 design language). */
function buildStyles(palette: HighliftPalette) {
  const panelStyle: React.CSSProperties = {
    background: palette.surface,
    border: `1px solid ${palette.line}`,
    borderRadius: 10,
    padding: 0,
    overflow: "hidden",
  };
  // Pill chip (design .chip) — neutral by default; callers add palette.primarySoft/primary for the active state.
  const chipBase: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 11,
    fontFamily: FONT_STACK,
    color: palette.ink3,
    background: palette.surface,
    border: `1px solid ${palette.line}`,
    cursor: "pointer",
  };
  // Primary action button (design: brand green on the soft wash).
  const buttonBase: React.CSSProperties = {
    appearance: "none",
    border: `1px solid ${palette.primary}`,
    background: palette.primarySoft,
    color: palette.primary,
    borderRadius: 7,
    padding: "7px 13px",
    fontSize: 12,
    fontFamily: FONT_STACK,
    fontWeight: 500,
    cursor: "pointer",
  };
  // Neutral secondary button (design .pbtn).
  const pbtn: React.CSSProperties = {
    appearance: "none",
    border: `1px solid ${palette.line}`,
    background: palette.surface,
    color: palette.ink2,
    borderRadius: 7,
    padding: "7px 13px",
    fontSize: 12,
    fontFamily: FONT_STACK,
    cursor: "pointer",
  };
  const muted12: React.CSSProperties = { fontSize: 12, color: palette.ink3, fontFamily: FONT_STACK };
  const mono11: React.CSSProperties = { fontSize: 11, fontFamily: MONO_STACK };
  return { panelStyle, chipBase, buttonBase, pbtn, muted12, mono11 };
}

// ── Data mode: a free-form board of draggable + RESIZABLE tiles (rects persist to localStorage). ──
// The board itself is the GENERAL `<TileBoard>` (components/workbench) — this surface just supplies
// its tiles + the "hlwb-board" persistence namespace. The generic board is back-compatible with the
// pre-existing {x,y}-only saved shape (missing w/h fall back to each tile's defaultRect size), so a
// saved board is not wiped. Tiles whose content reflows on resize (the live section PiP + the 3D
// trust surface) use the render-prop body to read the live inner size.

/** 32-hex run token for the live-progress poll (getRandomValues works in any context). */
function genHexToken(): string {
  const a = new Uint8Array(16);
  crypto.getRandomValues(a);
  return Array.from(a, (b) => b.toString(16).padStart(2, "0")).join("");
}

/** Ease a displayed number toward `target` so the big CL marker glides rather than
 *  jumping between polls. Returns null until a target exists. */
function useTweenedNumber(target: number | null | undefined, ms = 480): number | null {
  const [val, setVal] = useState<number | null>(target ?? null);
  const fromRef = useRef<number | null>(target ?? null);
  const rafRef = useRef<number | null>(null);
  useEffect(() => {
    if (target == null || !Number.isFinite(target)) {
      fromRef.current = null;
      setVal(null);
      return;
    }
    const from = fromRef.current ?? target;
    // Snap to a value immediately so the marker is never blank while a finite
    // target is known. requestAnimationFrame is throttled/paused in backgrounded
    // tabs; without this the marker would stick on "—" until the tab regains focus.
    setVal((v) => (v == null ? from : v));
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / ms);
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(from + (target - from) * eased);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
      else fromRef.current = target;
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [target, ms]);
  return val;
}

const SOLVE_PHASE_LABEL: Record<string, string> = {
  meshing: "re-meshing the geometry",
  preprocessing: "preprocessing",
  solving: "solving — converging",
  running: "solving",
  done: "done",
};

/** A small SVG sparkline of the CL convergence history (auto-scaled to its range). */
function ClSparkline({ history, color, dim }: { history: number[]; color: string; dim: string }) {
  const w = 168;
  const h = 40;
  if (history.length < 2) return null;
  let lo = Math.min(...history);
  let hi = Math.max(...history);
  if (hi - lo < 1e-9) {
    lo -= 0.5;
    hi += 0.5;
  }
  const pts = history
    .map((v, i) => {
      const x = (i / (history.length - 1)) * w;
      const y = h - ((v - lo) / (hi - lo)) * h;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  const lastX = w;
  const lastY = h - ((history[history.length - 1]! - lo) / (hi - lo)) * h;
  return (
    <svg width={w} height={h} style={{ display: "block" }} aria-hidden>
      <polyline points={pts} fill="none" stroke={color} strokeWidth={1.5} strokeLinejoin="round" />
      <circle cx={lastX} cy={lastY} r={2.5} fill={color} />
      <line x1={0} y1={h} x2={w} y2={h} stroke={dim} strokeWidth={0.5} />
    </svg>
  );
}

/** Centered live-solve overlay: phase + % bar + a BIG CL marker that settles as the
 *  flow converges, with a convergence sparkline. Shown over the foil/flow stage. */
function SolveProgressOverlay({
  progress,
  markerCl,
  sparkHistory,
  palette,
}: {
  progress: SolveProgress | null;
  markerCl: number | null;
  sparkHistory: number[];
  palette: HighliftPalette;
}) {
  const status = progress?.status ?? "meshing";
  const solving = status === "solving" || markerCl != null;
  const pct = typeof progress?.percentage === "number" ? progress.percentage : null;
  const tweenedCl = useTweenedNumber(solving ? markerCl : null, 160);
  const phase = SOLVE_PHASE_LABEL[status] ?? "solving";
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 3,
        display: "grid",
        placeItems: "center",
        background: "color-mix(in srgb, var(--v5-surface, #0b0e14) 62%, transparent)",
        backdropFilter: "blur(1.5px)",
        pointerEvents: "none",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 10,
          padding: "16px 18px",
          borderRadius: 12,
          background: palette.surface,
          border: `1px solid ${palette.line}`,
          boxShadow: "0 6px 24px rgba(0,0,0,0.28)",
          // Fit inside narrow dock panels (the SECTION GEOMETRY tile) without
          // clipping the big marker / phase label on either edge.
          maxWidth: "94%",
          boxSizing: "border-box",
        }}
      >
        <div style={{ fontSize: 11, letterSpacing: 1.4, textTransform: "uppercase", color: palette.ink3, fontFamily: MONO_STACK, textAlign: "center", width: "100%" }}>
          {phase}
          {solving && typeof progress?.step === "number" ? ` · step ${progress.step}` : ""}
        </div>

        {/* the big live CL marker */}
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <span style={{ fontSize: 13, color: palette.ink3, fontFamily: MONO_STACK }}>C</span>
          <span style={{ fontSize: 13, color: palette.ink3, fontFamily: MONO_STACK, alignSelf: "flex-end", marginLeft: -6 }}>L</span>
          <span
            data-cl-marker="1"
            style={{
              fontSize: "clamp(30px, 9vw, 40px)",
              fontWeight: 700,
              lineHeight: 1,
              fontFamily: MONO_STACK,
              color: tweenedCl == null ? palette.ink3 : palette.blue,
              fontVariantNumeric: "tabular-nums",
              transition: "color 200ms",
              textAlign: "right",
            }}
          >
            {tweenedCl == null ? "—" : tweenedCl.toFixed(3)}
          </span>
        </div>

        <ClSparkline history={sparkHistory} color={palette.blue} dim={palette.line} />

        {/* progress bar (solver %) or an indeterminate sweep while meshing */}
        <div style={{ width: 220, height: 6, borderRadius: 999, background: palette.surface2, overflow: "hidden" }}>
          <div
            style={
              pct != null
                ? { width: `${Math.max(2, Math.min(100, pct))}%`, height: "100%", background: palette.blue, transition: "width 300ms" }
                : { width: "40%", height: "100%", background: palette.blue, opacity: 0.6, animation: "highlift-solve-sweep 1.1s ease-in-out infinite" }
            }
          />
        </div>
        <div style={{ fontSize: 11, color: palette.ink3, fontFamily: MONO_STACK }}>
          {pct != null ? `${pct.toFixed(0)}% · re-meshed on the GPU box` : "on the GPU box · ~1 min"}
        </div>
      </div>
      <style>{`@keyframes highlift-solve-sweep { 0%{transform:translateX(-110%)} 100%{transform:translateX(360%)} }`}</style>
    </div>
  );
}

/** Corner HUD for the fill replay (FXC-9009): narrates the point being replayed —
 *  its condition, CL, which tier served it, and the acquisition's VERBATIM
 *  rationale. A corner card rather than a centered dim: the deploying section
 *  is the star of the replay, the card just captions it. */
function FillReplayOverlay({
  step,
  index,
  total,
  warm,
  palette,
}: {
  step: ReplayStep;
  index: number;
  total: number;
  warm: boolean;
  palette: HighliftPalette;
}) {
  const tweenedCl = useTweenedNumber(step.CL, 260);
  // Warm badge matches the runner's accounting (highlift_campaign counts
  // warm-started only for LOCAL inline solves): a cloud fork is never NN
  // warm-started, and it persists backend=null — so null on a done,
  // non-cached step reads "cloud", not "local".
  const badge = step.failed
    ? { text: "failed", color: palette.warn }
    : step.cached
      ? { text: "cached · free", color: palette.blue }
      : warm && step.backend === "local"
        ? { text: "solved · warm-started", color: palette.primary }
        : { text: `solved · ${step.backend ?? "cloud"}`, color: palette.primary };
  return (
    <div style={{ position: "absolute", left: 10, bottom: 10, zIndex: 3, pointerEvents: "none", maxWidth: "78%" }}>
      <div
        style={{
          padding: "10px 12px",
          borderRadius: 10,
          background: "color-mix(in srgb, var(--v5-surface, #0b0e14) 88%, transparent)",
          border: `1px solid ${palette.line}`,
          boxShadow: "0 4px 18px rgba(0,0,0,0.25)",
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}
      >
        <div style={{ fontSize: 10, letterSpacing: 1.2, textTransform: "uppercase", color: palette.ink3, fontFamily: MONO_STACK }}>
          Tailor fill replay · point {index + 1}/{total} · iteration {step.iterationIndex + 1}
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: palette.ink, fontFamily: MONO_STACK }}>
            α {step.alphaDeg.toFixed(1)}° · deploy {step.deployDeg.toFixed(1)}° · blow {step.blowing.toFixed(2)}
          </span>
          <span
            style={{
              fontSize: 22,
              fontWeight: 700,
              fontFamily: MONO_STACK,
              fontVariantNumeric: "tabular-nums",
              color: step.failed ? palette.warn : palette.blue,
            }}
          >
            {step.failed ? "✗" : tweenedCl == null ? "CL —" : `CL ${tweenedCl.toFixed(2)}`}
          </span>
          <span
            style={{
              fontSize: 10.5,
              fontFamily: MONO_STACK,
              color: badge.color,
              border: `1px solid ${badge.color}`,
              borderRadius: 999,
              padding: "1px 8px",
              whiteSpace: "nowrap",
            }}
          >
            {badge.text}
          </span>
        </div>
        <div
          title={step.reason}
          style={{
            fontSize: 11,
            color: palette.ink3,
            lineHeight: 1.35,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {step.reason || "—"}
        </div>
      </div>
    </div>
  );
}

/** Small line-icon helper for the spine step buttons (matches the design's `ico()`). */
function StepIcon({ paths }: { paths: string[] }) {
  return (
    <svg viewBox="0 0 16 16" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {paths.map((d, i) => (
        <path key={i} d={d} />
      ))}
    </svg>
  );
}

/** The five stage-spine steps, in order, with their design glyphs + sub-labels. */
const HLW_STAGES: Array<{ id: HlwStage; label: string; sub: string; icon: string[] }> = [
  { id: "rig", label: "Design", sub: "2D", icon: ["M2,9 C5,6 11,6 14,8 C11,10 5,10 2,9 Z", "M4.5,9 h1.6", "M11,8 h1.6"] },
  // Flow + Push merged (layout restructure step 2): they always shared the
  // solve backend, the Tailor queue and the campaign state — two stages was
  // one workflow split across a false wizard boundary.
  { id: "explore", label: "Explore", sub: "solve · fill", icon: ["M2,5 C6,5 10,5 14,5", "M2,8 C6,8 10,8 14,8", "M2,11 C6,11 10,11 14,11"] },
];

/** Lightweight upper/lower Cp polyline derived from the live flow mesh, if it
 *  carries surface pressure. NET-NEW; the current surface has no Cp plot. Returns
 *  null when no Cp is available so the caller shows an honest empty state instead
 *  of fabricating curves. */
export function deriveCpStrip(
  flow: FlowMesh | null,
): { elements: Array<{ element: string; upper: string; lower: string }> } | null {
  // Read the OPTIONAL per-element surface-Cp traces off the FlowMesh dict. Present
  // only when the solved slice carries airfoil surface samples — a remote
  // rans.pipeline change (like the disk wire). When absent we return null so the
  // caller shows the honest empty state; we NEVER fabricate a Cp curve.
  // TODO(highlift-studio): lights up when the rans runner emits surface Cp.
  const surfaceCp = flow?.surfaceCp;
  if (!surfaceCp || surfaceCp.length === 0) return null;

  // viewBox is 0 0 720 200; x/c (0..1) → 0..720 px; Cp plotted suction-UP (−Cp up),
  // scaled to the combined Cp range across all elements so the strip is comparable.
  let cpMin = Infinity;
  let cpMax = -Infinity;
  for (const el of surfaceCp) {
    for (const side of [el.upper, el.lower]) {
      for (const [, cp] of side) {
        if (!Number.isFinite(cp)) continue;
        if (cp < cpMin) cpMin = cp;
        if (cp > cpMax) cpMax = cp;
      }
    }
  }
  if (!Number.isFinite(cpMin) || !Number.isFinite(cpMax) || cpMax === cpMin) return null;
  const span = cpMax - cpMin;
  const PAD = 8;
  const toXY = (xc: number, cp: number): string => {
    const px = Math.max(0, Math.min(1, xc)) * 720;
    // −Cp up: high suction (very negative Cp) near the top.
    const py = PAD + ((cp - cpMin) / span) * (200 - 2 * PAD);
    return `${px.toFixed(1)},${py.toFixed(1)}`;
  };
  const toPolyline = (pts: [number, number][]): string =>
    pts.map(([xc, cp]) => toXY(xc, cp)).join(" ");
  const elements = surfaceCp
    .map((el) => ({
      element: el.element,
      upper: toPolyline(el.upper),
      lower: toPolyline(el.lower),
    }))
    .filter((e) => e.upper.length > 0 || e.lower.length > 0);
  return elements.length > 0 ? { elements } : null;
}

/** Resolve per-element force arrows for the hero from whichever schema is wired:
 *  (1) an `elementForces` array carried on the live /flow FlowMesh dict (best for
 *  the live hero), else (2) per-run scalars `element_<name>_CFx/_CFz/_CMy` flattened
 *  onto the selected run-table row. Returns null when neither is present so the
 *  overlay renders nothing (honest absent) — never fabricated.
 *  TODO(highlift-studio): lights up when the rans runner emits per-element forces. */
export function deriveElementForces(
  flow: FlowMesh | null,
  row: Record<string, unknown> | null,
): ElementForceArrow[] | null {
  // (1) Prefer the FlowMesh dict's elementForces (the live-slice payload).
  if (flow?.elementForces && flow.elementForces.length > 0) {
    const arrows = flow.elementForces
      .filter((f) => Number.isFinite(f.CFx) && Number.isFinite(f.CFz))
      .map((f) => ({
        element: f.element,
        CFx: f.CFx,
        CFz: f.CFz,
        CMy: Number.isFinite(f.CMy) ? f.CMy : 0,
        xCp: f.xCp,
        yCp: f.yCp,
      }));
    if (arrows.length > 0) return arrows;
  }
  // (2) Fall back to per-run scalars on the run row: element_<name>_CF{x,z}|CMy.
  if (row) {
    const byElement = new Map<string, { CFx?: number; CFz?: number; CMy?: number }>();
    for (const [key, value] of Object.entries(row)) {
      const m = /^element_(.+)_(CFx|CFz|CMy)$/.exec(key);
      if (!m || typeof value !== "number" || !Number.isFinite(value)) continue;
      const name = m[1]!;
      const comp = m[2] as "CFx" | "CFz" | "CMy";
      const e = byElement.get(name) ?? {};
      e[comp] = value;
      byElement.set(name, e);
    }
    const arrows: ElementForceArrow[] = [];
    for (const [name, e] of byElement) {
      if (e.CFx == null || e.CFz == null) continue;
      arrows.push({ element: name, CFx: e.CFx, CFz: e.CFz, CMy: e.CMy ?? 0 });
    }
    if (arrows.length > 0) return arrows;
  }
  return null;
}

/**
 * Default constant-section planform for the single-section loft: root == tip
 * (no taper / sweep / twist), so the loft is a straight extrusion of the CURRENT
 * 2-D section across the span. The span is a sensible default aspect-ratio
 * multiple of the section chord. Mapped to the API `WingPlanform` (snake_case)
 * that `WingViewer3D` consumes — it converts to the wingShell `WingPlanform3D`
 * itself (with zero dihedral/twist). */
const SINGLE_SECTION_AR = 3; // span = chord × AR for the degenerate single-section wing

function singleSectionPlanform(chord: number): WingPlanform {
  const c = Number.isFinite(chord) && chord > 0 ? chord : 1.0;
  return {
    root_le_x: 0.0,
    tip_le_x: 0.0, // zero sweep
    root_chord: c,
    tip_chord: c, // root == tip ⇒ constant section
    span: c * SINGLE_SECTION_AR,
    y_root: 0.0,
  };
}

function parseKnobLimit(value: string, scale: number): number | undefined {
  const n = Number(value);
  return value.trim() === "" || !Number.isFinite(n) ? undefined : n * scale;
}

/**
 * REAL single-section loft (FXC-9009 — "make the loft REAL"). The CURRENT
 * 2-D high-lift section is duplicated as root == tip and straight-extruded into
 * a constant-section 3-D wing via the same `buildWingShell` machinery + manifold
 * boolean used by `WingSectioningSurface`, then rendered in `WingViewer3D`. No
 * eta-stations / sections this phase (single-section only), so the viewer shows
 * just the lofted shell with no clickable markers. `WingViewer3D` owns the
 * manifold-async load + its own honest loading / error overlay, so this is a thin
 * wrapper: pass the live config, the derived planform, and a no-op selection.
 *
 * (Multi-section blend-or-terminate is the later Phase-4 piece.) */
function HlwSectionLoft({ config, palette }: { config: HighLiftAirfoil; palette: HighliftPalette }) {
  const planform = useMemo(() => singleSectionPlanform(config.main.chord), [config.main.chord]);
  return (
    <WingViewer3D
      planform={planform}
      airfoil={config}
      etaStations={[]}
      sections={[]}
      selectedEta={null}
      onSelectStation={() => {}}
      palette={palette}
    />
  );
}

/** A tiny rib thumbnail for the span ruler chips (design ribThumb). */

// ── Resize + collapse layout (FXC-9009) ──────────────────────────────────────
// The workbench's three columns (rail | hero | inspector) and the hero's vertical
// big/dock split are user-resizable + the rail/inspector are collapsible, persisted
// to localStorage. Forked verbatim from ThreadV5Surfaces' workbench-layout idiom
// (readWorkbenchLayout/writeWorkbenchLayout/workbenchStyle/clampNumber) so the
// pattern stays consistent across the two V5 surfaces.
const HIGHLIFT_LAYOUT_STORAGE_KEY = "thread:v5:highlift:layout";

interface HighliftLayout {
  /** Left rail width, px. */
  railWidth: number;
  /** Right inspector width, px. */
  inspWidth: number;
  /** Hero big-canvas vs dock-row vertical split, % of the hero given to the big canvas. */
  heroBigPct: number;
  /** Rail collapsed to a thin re-expand spine. */
  railCollapsed: boolean;
  /** Inspector collapsed to a thin re-expand spine. */
  inspCollapsed: boolean;
}

const RAIL_WIDTH_BOUNDS = { min: 180, max: 380 } as const;
const INSP_WIDTH_BOUNDS = { min: 280, max: 480 } as const;
const HERO_BIG_PCT_BOUNDS = { min: 30, max: 80 } as const;
/** Thin re-expand spine width when a column is collapsed, px. Mirrors the bar width. */
const HLW_COLLAPSED_W = 16;

const DEFAULT_HIGHLIFT_LAYOUT: HighliftLayout = {
  railWidth: 232,
  inspWidth: 332,
  heroBigPct: 60,
  // Rail starts COLLAPSED (§5.1): a first-timer sees geometry, one table, one
  // strip, one button — the rail opens on demand and the choice persists.
  railCollapsed: true,
  inspCollapsed: false,
};

function clampHl(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function readHighliftLayout(): HighliftLayout {
  if (typeof window === "undefined") return DEFAULT_HIGHLIFT_LAYOUT;
  try {
    const parsed = JSON.parse(window.localStorage.getItem(HIGHLIFT_LAYOUT_STORAGE_KEY) ?? "{}");
    return {
      railWidth: clampHl(Number(parsed.railWidth) || DEFAULT_HIGHLIFT_LAYOUT.railWidth, RAIL_WIDTH_BOUNDS.min, RAIL_WIDTH_BOUNDS.max),
      inspWidth: clampHl(Number(parsed.inspWidth) || DEFAULT_HIGHLIFT_LAYOUT.inspWidth, INSP_WIDTH_BOUNDS.min, INSP_WIDTH_BOUNDS.max),
      heroBigPct: clampHl(Number(parsed.heroBigPct) || DEFAULT_HIGHLIFT_LAYOUT.heroBigPct, HERO_BIG_PCT_BOUNDS.min, HERO_BIG_PCT_BOUNDS.max),
      railCollapsed: parsed.railCollapsed === undefined
        ? DEFAULT_HIGHLIFT_LAYOUT.railCollapsed
        : Boolean(parsed.railCollapsed),
      inspCollapsed: Boolean(parsed.inspCollapsed),
    };
  } catch {
    return DEFAULT_HIGHLIFT_LAYOUT;
  }
}

function writeHighliftLayout(layout: HighliftLayout): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(HIGHLIFT_LAYOUT_STORAGE_KEY, JSON.stringify(layout));
}

/** Inject the layout CSS vars consumed by the .hlw-wb grid + hero split. A collapsed
 *  column reports a thin spine width so the centre 1fr reclaims the freed space. */
function highliftLayoutStyle(layout: HighliftLayout): CSSProperties {
  return {
    "--hlw-rail-w": `${layout.railCollapsed ? HLW_COLLAPSED_W : layout.railWidth}px`,
    "--hlw-insp-w": `${layout.inspCollapsed ? HLW_COLLAPSED_W : layout.inspWidth}px`,
    "--hlw-hero-big": `${layout.heroBigPct}%`,
  } as CSSProperties;
}

type HighliftResizeKind = "rail" | "insp" | "hero";

export function HighliftMultifidelitySurface(props: SurfaceProps) {
  const { projectId, projectName, branch, persistedProject, selectedNodeId, onSelectRun, onOpenView } = props;
  const queryClient = useQueryClient();
  const palette = useHighliftTheme();
  const { theme, toggleTheme } = useTheme();
  const { buttonBase, muted12, mono11 } = useMemo(() => buildStyles(palette), [palette]);
  const classColorMap = useMemo(() => classColors(palette), [palette]);
  const foil = useFoilDesign();
  const { config, configuration, setters, flowConditions, setKnobConstraint, gapMetrics, motionGap } = foil;
  const { setDeployDeg, setFlaperonDeg } = setters;
  const { elements, blowing, addVane, addPlainFlap, addSlat, addKrueger, addSlottedFlap, addSingleSlottedFlap, addBlowing, removeCollectionItem } = foil;
  const { setHandleDescriptor } = foil;
  // XFOIL buffer-foil → Thread lineage: the live config is the BUFFER; `baseline` is the "current".
  // `loadConfig` re-baselines on load; `commitBaseline` promotes the buffer after a variant commit.
  const { isDirty, baseline, bufferFingerprint, loadConfig, commitBaseline } = foil;
  // NN-Newton design-opt (DESIGN §8): the inverse-design / optimise loop + its ranged-DOF sidecar.
  // surfaceCp + the loops are honest-null/no-op until an NN is bound via the `setNN` seam.
  const { ranges, setRange, clearRange, surfaceCp, hasNN, runInverseDesign, runOptimize, runGapTarget, runGeometricOptimize, gapMetricOutputs, setNN } = foil;

  // ── Aircraft CS → high-lift seed intake (control-surface fold). ONE-SHOT on mount: if the
  // aircraft Control Surfaces designer published a cut section (the sessionStorage handshake in
  // foil/sectionSeed.ts), start the workbench FROM THAT AIRCRAFT'S OWN SECTION.
  //
  // v2 (seed.cleanFoil present) — THREE MODES, rebuildable from the stashed seed at any time via
  // the compact control next to the seeded-from note (buildSeedDraft is the one builder):
  //   - "aileron" (DEFAULT — the section AS DESIGNED): the clean aircraft foil as the main (the
  //     SAME conditionFoil → sealed-TE path FoilPicker's .dat upload takes) PLUS the plain-flap
  //     aileron the seed rig describes (hinge x/c, deflection, slot gap → cove depth; seedFlapOpts).
  //   - "clean": the bare single-element clean foil only — the user shapes cove + devices with the
  //     NATIVE high-lift options; nothing is auto-shaped (the previous v2 default).
  //   - "twobody": the seed's EXACT cut contours rendered READ-ONLY over the hero with slot health
  //     measured from them; the parametric buffer underneath is untouched (Solve keeps running it).
  //
  // v1 fallback (no cleanFoil — an old seed — or a malformed one): UNCHANGED — the plain-flap
  // (aileron) element mapped onto the INCUMBENT design; no mode control (the seed cannot honestly
  // rebuild the other modes without its clean foil).
  //
  // Either way the RAW SlottedSection (exact wing/cs contours) is retained on `csSeed` — it feeds
  // the two-body view AND the sparse-wide cs_* provenance axes on persisted in-browser runs (see
  // webgpuPersist.csRunAxes). Mode switches never clear it; replacing the foil (FoilPicker) does.
  const [csSeed, setCsSeed] = useState<SectionSeed | null>(null);
  const [csSeedMode, setCsSeedMode] = useState<CsSeedMode>("aileron");
  // True when the v2 (cleanFoil) build applied at mount — gates the mode control, so an old v1
  // seed keeps exactly the previous behavior with no switch offered.
  const [csSeedParametric, setCsSeedParametric] = useState(false);
  useEffect(() => {
    // Fresh handoff first; else remount recovery. The shell can remount this surface at any time
    // (observed live: Solve -> the seeded design applied, then a remount silently replaced it with
    // the mount default - the one-shot key was consumed and all in-memory state died with the old
    // instance). The applied-seed record makes the whole lifecycle remount-proof: re-apply and
    // restore the mode. The same actions that abandon a seed clear the record too.
    const fresh = consumeSectionSeed();
    const applied = fresh ? null : readAppliedSeed();
    const seed = fresh ?? applied?.seed;
    if (!seed) return;
    const mode: CsSeedMode = applied?.mode ?? "aileron";
    // Remount recovery with a persisted LIVE buffer: restore the EXACT composed design (post-seed
    // device edits included) instead of rebuilding from the seed, which silently discarded every
    // device the user added on top (the remount-wipe trap). Only on a remount (not a fresh handoff)
    // and only when a buffer was persisted; setConfig migrates + rebinds the legacy refs.
    if (!fresh && applied?.buffer) {
      foil.setConfig(applied.buffer);
      setCsSeedParametric(true);
      setCsSeedMode(mode);
      setCsSeed(seed);
      return;
    }
    // DEFAULT: "aileron — as designed". Build the draft FULLY (conditioned coords + flap) before
    // the single setConfig; setMainCoords then re-asserts the coords through the supported
    // raw-coords setter (deep-copied, same as FoilPicker — NOT foilPickerSetters, which clears
    // csSeed). Ordering matters: setMainCoords is a QUEUED functional update, so the flap must be
    // built from the explicit conditioned coords, never read back through state.
    // Two-body restores its underlying parametric buffer as "aileron" (the overlay rides on top).
    const built = buildSeedDraft(seed, mode === "twobody" ? "aileron" : mode, config);
    if (built) {
      foil.setConfig(built.draft);
      setters.setMainCoords(built.coords);
      setCsSeedParametric(true);
      setCsSeedMode(mode);
      writeAppliedSeed({ seed, mode });
    } else if (fresh) {
      // v1 fallback: the plain-flap mapping onto the INCUMBENT design (rig maths shared via
      // seedFlapOpts — one home, not re-derived). Not remount-proof by design: re-adding a flap
      // onto whatever design a remount starts from would not be idempotent.
      const draft = structuredClone(config);
      addPlainFlapWithOpts(draft.design, draft.main.coords, seedFlapOpts(seed.section));
      foil.setConfig(draft);
    } else {
      // A stashed applied seed that no longer builds (should not happen - it built once) - drop
      // it rather than looping on a poisoned record.
      clearAppliedSeed();
      return;
    }
    setCsSeed(seed);
    // Mount-only by design: consume clears the key; a StrictMode second run re-applies the SAME
    // applied record (idempotent - the draft is rebuilt from identical inputs).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist the LIVE config buffer alongside the applied seed on every config change while a
  // parametric CS seed is active (Phase 3 remount-wipe fix): a shell remount then restores the exact
  // composed design (added vane / slotted flap / slat …) from the buffer instead of rebuilding from
  // the seed and discarding them. Gated on csSeedParametric so a v1 non-parametric fallback (which
  // can't rebuild) never writes a buffer. The invalidation call sites (FoilPicker / load geometry /
  // deep link) clear csSeed + the applied record, so this early-returns and never resurrects a seed.
  useEffect(() => {
    if (!csSeed || !csSeedParametric) return;
    writeAppliedSeed({ seed: csSeed, mode: csSeedMode, buffer: config });
  }, [csSeed, csSeedParametric, csSeedMode, config]);

  // The exact two-body view of the stashed seed (chord-normalized, orientation-matched against
  // the clean foil, slot health measured from the exact contours). Honest-null when the seed
  // carries no usable contours — the two-body mode button disables itself.
  const twoBody = useMemo(() => (csSeed ? normalizeTwoBodySection(csSeed) : null), [csSeed]);
  const twoBodyActive = csSeedMode === "twobody" && !!twoBody && !!csSeed;

  // Switch seed modes: parametric modes REBUILD the design from the stashed seed (same builder as
  // mount); two-body only flips the read-only overlay on — the parametric buffer stays as-is, so
  // Solve keeps running the last parametric design (the overlay banner says so). Never clears
  // csSeed (the cs_* provenance axes persist across switches).
  const applyCsSeedMode = useCallback(
    (mode: CsSeedMode) => {
      if (!csSeed) return;
      if (mode === "twobody") {
        setCsSeedMode("twobody");
        writeAppliedSeed({ seed: csSeed, mode });
        return;
      }
      const built = buildSeedDraft(csSeed, mode, config);
      if (!built) return;
      foil.setConfig(built.draft);
      setters.setMainCoords(built.coords);
      setCsSeedMode(mode);
      writeAppliedSeed({ seed: csSeed, mode });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [csSeed, config, foil.setConfig, setters],
  );

  // Replacing the base foil (FoilPicker) abandons the aircraft CS seed — the design no longer
  // derives from that cut, so later runs must not keep claiming cs_* provenance. Loading a saved
  // geometry clears it too (see handleLoadGeometry / the ?geometry= deep link below).
  const foilPickerSetters = useMemo<FoilSetters>(
    () => ({
      ...setters,
      setMainCoords: (coords) => {
        setCsSeed(null);
        clearAppliedSeed();
        // Seed gone ⇒ the mode control + two-body overlay unmount with it; reset the mode so a
        // hypothetical later seed never inherits a stale "twobody".
        setCsSeedMode("aileron");
        setCsSeedParametric(false);
        setters.setMainCoords(coords);
      },
    }),
    [setters],
  );

  // "Animate deploy": sweeps the flap-deploy angle back and forth across its effective limits
  // (`motionGap.deployRange`) so the deployment can be watched (FXC-9009). This drives the hero
  // canvas's IMPERATIVE `previewDeploy` handle each frame rather than `setDeployDeg` → React state:
  // a per-frame config mutation re-renders the entire workbench (3D loft rebuild, inspector panels,
  // AG-grid pivot, …), which on slower engines (Safari/WebKit) drops the effective rate to a few fps
  // and makes the sweep visibly STEP in big jumps. `previewDeploy` redraws only the 2D section canvas
  // with zero React work, so the sweep stays smooth; the final angle is committed to real state once,
  // on stop, so the rest of the tree (loft / slot health / sweep wire) catches up to the end pose.
  // Span folded into Design (restructure step 4): the hero toggles 2D section
  // editor | 3D constant-section loft. Declared HERE (before the animations)
  // because starting a replay/animate must force the 2D view — both draw on
  // the 2D canvas, which is display:none in 3D mode.
  const [heroView, setHeroView] = useState<"2d" | "3d">("2d");
  const ANIMATE_DEPLOY_MS = 2200;
  const heroCanvasRef = useRef<FoilGeometryCanvasHandle | null>(null);
  const animateDeployRafRef = useRef<number | null>(null);
  const animateDeployGenerationRef = useRef(0);
  const lastPreviewDeployRef = useRef<number | null>(null);
  // Which operation DOF the current preview sweep is driving (deploy track vs flaperon deflection).
  // A CS-seeded plain-flap aileron has no deploy-track device, so "animate" sweeps the flaperon axis
  // instead; the commit-on-stop must then write flaperonAngleDeg, not deployAngleDeg. The fill replay
  // only ever previews deploy and never touches this ref (reset to 'deploy' on every stop).
  const lastPreviewAxisRef = useRef<"deploy" | "flaperon">("deploy");
  const slotHealthRowRefs = useRef(new Map<string, HTMLDivElement>());
  const [animatingDeploy, setAnimatingDeploy] = useState(false);
  // The axis + range "animate deploy" and the range-of-motion envelope bar sweep for THIS design.
  const motionAxis = useMemo(() => animateMotionAxis(config, motionGap), [config, motionGap]);
  const previewSlotHealth = useCallback((angleDeg: number, axis: "deploy" | "flaperon" = "deploy") => {
    const rows = slotHealthRowRefs.current;
    if (rows.size === 0) return;
    const previewConfig: HighLiftAirfoil = {
      ...config,
      operation: {
        ...config.operation,
        ...(axis === "flaperon" ? { flaperonAngleDeg: angleDeg } : { deployAngleDeg: angleDeg }),
      },
    };
    const previewMetrics = computeGapMetrics(buildConfiguration(previewConfig));
    for (const slot of previewMetrics.slots) {
      const row = rows.get(slot.downstreamId);
      if (!row) continue;
      const gapPct = slot.metrics.gap * 100;
      const curPct = Math.max(0, Math.min(100, (slot.metrics.gap / 0.08) * 100));
      const mark = row.querySelector<HTMLElement>(".hlw-barmark");
      const value = row.querySelector<HTMLElement>(".pv");
      if (mark) mark.style.left = `${curPct}%`;
      if (value) value.textContent = `${gapPct.toFixed(1)}%`;
      const mSlot = motionGap.slots.find((s) => s.downstreamId === slot.downstreamId);
      row.title = mSlot
        ? `current ${gapPct.toFixed(2)}%c · range ${(mSlot.minGap * 100).toFixed(2)}–${(mSlot.maxGap * 100).toFixed(2)}%c across deploy ${motionGap.deployRange[0].toFixed(0)}–${motionGap.deployRange[1].toFixed(0)}° / flaperon ${motionGap.flaperonRange[0].toFixed(0)}–${motionGap.flaperonRange[1].toFixed(0)}°`
        : `current ${gapPct.toFixed(2)}%c`;
    }
  }, [config, motionGap]);
  const stopAnimateDeploy = useCallback(() => {
    animateDeployGenerationRef.current += 1;
    if (animateDeployRafRef.current != null) {
      cancelAnimationFrame(animateDeployRafRef.current);
      animateDeployRafRef.current = null;
    }
    heroCanvasRef.current?.clearDeployPreview();
    // Commit the last previewed angle once so the whole tree catches up to where the sweep stopped
    // (the canvas is already showing it via previewDeploy, so this is a no-flicker single re-render).
    // Commit onto the SAME axis the sweep drove — flaperon deflection for a plain-flap aileron,
    // deploy angle otherwise — so a stopped animation never writes a junk deploy angle into a design
    // whose deploy track is inert (which would silently shift NN sweep predictions).
    if (lastPreviewDeployRef.current != null) {
      if (lastPreviewAxisRef.current === "flaperon") setFlaperonDeg(lastPreviewDeployRef.current);
      else setDeployDeg(lastPreviewDeployRef.current);
      lastPreviewDeployRef.current = null;
    }
    lastPreviewAxisRef.current = "deploy";
    setAnimatingDeploy(false);
  }, [setDeployDeg, setFlaperonDeg]);
  // The fill replay (declared after the campaign seam below) shares the
  // previewDeploy channel — starting either animation must stop the other,
  // and a MANUAL start of either is a human takeover that also disarms the
  // replay's kiosk autoplay (else the 4s loop would stomp this sweep).
  // Refs break the declaration-order cycle: the replay assigns both below.
  const stopReplayFillRef = useRef<() => void>(() => {});
  const disarmReplayAutoplayRef = useRef<() => void>(() => {});
  // Fired when a replay pass ends NATURALLY (last step played) — the autoplay
  // choreography uses it to promote the trust map (§5.7). Assigned below.
  const onReplayNaturalEndRef = useRef<() => void>(() => {});
  const toggleAnimateDeploy = useCallback(() => {
    if (animatingDeploy) {
      stopAnimateDeploy();
      return;
    }
    // Sweep the axis this design actually moves on: the deploy track for the default multi-element
    // section, the flaperon deflection for a CS-seeded plain-flap aileron (whose deploy track is inert).
    const { axis, range } = motionAxis;
    const [lo, hi] = range;
    if (!(hi > lo)) return; // degenerate/fixed knob — nothing to sweep
    disarmReplayAutoplayRef.current();
    stopReplayFillRef.current();
    setHeroView("2d"); // the sweep draws on the 2D canvas — never run it hidden
    setAnimatingDeploy(true);
    lastPreviewAxisRef.current = axis;
    const generation = animateDeployGenerationRef.current + 1;
    animateDeployGenerationRef.current = generation;
    const span = hi - lo;
    const period = ANIMATE_DEPLOY_MS * 2; // one full lo->hi->lo cycle
    const start = performance.now();
    const tick = (now: number): void => {
      animateDeployRafRef.current = null;
      if (animateDeployGenerationRef.current !== generation) return;
      const elapsed = (now - start) % period;
      // Triangle wave 0->1->0 over one period: a continuous back-and-forth sweep, not a snap-reset.
      const frac = elapsed < ANIMATE_DEPLOY_MS ? elapsed / ANIMATE_DEPLOY_MS : 2 - elapsed / ANIMATE_DEPLOY_MS;
      const angle = lo + frac * span;
      lastPreviewDeployRef.current = angle;
      heroCanvasRef.current?.previewDeploy(angle, axis);
      previewSlotHealth(angle, axis);
      if (animateDeployGenerationRef.current !== generation) return;
      animateDeployRafRef.current = requestAnimationFrame(tick);
    };
    animateDeployRafRef.current = requestAnimationFrame(tick);
  }, [animatingDeploy, motionAxis, previewSlotHealth, stopAnimateDeploy]);
  // Stop on unmount so a pending rAF never fires into an unmounted tree.
  useEffect(() => () => stopAnimateDeploy(), [stopAnimateDeploy]);

  // Pure geometric flap-deployment optimizer (FXC-9009) — clearance/deflection/path/sizes from
  // geometry + first-principles slot-shape criteria, no NN/backend. Runs over whichever deployment
  // knobs the panel below has left free, respecting their limits. It's a synchronous ~1-2s Nelder-
  // Mead run (see `solveGeometricOptimize`'s own cost comments), so it's gated behind an explicit
  // click with a "optimizing…" state rather than running live.
  const [geomOptRunning, setGeomOptRunning] = useState(false);
  const [geomOptNote, setGeomOptNote] = useState<string | null>(null);
  // Guards the setState calls below against firing after unmount (the surface can unmount mid-run —
  // this is a ~1-2s synchronous solve — same `cancelled` pattern as the NN-bind effect below).
  const geomOptMountedRef = useRef(true);
  useEffect(() => {
    geomOptMountedRef.current = true;
    return () => {
      geomOptMountedRef.current = false;
    };
  }, []);
  const onOptimizeGeometry = useCallback(() => {
    setGeomOptRunning(true);
    setGeomOptNote(null);
    void runGeometricOptimize()
      .then((res) => {
        if (!geomOptMountedRef.current) return;
        if (!res.applied) {
          setGeomOptNote("no-op — every deployment knob is held fixed.");
          return;
        }
        const pct = res.initialCost !== 0 ? (100 * (res.initialCost - res.finalCost)) / Math.abs(res.initialCost) : 0;
        setGeomOptNote(`health cost ${res.initialCost.toFixed(1)} → ${res.finalCost.toFixed(1)} (${pct >= 0 ? "−" : "+"}${Math.abs(pct).toFixed(0)}%)`);
      })
      .finally(() => {
        if (geomOptMountedRef.current) setGeomOptRunning(false);
      });
  }, [runGeometricOptimize]);

  // Bind the flowfield-NN to the design hook on the main thread (same weights the field preview uses:
  // this project's registered model, else the static build asset). Makes surfaceCp + the inverse/optimise
  // loops live. Honest no-op (hasNN stays false) if the weights can't be fetched.
  //
  // FXC-9009 checkpoint stamp: `loadNN` now DETECTS the checkpoint's feature schema (v2
  // `raw_features` vs legacy raw9) and throws on anything unrecognised rather than silently
  // assuming raw9 — a wrong-schema forward produces confidently-wrong fields with no error.
  // We keep the detected {schemaVersion, rawFeatureCount} here and surface it on the NN badge,
  // so which contract the bound model speaks is visible, not archaeology.
  const [nnCheckpoint, setNnCheckpoint] = useState<{ schemaVersion: string; rawFeatureCount: number } | null>(null);
  useEffect(() => {
    let cancelled = false;
    const fallback = `${import.meta.env.BASE_URL ?? "/"}flowfield_mlp_web.json`;
    const primary = projectId ? `/api/projects/${projectId}/highlift/nn/model` : fallback;
    (async () => {
      try {
        let res = await fetch(primary);
        if (!res.ok && primary !== fallback) res = await fetch(fallback);
        if (!res.ok) return;
        const json = await res.json();
        const nn = loadNN(json);
        // FoilNN wraps the model: { nn, forward? } — forward defaults to nnInfer.forward.
        if (!cancelled && nn && Array.isArray(nn.channels) && nn.channels.length > 0) {
          setNN({ nn });
          setNnCheckpoint({ schemaVersion: nn.schemaVersion, rawFeatureCount: nn.rawFeatureCount });
        }
      } catch (error: unknown) {
        // Honest no-op — hasNN stays false, the panel says "bind an NN". An unknown-schema
        // checkpoint lands here too (loadNN throws), so log it: "NN silently absent" and
        // "NN refused a bad checkpoint" must be distinguishable in the console.
        if (!cancelled) setNnCheckpoint(null);
        console.warn("[highlift] flowfield-NN bind skipped", error);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId, setNN]);

  // Solve point's angle of attack — α isn't a section-design knob in Operation,
  // so the foil tool has no α slider; the solve/grid axis needs it, so we own it
  // here. mach/reynolds come from the live flow conditions; flap_deploy_deg from
  // the deploy angle.
  const [alphaDeg, setAlphaDeg] = useState(4);
  // Last solved coefficients, for the hero HUD cards (CL / L·D / CD). Captured from a solve
  // submission that carried them (local solves do; cache hits leave them null → HUD shows "—").
  const [lastCoeff, setLastCoeff] = useState<{ cl: number; cd: number | null; ld: number | null } | null>(null);

  // Responsive viewer stage — fill the column width (the old fixed 760px overflowed the ~630px
  // column and clipped the right edge). The foilView letterbox refits both layers to any w/h, so
  // both stay pixel-aligned. Height tracks the foil's 760:360 aspect so it never letterboxes.
  // Measure the hero stage width with a CALLBACK ref, not a mount-only effect. The hero unmounts
  // when the Data board is shown and remounts on return to Design; a `[]`-deps effect would keep
  // observing the stale, detached node (clientWidth 0 → stageW pinned to its 360 floor) and never
  // re-measure the fresh wrapper — so the flowfield never grew back after a Design↔Data round-trip.
  // A callback ref re-attaches the ResizeObserver on every (re)mount, so the stage recovers.
  const [stageW, setStageW] = useState(720);
  const stageRoRef = useRef<ResizeObserver | null>(null);
  const stageWrapRef = useCallback((el: HTMLDivElement | null) => {
    stageRoRef.current?.disconnect();
    stageRoRef.current = null;
    if (!el) return;
    const measure = () => setStageW(Math.max(360, Math.round(el.clientWidth)));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    stageRoRef.current = ro;
  }, []);
  const stageH = Math.round((stageW * 360) / 760);


  // ── Resize + collapse layout state (FXC-9009) ──────────────────────────────
  // Persisted column widths + hero split + collapse flags. The .hlw-wb element is
  // position:relative and carries the CSS vars; the three pointer-driven bars below
  // patch the relevant field, clamped to bounds, and persist on every change.
  const wbRef = useRef<HTMLDivElement | null>(null);
  const [layout, setLayout] = useState<HighliftLayout>(() => readHighliftLayout());
  useEffect(() => { writeHighliftLayout(layout); }, [layout]);
  const layoutStyle = useMemo(() => highliftLayoutStyle(layout), [layout]);

  // Mobile sheets (§5.9): at ≤900px the rail becomes a left slide-over and the
  // inspector a bottom sheet — SAME components, same sections/folds, summoned
  // by the spine's mobile toolbar. Transient (never persisted) so a phone
  // session can't leave a desktop profile with a surprise layout.
  const [mobileSheet, setMobileSheet] = useState<null | "rail" | "insp">(null);
  const openMobileSheet = useCallback((which: "rail" | "insp") => {
    // Force-expand the column the sheet shows (a desktop-collapsed column would
    // otherwise open an empty sheet).
    setLayout((cur) =>
      which === "rail"
        ? cur.railCollapsed ? { ...cur, railCollapsed: false } : cur
        : cur.inspCollapsed ? { ...cur, inspCollapsed: false } : cur,
    );
    setMobileSheet((cur) => (cur === which ? null : which));
  }, []);

  const resetHighliftResize = useCallback((kind: HighliftResizeKind) => {
    setLayout((cur) => kind === "rail"
      ? { ...cur, railWidth: DEFAULT_HIGHLIFT_LAYOUT.railWidth }
      : kind === "insp"
      ? { ...cur, inspWidth: DEFAULT_HIGHLIFT_LAYOUT.inspWidth }
      : { ...cur, heroBigPct: DEFAULT_HIGHLIFT_LAYOUT.heroBigPct });
  }, []);

  const beginHighliftResize = useCallback((event: ReactPointerEvent<HTMLButtonElement>, kind: HighliftResizeKind) => {
    event.preventDefault();
    const rect = wbRef.current?.getBoundingClientRect();
    const usableWidth = rect?.width && rect.width > 0 ? rect.width : window.innerWidth;
    const usableHeight = rect?.height && rect.height > 0 ? rect.height : window.innerHeight;
    const startX = event.clientX;
    const startY = event.clientY;
    const startLayout = layout;
    // The hero occupies the grid middle row (minus the 52px spine; the trust
    // strip row is retired — the hero got its 66px back).
    const heroHeight = Math.max(120, usableHeight - 52);
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = kind === "hero" ? "row-resize" : "col-resize";
    document.body.style.userSelect = "none";

    const handleMove = (moveEvent: PointerEvent) => {
      setLayout(() => {
        if (kind === "rail") {
          return { ...startLayout, railWidth: clampHl(startLayout.railWidth + moveEvent.clientX - startX, RAIL_WIDTH_BOUNDS.min, Math.min(RAIL_WIDTH_BOUNDS.max, usableWidth * 0.4)) };
        }
        if (kind === "insp") {
          return { ...startLayout, inspWidth: clampHl(startLayout.inspWidth + startX - moveEvent.clientX, INSP_WIDTH_BOUNDS.min, Math.min(INSP_WIDTH_BOUNDS.max, usableWidth * 0.5)) };
        }
        const deltaPct = ((moveEvent.clientY - startY) / heroHeight) * 100;
        return { ...startLayout, heroBigPct: clampHl(startLayout.heroBigPct + deltaPct, HERO_BIG_PCT_BOUNDS.min, HERO_BIG_PCT_BOUNDS.max) };
      });
    };
    const handleUp = () => {
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      window.removeEventListener("pointercancel", handleUp);
    };
    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    window.addEventListener("pointercancel", handleUp);
  }, [layout]);

  // Shared chord-coordinate view for the foil canvas + the LIC flow layer. The
  // mouse wheel zooms it about the cursor (FoilGeometryCanvas → setFoilView), and
  // both layers re-render at the new view so the streamlines recompute. `showLabels`
  // toggles the text overlays (control-point ids, gap metrics, axes, hint).
  const [foilView, setFoilView] = useState<ViewRect>(FOIL_VIEW);
  // clip-path (rect minus smooth foil contours) emitted by FoilGeometryCanvas, applied
  // to the LIC field so its boundary is the smooth foil edge, not the grid-mask staircase.
  const [fieldClip, setFieldClip] = useState<string | null>(null);
  const [showLabels, setShowLabels] = useState(true);
  const [showAnnotations, setShowAnnotations] = useState(true);
  // Stage spine (redesign IA): the workbench is a Rig → Flow → Push → Trust → Span
  // progression instead of the old Design/Data segmented switch. `mode` is DERIVED
  // from the stage so every existing `mode === "data"` guard (ConditionPivotTable
  // isVisible, the run-table staleness, etc.) keeps working unchanged: the
  // Trust/Span stages are the data-facing ones.
  const [stage, setStage] = useState<HlwStage>(() => {
    // Deep-linkable stage (?stage=), mirroring the shell's ?view= convention —
    // a reload used to silently dump the user back to the default stage.
    if (typeof window === "undefined") return "rig";
    const s = new URLSearchParams(window.location.search).get("stage");
    // Legacy deep-links: the split Flow/Push stages merged into Explore; the
    // Database stage handed off to the rail's Model/Trust nouns; Span folded
    // into Design's 2D|3D hero toggle.
    if (s === "flow" || s === "push" || s === "trust") return "explore";
    return s === "rig" || s === "explore" ? s : "rig";
  });
  useEffect(() => {
    // Sync the stage back to the URL (replaceState — stage hops are not history
    // entries) and reset the shared inspector scroll: the scroll container is
    // reused across stages, so a switch used to strand users mid-panel.
    const url = new URL(window.location.href);
    if (stage === "rig") url.searchParams.delete("stage");
    else url.searchParams.set("stage", stage);
    window.history.replaceState(null, "", url);
    document.querySelector(".hlw-insp")?.scrollTo({ top: 0 });
  }, [stage]);
  // The Explore room's promoted lanes (FXC-9009 two-rooms hero inversion +
  // phase-3 gestures): 1-up focus by default (the fused CL surface); click a
  // filmstrip card to focus it, drag one onto the hero to split (2-up → quad,
  // capped). Last layout persists (the "last-used" idiom; the three named
  // presets land in phase 5). When the geometry card is promoted, the live
  // section hero renders INSIDE its lane (exactly one canvas instance ever).
  const EXPLORE_LANES_KEY = "thread:v5:hlw:explore-lanes";
  const [exploreLanes, setExploreLanes] = useState<ExploreCardId[]>(() => {
    if (typeof window === "undefined") return ["cl"];
    try {
      const saved = normalizeLanes(JSON.parse(window.localStorage.getItem(EXPLORE_LANES_KEY) ?? "null"));
      return saved ?? ["cl"];
    } catch {
      return ["cl"];
    }
  });
  const handleExploreLanes = useCallback((lanes: ExploreCardId[]) => {
    const next = lanes.length > 0 ? lanes : (["cl"] as ExploreCardId[]);
    setExploreLanes(next);
    try {
      window.localStorage.setItem(EXPLORE_LANES_KEY, JSON.stringify(next));
    } catch {
      // best-effort persistence
    }
  }, []);

  // Tutorial tour (FXC-9009): a workbench-scoped OnboardingProvider whose challenges poll the LIVE
  // design state via this ref (refreshed every render). The tour walks four real edits — replace
  // foil, add cove, change aileron, Frise aileron — gated on the design actually changing from the
  // baseline captured when the tour starts. See ./highlift/tour/highliftTour.
  const tourStateRef = useRef<HighliftTourState>({ live: emptyTourLive(), baseline: null });
  tourStateRef.current.live = {
    foilDigest: mainCoordsDigest(config.main.coords),
    elementCount: elements.length,
    aileronDeflDeg: config.operation.flaperonAngleDeg,
    friseOn: elements.some(
      (e) => (e.kind === "flaperon-derived" || e.kind === "plain-flap") && e.frise === true,
    ),
  };
  const startTutorial = useCallback(() => {
    startHighliftTour({
      stateRef: tourStateRef,
      setStage: (s) => setStage(s),
      // Review fix: the rail defaults collapsed since the calm-down — the
      // tour's replace-foil/add-element steps live there, so it must expand.
      expandRail: () => setLayout((cur) => (cur.railCollapsed ? { ...cur, railCollapsed: false } : cur)),
    });
  }, [setStage]);
  const viewMoved = !isHomeView(foilView);

  // AC-H selection + hover read-out. `selectedId` is the selected element (drives the Shape panel +
  // handle emphasis); `hoverReadout` is the "what am I dragging" line in the hero HUD.
  const [selectedId, setSelectedId] = useState<string>("vane");
  const [hoverReadout, setHoverReadout] = useState<string>("hover a control point — or drag to reshape the section");
  // The corner "what am I dragging" pill is opt-in via the view kebab (the calm
  // default keeps the hero at two chrome clusters — FXC-9009 §5.1/acceptance 2).
  const [showHoverReadout, setShowHoverReadout] = useState(false);
  const onHoverHandle = useCallback(
    (h: { label: string; world: [number, number] } | null) => {
      setHoverReadout(
        h ? `${h.label}  (${h.world[0].toFixed(3)}, ${h.world[1].toFixed(3)})` : "hover a control point — or drag to reshape the section",
      );
    },
    [],
  );

  // AC-6 control-surface choice (Qiqi slotted ↔ xfoil plain-flap) — state-only this tranche, but it
  // shapes the FlapSpec the register/prior calls use (see registerHighliftComposite below).
  const [controlSurface, setControlSurface] = useState<ControlSurface>("qiqi");

  // AC-L global layout lock — freezes panel drag, dock resize, the data board tiles + PiP.
  const [locked, setLocked] = useState(false);
  // Sweep grid size, surfaced for the Sweep panel's dock-header badge.
  // "Fill view": frame the actual deployed foil (re-centres no matter how far it's
  // been deployed, dragged, or zoomed) from the live configuration's contours.
  const fillView = useCallback(() => {
    setFoilView(framePoints([configuration.main, configuration.vane, configuration.flaperon]));
  }, [configuration]);

  // --- saved geometries (AC-5): name the live design + reload one later -------
  // A compact Save/Load bar. The polished editor (add/move elements, control
  // surfaces, blowing) comes in the next tranche; this just persists + reloads
  // the existing HighLiftAirfoil shape verbatim.
  const [savedGeometries, setSavedGeometries] = useState<SavedGeometry[]>([]);
  const [geometryBusy, setGeometryBusy] = useState(false);
  const [geometryNote, setGeometryNote] = useState<string | null>(null);

  const refreshGeometries = useCallback(() => {
    if (!persistedProject) return;
    listHighliftGeometries(projectId, branch)
      .then((rows) => setSavedGeometries(rows))
      .catch((error: unknown) => {
        console.warn("[highlift] list geometries failed", error);
      });
  }, [projectId, branch, persistedProject]);

  useEffect(() => {
    refreshGeometries();
  }, [refreshGeometries]);

  // Save / promote the BUFFER. XFOIL PANE/EXEC: when the buffer was reshaped from a saved baseline
  // (loaded/committed) and has diverged, this commits it as a DERIVED variant — passing the baseline
  // slug so the server writes a `derived_from` lineage edge baseline → variant. A clean root design
  // (seed baseline, or no parent) saves with no parent edge. On success it re-baselines the buffer
  // (`commitBaseline`) so the live shape becomes the new "current".
  const handleSaveGeometry = useCallback(() => {
    const isVariant = isDirty && (baseline.source === "loaded" || baseline.source === "committed") && !!baseline.slug;
    const promptLabel = isVariant
      ? `Promote this reshaped buffer as a variant derived from “${baseline.name ?? baseline.slug}”. Name it:`
      : "Name this geometry";
    const name = window.prompt(promptLabel);
    if (name == null) return;
    const trimmed = name.trim();
    if (!trimmed) {
      setGeometryNote("Name required.");
      return;
    }
    setGeometryBusy(true);
    setGeometryNote(null);
    saveHighliftGeometry(projectId, branch, {
      name: trimmed,
      config,
      derived_from: isVariant ? baseline.slug : null,
    })
      .then((saved) => {
        // Promote: the just-committed shape becomes the new baseline (the buffer is now clean).
        commitBaseline({ name: saved.name, slug: saved.slug, nodeId: saved.node_id });
        const parent = saved.derived_from_slug ? ` · derived from “${baseline.name ?? saved.derived_from_slug}”` : "";
        const hash = saved.design_hash ? ` · #${saved.design_hash}` : "";
        setGeometryNote(`committed “${saved.name}”${parent}${hash}`);
        refreshGeometries();
      })
      .catch((error: unknown) => {
        setGeometryNote(error instanceof Error ? error.message : "save failed");
      })
      .finally(() => setGeometryBusy(false));
  }, [projectId, branch, config, isDirty, baseline, commitBaseline, refreshGeometries]);

  const handleLoadGeometry = useCallback(
    (slug: string) => {
      const match = savedGeometries.find((g) => g.slug === slug);
      if (!match) return;
      // Re-baseline to the loaded geometry (XFOIL "current" ← loaded foil) so reshaping diverges from it.
      loadConfig(match.config, { name: match.name, slug: match.slug, nodeId: match.node_id });
      // The loaded design is unrelated to any aircraft CS cut — drop the seed's cs_* provenance
      // (and the seed-mode control / two-body overlay with it).
      setCsSeed(null);
      clearAppliedSeed();
      setCsSeedMode("aileron");
      setCsSeedParametric(false);
      setGeometryNote(`loaded “${match.name}”`);
    },
    [savedGeometries, loadConfig],
  );

  // Deep link: `?geometry=<slug>` auto-loads that saved geometry once the list arrives,
  // so a design is shareable as a URL. Runs at most once per mount.
  const deepLinkedRef = useRef(false);
  useEffect(() => {
    if (deepLinkedRef.current) return;
    const slug = new URLSearchParams(window.location.search).get("geometry");
    if (!slug) return;
    const match = savedGeometries.find((g) => g.slug === slug);
    if (!match) return; // list may not have loaded yet — this effect re-runs when it does
    deepLinkedRef.current = true;
    loadConfig(match.config, { name: match.name, slug: match.slug, nodeId: match.node_id });
    setCsSeed(null);
    clearAppliedSeed();
    setCsSeedMode("aileron");
    setCsSeedParametric(false);
    setGeometryNote(`loaded “${match.name}” from link`);
  }, [savedGeometries, loadConfig]);

  // --- composite registration (guarded per project+branch, re-armed on solve) --
  // FXC-9009 loop-close: registration is no longer strictly one-shot. The mount
  // register seeds the provenance-bound snapshot; a completed solve re-arms the
  // guard (via `registerNonce`) so the snapshot is rebuilt from the NEW run nodes
  // and the trust-map / next-N refetch against fresh data — alongside the live
  // overlay the read endpoints already apply. The guard still collapses repeat
  // mounts / config edits to one register per (project, branch, nonce), so this
  // does not re-register on every keystroke.
  const registeredKeyRef = useRef<string | null>(null);
  const [registerState, setRegisterState] = useState<"idle" | "registering" | "done" | "error">("idle");
  const [registerNote, setRegisterNote] = useState<string | null>(null);
  const [registerNonce, setRegisterNonce] = useState(0);
  // The trust/surface refetch nonce — declared here (ahead of the register effect)
  // so the effect can bump it after a re-register without a use-before-define.
  const [trustNonce, setTrustNonce] = useState(0);

  useEffect(() => {
    if (!persistedProject) return;
    const key = `${projectId}::${branch}::${registerNonce}`;
    if (registeredKeyRef.current === key) return;
    registeredKeyRef.current = key;
    setRegisterState("registering");
    setRegisterNote(null);
    let cancelled = false;
    registerHighliftComposite(projectId, branch, {
      design: designPayload(config),
      // The control-surface choice (AC-6) shapes the FlapSpec: plain-flap pins the prior to a fixed
      // deflection; slotted leaves it to each point's flap_deploy_deg.
      flap: controlSurfaceFlapSpec(config, controlSurface),
      // Register BOTH flexfoil fidelities so the RHS map can show inviscid vs viscous.
      // Viscous returns empty at M>=0.15 (so it contributes no rows at the demo's M~0.2);
      // it appears at low Mach. See the RHS-map note + the handover for the full split.
      fidelities: ["inviscid", "viscous"],
    })
      .then((res: RegisterResponse) => {
        if (cancelled) return;
        setRegisterState("done");
        setRegisterNote(`${res.grid_points} grid points · ROM + CFD seeded`);
        // The snapshot just changed — refetch the trust-map / surface / next-N
        // against it (the live overlay already reflects the runs, this keeps the
        // provenance-bound snapshot view in lockstep too).
        setTrustNonce((nonce) => nonce + 1);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // Tolerate failure quietly — the explorer still works off the live grid.
        setRegisterState("error");
        setRegisterNote(error instanceof Error ? error.message : "register unavailable");
        // Allow a retry if the project/branch/nonce is revisited.
        registeredKeyRef.current = null;
        console.warn("[highlift] composite register failed", error);
      });
    return () => {
      cancelled = true;
    };
    // Register once per (project, branch, registerNonce). Mount seeds it; a solve
    // bumps registerNonce to re-register against the new run nodes. Config edits
    // alone drive solves, not re-registration.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, branch, persistedProject, registerNonce]);

  // Re-arm registration so the next effect run rebuilds the snapshot from the
  // live run nodes. Called once per solve completion (see runSolve).
  const reregisterComposite = useCallback(() => {
    registeredKeyRef.current = null;
    setRegisterNonce((nonce) => nonce + 1);
  }, []);

  // --- local-solve capability (FXC-9009 Phase 2) -------------------------------
  // Whether THIS API host can re-mesh + solve each sweep point locally. Read from
  // the priors response (the cheapest capability surface — one point). Default
  // false (conservative: assume cloud-only until we hear otherwise, so the Sweep
  // panel never stages a multi-deploy sweep that would all-fail on a single mesh).
  const [localSolveAvailable, setLocalSolveAvailable] = useState(false);
  useEffect(() => {
    let cancelled = false;
    // Gate on a persisted (UUID) project: highlift routes type project_id as a UUID and 422 a
    // raw slug. An unresolved slug URL leaves projectId non-UUID — skip the capability probe
    // instead of 422-ing (matches the other highlift effects' persistedProject guard).
    if (!persistedProject) { setLocalSolveAvailable(false); return; }
    fetchHighliftPriors(projectId, {
      // A single throwaway point — we only read the host capability flag, not the lift.
      points: [{ alpha_deg: alphaDeg, mach: flowConditions.mach, reynolds: flowConditions.reynolds, flap_deploy_deg: config.operation.deployAngleDeg }],
      flap: controlSurfaceFlapSpec(config, controlSurface),
      fidelities: ["inviscid"],
    })
      .then((res) => {
        if (!cancelled) setLocalSolveAvailable(Boolean(res.local_solve_available));
      })
      .catch(() => {
        // Capability unknown — stay conservative (cloud-only assumption).
        if (!cancelled) setLocalSolveAvailable(false);
      });
    return () => {
      cancelled = true;
    };
    // Capability is per-host, not per-edit: refetch only when the project changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, persistedProject]);

  // --- trust summary over the selected (axisX × axisY) plane --------------------
  // FXC-9009: the surface axes are user-selectable (default the α × deploy operating
  // plane). Putting a geometry shape DOF on an axis re-projects both the trust-map
  // summary and the 3D trust surface onto that axis — the backend accepts any
  // canonical composite axis (it 400s an unknown one).
  const [trust, setTrust] = useState<TrustMapResponse | null>(null);
  const [trustLoading, setTrustLoading] = useState(false);
  const [trustError, setTrustError] = useState<string | null>(null);
  const [trustAxisX, setTrustAxisX] = useState("alpha_deg");
  const [trustAxisY, setTrustAxisY] = useState("flap_deploy_deg");
  // `trustNonce` is declared up by the registration block (it is bumped there too).

  // The non-axis flow conditions held fixed for the compact trust summary and 3D
  // trust surface slice. Re is pinned on the schema's `re_chord_millions`
  // identity axis (in millions), and plotted axes are removed because an axis
  // cannot be both held and swept.
  const trustFixedForAxes = useCallback(
    (axisX: string, axisY: string) => {
      const base: Record<string, number> = {
        mach: flowConditions.mach,
        reynolds: flowConditions.reynolds,
        re_chord_millions: Math.round((flowConditions.reynolds / 1e6) * 10) / 10,
        disk_thrust_loading: config.operation.diskLoading,
        alpha_deg: alphaDeg,
        flap_deploy_deg: config.operation.deployAngleDeg,
      };
      for (const knob of KNOB_REGISTRY) {
        if (knob.group !== "Geometry") continue;
        const v = knob.read({
          alphaDeg,
          flowConditions: { mach: flowConditions.mach, reynolds: flowConditions.reynolds },
          config,
        });
        // Only pin a geometry axis the live config actually defines — null-safe knob
        // reads return NaN for an absent field; never send NaN as a held value.
        if (Number.isFinite(v)) base[knob.key] = v;
      }
      delete base[axisX];
      delete base[axisY];
      return base;
    },
    [flowConditions.mach, flowConditions.reynolds, config, alphaDeg],
  );
  const trustFixed = useMemo(
    () => trustFixedForAxes(trustAxisX, trustAxisY),
    [trustFixedForAxes, trustAxisX, trustAxisY],
  );
  // (The Data-mode tile board that consumed an α × deploy-fixed twin of this
  // record was retired with the dock/board restructure — only the selected-axes
  // slice below is fetched now.)

  // Dragging a geometry handle calls onSetControlPoint on EVERY pointermove, which
  // recomputes trustFixed. Feeding that straight into the surface query + trust-map
  // fetch fires one single-worker surrogate fit per move → the drag pegs the backend
  // (and refreshTrust depended on trustFixed's object identity, so the trust map
  // refetched every move even when no pinned value changed). Debounce the value that
  // drives those two fetches; the live geometry editor + cheap previews are unaffected.
  // The string key gives a stable identity that only changes on real content change. (FXC-9009)
  const trustFixedKey = JSON.stringify(trustFixed);
  const [debouncedFixedKey, setDebouncedFixedKey] = useState(trustFixedKey);
  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedFixedKey(trustFixedKey), 250);
    return () => window.clearTimeout(handle);
  }, [trustFixedKey]);
  const debouncedTrustFixed = useMemo(
    () => JSON.parse(debouncedFixedKey) as Record<string, number>,
    [debouncedFixedKey],
  );

  const refreshTrust = useCallback(() => {
    if (!persistedProject) return;
    if (trustAxisX === trustAxisY) {
      setTrust(null);
      setTrustLoading(false);
      setTrustError(null);
      return; // degenerate plane; the panel shows the empty state
    }
    let cancelled = false;
    setTrustLoading(true);
    setTrustError(null);
    fetchHighliftTrustMap(projectId, branch, HIGHLIFT_QUANTITY, trustAxisX, trustAxisY, debouncedTrustFixed)
      .then((res) => {
        if (!cancelled) setTrust(res);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setTrust(null);
        setTrustError(error instanceof Error ? error.message : "trust map unavailable");
      })
      .finally(() => {
        if (!cancelled) setTrustLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, branch, persistedProject, trustAxisX, trustAxisY, debouncedFixedKey, debouncedTrustFixed]);

  useEffect(() => {
    const cleanup = refreshTrust();
    return cleanup;
  }, [refreshTrust, trustNonce]);

  const { mix, total } = useMemo(() => trustMix(trust), [trust]);

  // --- fork seam: solve → invalidate the explorer's grid query -----------------
  const [solveState, setSolveState] = useState<SolveState>("idle");
  const [solveNote, setSolveNote] = useState<string | null>(null);

  // --- WebGPU in-browser solve backend (FXC-9009 P5; ADDITIVE, OPT-IN) ----------
  // A selectable alternative to the RANS/V100 path. Default 'rans' (untouched); the
  // WebGPU option only enables when an adapter is present. A successful WebGPU solve
  // is a LIVE PREVIEW: its FlowMesh is held in `localFlowMesh` and overrides the
  // server flow query, but isn't persisted to the grid (persistence is a later phase).
  // Default to the LOCAL V100 hook when it's configured (VITE_RANS_V100_URL) — otherwise the cloud
  // "rans" default 404s in local dev with no thread-api. Prod/preprod (no hook) keep the cloud default.
  const [solveBackend, setSolveBackend] = useState<"rans" | "webgpu" | "rans-v100">(() => (ransV100Enabled() ? "rans-v100" : "rans"));
  const webgpuSolver = useMemo(() => createWebGpuSolver(), []);
  const [webgpuAvailable, setWebgpuAvailable] = useState(false);
  useEffect(() => {
    void Promise.resolve(webgpuSolver.available()).then(setWebgpuAvailable);
  }, [webgpuSolver]);
  const [localFlowMesh, setLocalFlowMesh] = useState<FlowMesh | null>(null);

  // --- NN flowfield preview (FXC-9009): the coordinate-MLP predicts the WHOLE field client-side
  // (Web Worker; WebGPU matmul with a verified JS fallback) and renders in the live FlowVisLayer.
  // Instant on a geometry/condition edit (no solve). `nnPreview` overrides the displayed flow, so
  // you can flip NN vs the RANS field for the same point. Weights ship at public/flowfield_mlp_web.json.
  const [nnPreview, setNnPreview] = useState(false);
  // FXC-9009: NN warm-start the solve (default ON — the headline "augment, not replace"). Toggling
  // OFF sends no warm_start, so the solve runs COLD — lets the demo show the cold→warm speedup A/B.
  const [warmStartOn, setWarmStartOn] = useState(true);
  // "Watch it converge": replay a V100 RANS solve (NN warm-start guess → converged) over the hero stage.
  const [convPlay, setConvPlay] = useState(false);
  const [nnFlowMesh, setNnFlowMesh] = useState<FlowMesh | null>(null);
  // Instant NN aero surrogate (CL/CD by surface-Cp integration) for the previewed point — the
  // worker returns it alongside the field, so it updates live with the geometry/condition.
  const [nnForces, setNnForces] = useState<{ CL: number; CD: number } | null>(null);
  const nnWorkerRef = useRef<Worker | null>(null);
  const nnReqRef = useRef(0);
  useEffect(() => {
    let worker: Worker;
    try {
      worker = new Worker(new URL("../highlift/flowfield-nn/nnWorker.ts", import.meta.url), { type: "module" });
    } catch {
      return;
    }
    worker.onmessage = (e: MessageEvent<{ id: number; mesh?: FlowMesh; forces?: { CL: number; CD: number } }>) => {
      if (e.data.id !== nnReqRef.current) return;
      if (e.data.mesh) setNnFlowMesh(e.data.mesh);
      if (e.data.forces) setNnForces(e.data.forces);
    };
    nnWorkerRef.current = worker;
    return () => { nnWorkerRef.current = null; worker.terminate(); };
  }, []);
  // Re-predict on geometry / condition change while the NN preview is on (debounced).
  useEffect(() => {
    if (!nnPreview || !nnWorkerRef.current) return;
    const handle = window.setTimeout(() => {
      const elements = buildConfiguration(config).elements.map((el) => {
        const label = String(el.label ?? el.id ?? "").toLowerCase();
        const name = label.includes("flap") ? "flap" : (label.includes("vane") || label.includes("slat")) ? "vane" : "main";
        return { name, contour: el.contour as number[][] };
      });
      nnReqRef.current += 1;
      nnWorkerRef.current!.postMessage({
        id: nnReqRef.current,
        // FXC-9009: prefer this project's registered NN model (a project artifact) over the
        // static build asset; the worker falls back to the static when none is registered.
        weightsUrl: projectId
          ? `/api/projects/${projectId}/highlift/nn/model`
          : `${import.meta.env.BASE_URL ?? "/"}flowfield_mlp_web.json`,
        fallbackUrl: `${import.meta.env.BASE_URL ?? "/"}flowfield_mlp_web.json`,
        cond: {
          alpha: alphaDeg,
          mach: flowConditions.mach,
          re: flowConditions.reynolds,
          blow: config.operation.diskLoading ?? 0,
        },
        elements,
        opts: { W: 160, H: 104 },
      });
    }, 130);
    return () => window.clearTimeout(handle);
  }, [nnPreview, config, alphaDeg, flowConditions.mach, flowConditions.reynolds]);

  // --- DEV-ONLY: "V100 dev box" backend (FXC-9009; ADDITIVE, FLAG-GATED) -----
  // A free/fast/local high-fidelity RANS path on the V100 box, beside the cloud
  // "Flow360 (cloud)" fork and the WebGPU preview. Hidden unless VITE_RANS_V100_URL
  // points at the local service (tools/rans_v100_service.py, reached over an SSH
  // tunnel). It serializes the live geometry to the run_case.py contours JSON, POSTs
  // it, and polls the converged forces (~1–6 min). The aborter cancels the long poll
  // on unmount / backend switch.
  const ransV100On = useMemo(() => ransV100Enabled(), []);
  const [ransV100Result, setRansV100Result] = useState<RansV100Result | null>(null);
  const ransV100AbortRef = useRef<AbortController | null>(null);
  useEffect(() => () => ransV100AbortRef.current?.abort(), []);
  // A backend the build doesn't expose can't stay selected: if the flag is off, fall back to RANS.
  useEffect(() => {
    if (solveBackend === "rans-v100" && !ransV100On) setSolveBackend("rans");
  }, [solveBackend, ransV100On]);

  // --- DEV-ONLY: server-side 2nd-order CPU refine (Phase C; ADDITIVE, FLAG-GATED) ----
  // Hidden unless VITE_EULER_REFINE_URL points at the local refine service
  // (tools/euler_refine_service.py). It POSTs the just-solved WebGPU mesh
  // (localFlowMesh.points/tris → legacy VTK) and polls a CPU-f64 2nd-order JFNK
  // solve (~10 min). NOT interactive — a faithful refine BESIDE the 1st-order GPU
  // result. The aborter cancels the long poll on unmount / re-refine.
  const refineOn = useMemo(() => refineEnabled(), []);
  const [refineResult, setRefineResult] = useState<RefineResult | null>(null);
  const [refineBusy, setRefineBusy] = useState(false);
  const [refineNote, setRefineNote] = useState<string | null>(null);
  const refineAbortRef = useRef<AbortController | null>(null);
  useEffect(() => () => refineAbortRef.current?.abort(), []);

  const handleRefine = useCallback(() => {
    if (!localFlowMesh || localFlowMesh.points.length < 6 || localFlowMesh.tris.length < 3) {
      setRefineNote("Solve a WebGPU point first — no mesh to refine.");
      return;
    }
    refineAbortRef.current?.abort();
    const ac = new AbortController();
    refineAbortRef.current = ac;
    setRefineBusy(true);
    setRefineResult(null);
    setRefineNote("submitting mesh to CPU refine…");
    let meshVtk: string;
    try {
      meshVtk = flowMeshToVtk({ points: localFlowMesh.points, tris: localFlowMesh.tris });
    } catch (error: unknown) {
      setRefineBusy(false);
      setRefineNote(error instanceof Error ? error.message : "mesh serialize failed");
      return;
    }
    void refineSecondOrder(
      { meshVtk, alphaDeg, mach: flowConditions.mach, secondOrder: true },
      {
        signal: ac.signal,
        onProgress: (p) => {
          if (p.status === "running") setRefineNote(`2nd-order CPU solve running… ${Math.round(p.elapsedSec)}s (~10 min)`);
        },
      },
    )
      .then((res) => {
        setRefineResult(res);
        setRefineNote(`2nd-order CL ${res.cl.toFixed(4)} · ΔCL ${(res.cl - (lastCoeff?.cl ?? res.cl)).toFixed(4)} vs GPU 1st-order`);
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") return; // cancelled — silent
        setRefineNote(error instanceof Error ? error.message : "refine failed");
      })
      .finally(() => setRefineBusy(false));
  }, [localFlowMesh, alphaDeg, flowConditions.mach, lastCoeff]);

  // FXC-9009 P3 — persisted warm-start session for LIVE drag-to-resolve. Lazily created on the
  // first WebGPU solve (holds the wgpu device + last converged field), so a control-point drag
  // re-solves cheaply via `session.resolve` (warm) instead of a cold round-trip. Freed when the
  // backend switches away from WebGPU or the surface unmounts (see the cleanup effect below).
  // A `getSession` in-flight promise dedupes concurrent first-solve attempts.
  const sessionRef = useRef<WebGpuSession | null>(null);
  const sessionPromiseRef = useRef<Promise<WebGpuSession> | null>(null);
  const getSession = useCallback(async (): Promise<WebGpuSession> => {
    if (sessionRef.current) return sessionRef.current;
    if (!sessionPromiseRef.current) {
      sessionPromiseRef.current = webgpuSolver.createSession().then((s) => {
        sessionRef.current = s;
        return s;
      });
      sessionPromiseRef.current.catch(() => {
        sessionPromiseRef.current = null; // allow a retry on the next solve
      });
    }
    return sessionPromiseRef.current;
  }, [webgpuSolver]);
  const freeSession = useCallback(() => {
    sessionRef.current?.free();
    sessionRef.current = null;
    sessionPromiseRef.current = null;
  }, []);

  // Assemble the WebGpuSolveInput from the live config + α exactly as solveWebGpu does
  // (elements from buildConfiguration, disks from blowingOf, flow from buildSweepInputs).
  // Shared by the cold solve + the warm drag-to-resolve so they re-mesh the same geometry.
  const assembleWebGpuInput = useCallback(
    (cfg: HighLiftAirfoil, alpha: number): WebGpuSolveInput => ({
      elements: buildConfiguration(cfg).elements.map((el) => ({
        name: el.label ?? el.id,
        contour: el.contour,
      })),
      // Disks are accepted but IGNORED by the solver this phase (blowing is later).
      disks: blowingOf(cfg).map((d) => ({
        center: [d.x, d.z],
        radius: d.diameter / 2,
        thickness: d.diameter / 16,
        axis: [-Math.cos((d.tiltDeg * Math.PI) / 180), -Math.sin((d.tiltDeg * Math.PI) / 180)],
        loading: cfg.operation.diskLoading,
      })),
      flow: buildSweepInputs(cfg).flow,
      alphaDeg: alpha,
    }),
    [],
  );

  // Free the session when leaving the WebGPU backend, and on unmount. Re-selecting WebGPU
  // lazily creates a fresh session (its first solve is cold again).
  useEffect(() => {
    if (solveBackend !== "webgpu") freeSession();
  }, [solveBackend, freeSession]);
  useEffect(() => () => freeSession(), [freeSession]);

  // "Solve next N (Tailor)" now PROPOSES into an editable queue (which points + why),
  // rather than forking immediately. The user can change N, edit α/flap, drop rows,
  // then run the queue. queueLoading = fetching the proposal.
  const [queue, setQueue] = useState<QueuedPoint[]>([]);
  const [queueN, setQueueN] = useState(SOLVE_NEXT_BUDGET);
  const [queueLoading, setQueueLoading] = useState(false);
  // small inline styles for the queue panel (palette-driven)
  const qInput: CSSProperties = { ...mono11, width: 50, background: palette.surface2, border: `1px solid ${palette.line}`, color: palette.ink, borderRadius: 4, padding: "2px 4px" };
  const stepBtn: CSSProperties = { ...mono11, cursor: "pointer", color: palette.ink, background: palette.surface2, border: `1px solid ${palette.line}`, borderRadius: 4, padding: "0 7px", lineHeight: "18px" };
  const ghostBtn: CSSProperties = { ...mono11, cursor: "pointer", color: palette.ink3, background: "transparent", border: `1px solid ${palette.line}`, borderRadius: 4, padding: "1px 7px" };
  const refreshTimers = useRef<number[]>([]);

  // Live solve progress (local backend): polled while a solve is in flight to drive
  // a phase + % bar. `clHistory` is the real CL convergence trajectory streamed from
  // the solver; `clCursor` replays the big marker through it (the GPU burst is too
  // fast to stream step-by-step, so we replay the recorded trajectory instead).
  const [solveProgress, setSolveProgress] = useState<SolveProgress | null>(null);
  const [clHistory, setClHistory] = useState<number[]>([]);
  const [clCursor, setClCursor] = useState(0);
  const progressTimer = useRef<number | null>(null);
  // Poll-generation stamp (FXC-9009 "solve stuck in a little loop"): the embedded solve
  // BLOCKS the single-worker API, so poll GETs issued while it runs queue up browser- and
  // server-side and only drain AFTER the solve returns. Clearing the interval does not
  // cancel those in-flight fetches — without a stamp their late `.then`s kept re-seeding
  // `clHistory`/`solveProgress` for tens of seconds, resurrecting the convergence overlay
  // in an endless replay loop. Bump on stop; responses from an older generation are dropped.
  const progressGenRef = useRef(0);
  // One poll in flight at a time — while the API is busy the naive interval stacked
  // ~2 fetches/s into that queue (≈100 for a minute-long solve); this cap means at most
  // ONE stale response can ever drain late (and the stamp above drops even that one).
  const progressInFlightRef = useRef(false);
  const stopProgressPolling = useCallback(() => {
    progressGenRef.current += 1;
    progressInFlightRef.current = false;
    if (progressTimer.current != null) {
      clearInterval(progressTimer.current);
      progressTimer.current = null;
    }
  }, []);
  useEffect(() => () => stopProgressPolling(), [stopProgressPolling]);

  // Replay: sweep the cursor toward the latest known trajectory point (~22/s). The
  // interval restarts when the trajectory grows (each poll), and rests at the end.
  useEffect(() => {
    if (clHistory.length === 0) return;
    const id = window.setInterval(() => {
      setClCursor((c) => Math.min(c + 1, clHistory.length - 1));
    }, 45);
    return () => clearInterval(id);
  }, [clHistory.length]);

  // Once the solve has ended AND the replay has caught up, hold the converged value
  // briefly, then clear so the overlay fades and the flow field (already loaded) shows.
  useEffect(() => {
    if (solveState !== "done" && solveState !== "error") return;
    if (clHistory.length === 0 || clCursor < clHistory.length - 1) return;
    const id = window.setTimeout(() => {
      setClHistory([]);
      setClCursor(0);
      setSolveProgress(null);
    }, 1100);
    return () => clearTimeout(id);
  }, [solveState, clCursor, clHistory.length]);

  // Clear any pending delayed refetch timers on unmount.
  useEffect(
    () => () => {
      for (const id of refreshTimers.current) window.clearTimeout(id);
      refreshTimers.current = [];
    },
    [],
  );

  const refreshGrid = useCallback(() => {
    // The seam: AdbExplorerSurface's query key (ThreadV5AdbExplorer.tsx).
    void queryClient.invalidateQueries({ queryKey: ["v5-adb-explorer", projectId, branch] });
    // The run-table feeds the Analysis polars + ConditionPivotTable; invalidate so
    // a freshly-solved point shows there too (this key has no nonce — see above).
    void queryClient.invalidateQueries({ queryKey: ["run-table", projectId, branch, "full"] });
    // FXC-9009: bump the trust nonce so the surface / trust-map / next-N refetch.
    // The backend read endpoints now fold the live run nodes in (highlift_live
    // overlay), so this refetch alone closes the loop visually. Snapshot
    // re-registration is fired ONCE per solve (not on every delayed refresh) —
    // see runSolve's reregisterComposite() call.
    setTrustNonce((nonce) => nonce + 1);
  }, [queryClient, projectId, branch]);

  const refreshFlowRun = useCallback((runId: string) => {
    void queryClient.invalidateQueries({ queryKey: ["highlift-flow", projectId, branch, runId] });
  }, [queryClient, projectId, branch]);

  const scheduleDelayedRefresh = useCallback(() => {
    for (const id of refreshTimers.current) window.clearTimeout(id);
    refreshTimers.current = POST_SOLVE_REFRESH_DELAYS.map((delay) =>
      window.setTimeout(() => refreshGrid(), delay),
    );
  }, [refreshGrid]);

  const summarizeSolve = useCallback((res: SolveResponse): string => {
    // res.failed already includes errored submissions; res.cached is the count
    // served from the cross-project cache (no fork). Don't re-derive either.
    const forked = res.submitted.filter((s) => s.case_id && !s.error).length;
    const failed = res.failed;
    const cached = res.cached;
    const bits = [`forked ${forked}`, `cached ${cached}`];
    if (failed) bits.push(`${failed} failed`);
    if (res.note) bits.push(res.note);
    return bits.join(" · ");
  }, []);

  const runSolve = useCallback(
    async (points: SolvePoint[], emptyMessage: string) => {
      if (!points.length) {
        setSolveState("error");
        setSolveNote(emptyMessage);
        return null;
      }
      // Highlift /solve types project_id as a UUID and 422s a raw slug. Don't fire a solve
      // for a non-persisted (unresolved-slug) project — surface a clear message instead.
      if (!persistedProject) {
        setSolveState("error");
        setSolveNote("This view isn't backed by a saved project — open a persisted project to solve.");
        return null;
      }
      // HARD GATE: never hand the mesher overlapping element contours - an
      // overlap does not fail loudly, it burns the mesher on an impossible
      // boundary (warm-solve exit 2, measured). Check the EXACT configuration
      // being meshed and refuse with the pair + penetration depth.
      {
        const boundary = meshBoundaryCheck(config);
        if (!boundary.ok) {
          const v = boundary.violations[0]!;
          setSolveState("error");
          setSolveNote(
            `Blocked before meshing: ${v.a} overlaps ${v.b}`
            + (v.penetrationC > 0 ? ` by ${(100 * v.penetrationC).toFixed(2)}%c` : " (contours touch)")
            + " - separate the elements (drag handles / reduce deflection) and re-solve.",
          );
          return null;
        }
      }
      setSolveState("solving");
      setSolveNote(null);
      setLocalFlowMesh(null); // a RANS solve hands the viewer back to the server field
      // Live-progress: name the local run dir with a token and poll it while the
      // (blocking) solve runs, so the overlay can stream the convergence + CL.
      const token = genHexToken();
      setSolveProgress(null);
      setClHistory([]);
      setClCursor(0);
      stopProgressPolling();
      const progressGen = progressGenRef.current;
      progressTimer.current = window.setInterval(() => {
        if (progressInFlightRef.current) return; // one at a time — never stack on a busy API
        progressInFlightRef.current = true;
        void fetchHighliftSolveProgress(projectId, token, branch)
          .then((p) => {
            // Stale drain: the solve already finished (or a new one started) while this
            // response was queued behind the blocking solve — never re-seed the overlay.
            if (progressGenRef.current !== progressGen) return;
            if (!p) return;
            setSolveProgress(p);
            // Adopt the server's authoritative CL trajectory (the replay sweeps the
            // marker through it). Fall back to appending the single live CL.
            const hist = p.cl_history;
            if (Array.isArray(hist) && hist.length) {
              setClHistory((prev) => (hist.length >= prev.length ? hist : prev));
            } else if (typeof p.CL === "number" && Number.isFinite(p.CL)) {
              const cl = p.CL;
              setClHistory((h) => (h.length && h[h.length - 1] === cl ? h : [...h, cl]).slice(-160));
            }
          })
          .catch(() => {})
          .finally(() => {
            if (progressGenRef.current === progressGen) progressInFlightRef.current = false;
          });
      }, 400);
      try {
        // Send per-point local inputs so deploy-specific Tailor batches re-mesh
        // the same geometry they register in provenance/cache.
        const sweepInputs = buildSweepInputs(config);
        const localInputs = points.map((point) => {
          const deploy = Number(point.flap_deploy_deg);
          const pointConfig = Number.isFinite(deploy)
            ? { ...config, operation: { ...config.operation, deployAngleDeg: deploy } }
            : config;
          const inputs = pointConfig === config ? sweepInputs : buildSweepInputs(pointConfig);
          return {
            elements: inputs.elements,
            // FXC-9009 N-disk wire: send ALL blowing sources (+ the legacy single-disk alias for
            // back-compat with older API hosts that only read `disk`).
            disks: inputs.disks,
            disk: inputs.disk,
            temperature: point.temperature ?? inputs.flow.temperature,
          };
        });
        const res = await solveHighliftRuns(
          projectId,
          {
            points,
            design: designPayload(config),
            operation: { ...config.operation },
            elements: sweepInputs.elements,
            local_inputs: localInputs,
            progress_token: token,
            // Buffer-lineage: tag the run with the COMMITTED variant slug only when the live buffer
            // still matches its saved baseline (clean). A dirty/uncommitted buffer keys on its own
            // design_hash, but isn't linked to a named variant until promoted.
            geometry_slug:
              !isDirty && (baseline.source === "loaded" || baseline.source === "committed")
                ? baseline.slug
                : null,
            // FXC-9009: warm-start the solve from the flowfield-NN (~2.5-3× fewer steps to the same
            // CL) — the local backend applies the proven NN→restart recipe; the cloud fork ignores
            // it. Gated by warmStartOn so the demo can toggle it off for the cold A/B baseline.
            warm_start: warmStartOn
              ? {
                  mode: "nn-flowfield",
                  weights_url: `${import.meta.env.BASE_URL ?? "/"}flowfield_mlp_web.json`,
                  blow: config.operation.diskLoading ?? 0,
                }
              : null,
          },
          branch,
        );
        setSolveState("done");
        setSolveNote(summarizeSolve(res));
        // Focus a run that has a recallable slice RIGHT NOW — a cache hit or a
        // completed local solve — NOT an arbitrary pending fork, and sync the foil to
        // its condition so the geometry layer matches the flow slice. This fixes the
        // batch-solve bug where the field blanked (focused run 404s until ingest) and
        // the foil "snapped to the wrong geometry" (focused run ≠ displayed deploy).
        const recallable =
          res.submitted.find((s) => s.run_path && s.backend === "cache") ??
          res.submitted.find((s) => s.run_path && s.CL != null) ??
          null;
        if (recallable?.run_path) {
          const deploy = Number(recallable.flap_deploy_deg);
          if (Number.isFinite(deploy)) setDeployDeg(deploy);
          if (Number.isFinite(recallable.alpha_deg)) setAlphaDeg(recallable.alpha_deg);
          setSolvedFlowRun(recallable.run_path);
          setPendingFlowRun(null);
          refreshFlowRun(recallable.run_path);
        }
        // Capture coefficients for the hero HUD (any submission that returned them).
        const coeffSub = res.submitted.find((s) => s.CL != null) ?? null;
        if (coeffSub && coeffSub.CL != null) {
          setLastCoeff({ cl: coeffSub.CL, cd: coeffSub.CD ?? null, ld: coeffSub.L_over_D ?? null });
        }
        // else: leave the current flow field on screen — pending forks surface on the
        // grid as their cases ingest; don't blank the viewer for a miss-only batch.
        // Two-rooms Study Cursor (§5.4): a Design-room solve writes the solved
        // condition back as the SHARED selected point — the Explore geometry card
        // and every synced surface highlight follow it. Prefer the recallable
        // submission (it carries a run_path); fall back to the first point solved.
        {
          const focus = recallable ?? res.submitted.find((s) => !s.error) ?? null;
          const basePoint = points[0];
          if (focus && basePoint) {
            // Pure mapping (solveFocusToSelectedPoint, unit-pinned): every axis
            // reads the FOCUSED submission first, falling back to the request
            // point only when the response omits a field — no chimera vectors.
            patchStudyCursor(projectId, branch, {
              selectedPoint: solveFocusToSelectedPoint(focus, basePoint, config.operation),
            });
          }
        }
        refreshGrid();
        scheduleDelayedRefresh();
        // FXC-9009: re-arm the composite registration ONCE per solve so the
        // provenance-bound snapshot is rebuilt from the new run nodes (the live
        // overlay already reflects them in refreshGrid's refetch above; this keeps
        // the persisted snapshot in lockstep for later remounts / saved plots).
        reregisterComposite();
        return res;
      } catch (error: unknown) {
        setSolveState("error");
        setSolveNote(error instanceof Error ? error.message : "solve failed");
        return null;
      } finally {
        // Stop polling, but leave the trajectory so the marker finishes replaying the
        // convergence; the clear-after-done effect tidies up once the sweep lands.
        stopProgressPolling();
      }
    },
    [projectId, branch, persistedProject, warmStartOn, config, isDirty, baseline, summarizeSolve, refreshFlowRun, refreshGrid, scheduleDelayedRefresh, reregisterComposite, stopProgressPolling, setDeployDeg],
  );

  // ── Tailor fill-campaign seam (FXC-9009 follow-on) ──────────────────────────
  // The RANGE-driven sibling of runSolve: FillPanel declares a box + budget, the server-side
  // campaign runner adaptively fills it through the SAME /highlift/solve path, and we poll the
  // campaign row every 4s — firing runSolve's exact post-solve trio (refreshGrid +
  // scheduleDelayedRefresh + reregisterComposite) whenever the completed count ticks up, so
  // campaign points land on the ADB grid / trust map live, indistinguishable from a manual
  // sweep. The active campaign id also re-attaches on mount (list → newest running/paused),
  // so a page refresh mid-campaign resumes the live view instead of losing it.
  const [campaignId, setCampaignId] = useState<string | null>(null);
  const [campaignBusy, setCampaignBusy] = useState(false);
  const [campaignNote, setCampaignNote] = useState<string | null>(null);

  // Re-attach on mount: a campaign is a SERVER-side loop, so it survives a page refresh —
  // find the newest still-active one and adopt it (the list is newest-first, server-capped).
  // (The old Grid|Tailor-fill toggle auto-switch went with the merged axis table —
  // the ONE table always shows the attached campaign's status inline.)
  const adoptedCampaignRef = useRef<string | null>(null);
  // Mirror of campaignId for the adoption retry below: the effect deliberately
  // excludes campaignId from its deps (re-running per attach would loop), so a
  // late-resolving retry reads the CURRENT attachment through this ref and
  // never stomps a campaign the user just started with a stale older one.
  const campaignIdRef = useRef<string | null>(null);
  useEffect(() => {
    campaignIdRef.current = campaignId;
  }, [campaignId]);
  useEffect(() => {
    if (!persistedProject) return;
    let cancelled = false;
    let timer: number | null = null;
    let attempts = 0;
    const attempt = () => {
      listHighliftCampaigns(projectId, branch)
        .then((rows) => {
          if (cancelled || campaignIdRef.current != null) return;
          const active = rows.find((r) => r.status === "running" || r.status === "paused");
          // Fall back to the newest COMPLETED campaign so the fill replay works
          // after a page refresh (a finished pitch demo would otherwise have no
          // steps). Adoption only sets the campaign id — the Grid|Tailor-fill
          // toggle is auto-switched for ACTIVE campaigns alone, and never over an
          // explicit user choice.
          const adopt = active ?? rows.find((r) => r.status === "completed") ?? null;
          if (adopt) {
            setCampaignId(adopt.campaign_id);
            adoptedCampaignRef.current = adopt.campaign_id;
          }
        })
        .catch(() => {
          // RETRY, don't die: on a cold single-worker API the first fetches can
          // fail/stall while the boot computations drain — a one-shot silent
          // catch here left the autoplay replay with nothing to play (FXC-9009).
          // A genuinely campaign-less older API keeps 404ing; we stop after the
          // bounded window and stay honestly idle.
          if (cancelled || attempts >= 24) return;
          attempts += 1;
          timer = window.setTimeout(attempt, 5000);
        });
    };
    attempt();
    return () => {
      cancelled = true;
      if (timer != null) window.clearTimeout(timer);
    };
  }, [projectId, branch, persistedProject]);

  // Poll the attached campaign while it's alive. react-query owns the interval (the same
  // idiom as the pending-flow poll above): the refetchInterval callback reads the freshest
  // status, so polling stops by itself when the runner reaches a terminal state.
  const campaignQuery = useQuery<CampaignDetail>({
    queryKey: ["highlift-campaign", projectId, branch, campaignId],
    queryFn: () => fetchHighliftCampaign(projectId, campaignId!, branch),
    enabled: persistedProject && !!campaignId,
    refetchInterval: (query) => {
      // No data yet (including a failed FIRST fetch — e.g. the create→commit
      // visibility race, a dev-server reload, a transient 500): keep polling.
      // With retry:false a data-gated interval would otherwise die permanently
      // on one miss and the panel would silently stop tracking a live runner.
      if (!query.state.data) return CAMPAIGN_POLL_MS;
      const status = query.state.data.state?.status;
      return status === "running" || status === "paused" ? CAMPAIGN_POLL_MS : false;
    },
    // A campaign is a SERVER-side loop that keeps solving while the user works in
    // another tab — keep tracking it there (react-query pauses intervals in
    // unfocused tabs by default, which froze the panel at its first response).
    refetchIntervalInBackground: true,
    retry: false,
  });
  const campaign = campaignQuery.data ?? null;
  // Surface a fetch error on the panel rather than dying silently — the poll above keeps
  // retrying, so this heals itself and the note clears on the next good response.
  const campaignFetchNote =
    campaignQuery.error && campaignId
      ? `campaign state fetch failing (retrying): ${campaignQuery.error instanceof Error ? campaignQuery.error.message : "error"}`
      : null;

  // The completed-count seam: each time the campaign's completed counter increases, fire the
  // SAME post-solve trio runSolve uses. Keyed per campaign id so re-attaching to a half-done
  // campaign doesn't fire a spurious refresh for the points that were already on the grid.
  const campaignProgressRef = useRef<{ id: string | null; completed: number }>({ id: null, completed: 0 });
  useEffect(() => {
    const id = campaign?.campaign_id ?? null;
    const done = campaign?.state?.completed ?? 0;
    const prev = campaignProgressRef.current;
    if (id && prev.id === id && done > prev.completed) {
      refreshGrid();
      scheduleDelayedRefresh();
      reregisterComposite();
    }
    campaignProgressRef.current = { id, completed: done };
  }, [campaign, refreshGrid, scheduleDelayedRefresh, reregisterComposite]);

  // ── Fill replay (FXC-9009): replay the campaign's dispatch log chronologically ──
  // The pitch move: after (or during) a fill, step through the points in the order
  // Tailor picked them — the hero section tweens its flap to each point's deploy
  // angle over the SAME imperative previewDeploy channel as "Animate deploy"
  // (mutually exclusive with it), while a corner HUD narrates the condition, the
  // CL it landed, the tier that served it, and the acquisition's verbatim reason.
  // α commits per STEP via setAlphaDeg (cheap HUD state); deploy never touches
  // React state until the replay stops (single no-flicker commit, same as
  // stopAnimateDeploy).
  const REPLAY_TWEEN_MS = 900;
  const REPLAY_HOLD_MS = 1500;
  const [replayingFill, setReplayingFill] = useState(false);
  const [replayEntry, setReplayEntry] = useState<{
    step: ReplayStep;
    index: number;
    total: number;
    /** The snapshot the cursor indexes — the LIVE replaySteps memo grows every
     *  poll during a running campaign, shifting indices when a pending point
     *  settles mid-batch; the fill map must draw the array the cursor means. */
    steps: ReplayStep[];
  } | null>(null);
  const replayGenerationRef = useRef(0);
  const replayRafRef = useRef<number | null>(null);
  const replayTimerRef = useRef<number | null>(null);
  // Active-ness lives in a ref so stopReplayFill can be called from ANY seam
  // (campaign switch, unmount, the other animation's toggle) without touching
  // the shared previewDeploy channel when no replay is actually running —
  // otherwise a no-op stop would clear a live animate-deploy preview.
  const replayActiveRef = useRef(false);
  const replaySteps = useMemo(() => campaignReplaySteps(campaign), [campaign]);
  const stopReplayFill = useCallback(() => {
    if (!replayActiveRef.current) return;
    replayActiveRef.current = false;
    replayGenerationRef.current += 1;
    if (replayRafRef.current != null) {
      cancelAnimationFrame(replayRafRef.current);
      replayRafRef.current = null;
    }
    if (replayTimerRef.current != null) {
      window.clearTimeout(replayTimerRef.current);
      replayTimerRef.current = null;
    }
    heroCanvasRef.current?.clearDeployPreview();
    if (lastPreviewDeployRef.current != null) {
      setDeployDeg(lastPreviewDeployRef.current);
      lastPreviewDeployRef.current = null;
    }
    setReplayEntry(null);
    setReplayingFill(false);
  }, [setDeployDeg]);
  useEffect(() => {
    stopReplayFillRef.current = stopReplayFill;
  }, [stopReplayFill]);
  // A replay narrates ONE campaign — switching the attached campaign
  // (startCampaign, re-attach adoption) stops any replay of the old one.
  useEffect(() => {
    stopReplayFillRef.current();
  }, [campaignId]);
  const toggleReplayFill = useCallback(() => {
    if (replayingFill) {
      stopReplayFill();
      return;
    }
    // Freeze a snapshot: a live campaign appends points every 4s poll and the
    // memo identity changes with it — the replay shows what existed when it
    // started (restart it to include newer points) instead of restarting itself.
    const steps = replaySteps.slice();
    if (steps.length === 0) return;
    // Capture where the section actually is BEFORE stopping the other
    // animation: stopAnimateDeploy commits and NULLS lastPreviewDeployRef, and
    // the closure's config deploy angle is the pre-sweep value — starting the
    // first tween from it would visibly snap.
    const startDeploy = lastPreviewDeployRef.current ?? config.operation.deployAngleDeg ?? 0;
    stopAnimateDeploy(); // shared previewDeploy channel — never both
    setHeroView("2d"); // the replay draws on the 2D canvas — never run it hidden
    replayActiveRef.current = true;
    setReplayingFill(true);
    const generation = replayGenerationRef.current + 1;
    replayGenerationRef.current = generation;
    const playFrom = (k: number, fromDeploy: number): void => {
      if (replayGenerationRef.current !== generation) return;
      const step = steps[k];
      if (!step) {
        // Natural end of the pass: the autoplay choreography ends by promoting
        // the trust map (§5.7) — a manual stop never does.
        const finished = onReplayNaturalEndRef.current;
        stopReplayFill();
        finished();
        return;
      }
      setReplayEntry({ step, index: k, total: steps.length, steps });
      setAlphaDeg(step.alphaDeg);
      // Each replayed point becomes the SHARED selected point (§5.4): the
      // geometry card re-rigs, every surface highlights it, and the flow slice
      // follows its run — one commit per step (~2.4s), never per frame.
      patchStudyCursor(projectId, branch, {
        selectedPoint: {
          runNodeId: step.runPath,
          inputs: {
            alpha_deg: step.alphaDeg,
            flap_deploy_deg: step.deployDeg,
            flaperon_deflection_deg: step.flaperonDeg,
            mach: step.mach,
            re_chord_millions: step.reMillions,
            disk_thrust_loading: step.blowing,
          },
        },
      });
      const span = step.deployDeg - fromDeploy;
      const start = performance.now();
      const tick = (now: number): void => {
        replayRafRef.current = null;
        if (replayGenerationRef.current !== generation) return;
        const frac = Math.min(1, (now - start) / REPLAY_TWEEN_MS);
        // Ease-in-out so each point lands softly rather than snapping.
        const eased = frac < 0.5 ? 2 * frac * frac : 1 - ((2 - 2 * frac) ** 2) / 2;
        const angle = fromDeploy + eased * span;
        lastPreviewDeployRef.current = angle;
        heroCanvasRef.current?.previewDeploy(angle);
        previewSlotHealth(angle);
        if (frac < 1) {
          replayRafRef.current = requestAnimationFrame(tick);
          return;
        }
        replayTimerRef.current = window.setTimeout(() => {
          replayTimerRef.current = null;
          playFrom(k + 1, step.deployDeg);
        }, REPLAY_HOLD_MS);
      };
      replayRafRef.current = requestAnimationFrame(tick);
    };
    playFrom(0, startDeploy);
  }, [replayingFill, replaySteps, stopAnimateDeploy, stopReplayFill, previewSlotHealth, setAlphaDeg, config.operation.deployAngleDeg, projectId, branch]);
  // Stop on unmount so a pending rAF/timeout never fires into an unmounted tree.
  useEffect(() => () => stopReplayFill(), [stopReplayFill]);
  // Warm badge honesty: the runner counts local inline solves as warm-started only
  // while the template carries a warm_start spec — derive the same way per step.
  const replayWarmOn = !!campaign?.config?.solve_template?.warm_start;
  // Coverage at the replay cursor: the latest coverage_after among iterations the
  // cursor has crossed — so the caption ticks up WITH the dots, not ahead of them.
  const replayCoverage = useMemo(() => {
    if (!replayEntry || !campaign?.state?.iterations) return null;
    const crossed = campaign.state.iterations
      .filter((it) => it.index <= replayEntry.step.iterationIndex)
      .map((it) => it.coverage_after)
      .filter((v): v is number => typeof v === "number" && Number.isFinite(v));
    return crossed.length ? crossed[crossed.length - 1] : null;
  }, [replayEntry, campaign]);

  // ── Replay autoplay (?fillReplay=auto — optional, FXC-9009) ────────────────
  // Opening the highlift view with ?fillReplay=auto (or =1) starts the fill
  // replay as soon as the adopted campaign's steps land, and LOOPS it with a
  // breather between passes — inside the REGULAR Thread layout (sidebar + top
  // bar); autoplay is an option on the project, not a separate kiosk skin. Any manual press of the replay button takes
  // over (autoplay disarms for the rest of the session). Same ?param idiom as
  // the geometry deep-link above; note rAF pauses in hidden tabs, so the loop
  // only advances while the tab is actually on screen — which is the point.
  const AUTOPLAY_FIRST_MS = 1500;
  const AUTOPLAY_LOOP_MS = 4000;
  const autoplayArmedRef = useRef<boolean>(
    typeof window !== "undefined" &&
      ["auto", "1"].includes(new URLSearchParams(window.location.search).get("fillReplay") ?? ""),
  );
  // React mirror of the armed flag — drives the ticker/progress chrome and the
  // any-interaction dismissal listener (§5.7: overlay, never a kiosk skin).
  const [autoplayOn, setAutoplayOn] = useState<boolean>(() => autoplayArmedRef.current);
  const autoplayStartedOnceRef = useRef(false);
  useEffect(() => {
    if (!autoplayArmedRef.current || replayingFill || replaySteps.length === 0) return;
    const delay = autoplayStartedOnceRef.current ? AUTOPLAY_LOOP_MS : AUTOPLAY_FIRST_MS;
    const timer = window.setTimeout(() => {
      // Re-read at fire time: a disarm (either button) or an active replay
      // between scheduling and firing must win over a pending kiosk timer.
      if (!autoplayArmedRef.current || replayActiveRef.current) return;
      autoplayStartedOnceRef.current = true;
      // The §5.7 choreography, all EXISTING mechanisms on the normal layout:
      // land on Explore, collapse rail + inspector, promote the surface card
      // (each pass starts on the CL board and ends on the trust map).
      setStage("explore");
      setLayout((cur) => {
        if (cur.railCollapsed && cur.inspCollapsed) return cur;
        // Review fix: the forced collapse used to write into the PERSISTED
        // layout with no restore — snapshot the pre-autoplay state once so
        // dismissal can put the user's columns back.
        if (autoplayLayoutSnapshotRef.current == null) {
          autoplayLayoutSnapshotRef.current = { railCollapsed: cur.railCollapsed, inspCollapsed: cur.inspCollapsed };
        }
        return { ...cur, railCollapsed: true, inspCollapsed: true };
      });
      handleExploreLanes(["cl"]);
      toggleReplayFill();
    }, delay);
    return () => window.clearTimeout(timer);
  }, [replayingFill, replaySteps, toggleReplayFill, handleExploreLanes]);
  // Pre-autoplay rail/inspector collapse state, restored on dismissal.
  const autoplayLayoutSnapshotRef = useRef<{ railCollapsed: boolean; inspCollapsed: boolean } | null>(null);
  const disarmAutoplay = useCallback(() => {
    autoplayArmedRef.current = false;
    setAutoplayOn(false);
    const snapshot = autoplayLayoutSnapshotRef.current;
    if (snapshot) {
      autoplayLayoutSnapshotRef.current = null;
      setLayout((cur) => ({ ...cur, ...snapshot }));
    }
    // The surface remounts on a ?view= hop and the ref re-initializes from the
    // URL — strip the param so "disarmed for the rest of the session" holds
    // across view switches, not just this component's lifetime.
    const url = new URL(window.location.href);
    if (url.searchParams.has("fillReplay")) {
      url.searchParams.delete("fillReplay");
      window.history.replaceState(null, "", url);
    }
  }, []);
  useEffect(() => {
    disarmReplayAutoplayRef.current = disarmAutoplay;
  }, [disarmAutoplay]);
  // The choreography's closing beat: a NATURALLY-finished pass promotes the
  // trust map (§5.7). Manual stops and dismissals never reach this.
  useEffect(() => {
    onReplayNaturalEndRef.current = () => {
      if (autoplayArmedRef.current) handleExploreLanes(["trust3d"]);
    };
  }, [handleExploreLanes]);
  // ANY user interaction dismisses the story overlay into the normal Explore
  // layout (capture-phase, non-blocking — the click still lands where aimed).
  // Review fix (finding: '■ stop replay' during autoplay RESTARTED it): a
  // press on the replay control must NOT also stop the replay here — this
  // handler stopped it first, so the button's own toggle then read
  // 'not replaying' and started a fresh pass. The press still disarms the
  // loop; the button's onClick performs the actual stop.
  useEffect(() => {
    if (!autoplayOn) return;
    const dismiss = (e: Event) => {
      disarmAutoplay();
      const target = e.target as HTMLElement | null;
      if (target?.closest?.('[data-testid="hlw-replay-fill"]')) return;
      stopReplayFillRef.current();
    };
    window.addEventListener("pointerdown", dismiss, true);
    window.addEventListener("keydown", dismiss, true);
    return () => {
      window.removeEventListener("pointerdown", dismiss, true);
      window.removeEventListener("keydown", dismiss, true);
    };
  }, [autoplayOn, disarmAutoplay]);
  const onReplayButtonClick = useCallback(() => {
    disarmAutoplay(); // a human at the controls beats the kiosk loop
    toggleReplayFill();
  }, [disarmAutoplay, toggleReplayFill]);

  // Create a campaign from the panel's declared box + this surface's live solve template —
  // the EXACT records runSolve sends on /highlift/solve (design/operation/elements/
  // warm_start/geometry_slug), so a campaign batch is provenance-identical to a manual
  // sweep of the same geometry. highlift_config carries the FULL live config so the
  // server-side runner can re-mesh geometry-axis (deploy/flaperon) points through the
  // geometry CLI exactly as the browser would per grid point.
  const startCampaign = useCallback(
    async (spec: FillCampaignSpec) => {
      if (!persistedProject) {
        setCampaignNote("This view isn't backed by a saved project — open a persisted project to run a campaign.");
        return;
      }
      setCampaignBusy(true);
      setCampaignNote(null);
      try {
        const sweepInputs = buildSweepInputs(config);
        const res = await createHighliftCampaign(projectId, branch, {
          name: spec.name,
          quantity: "cl",
          budget: spec.budget,
          batch_size: spec.batchSize,
          target_coverage: spec.targetCoverage,
          axes: spec.axes,
          solve_template: {
            design: designPayload(config),
            operation: { ...config.operation },
            elements: sweepInputs.elements,
            // Buffer-lineage: same rule as runSolve — tag runs with the COMMITTED variant slug
            // only when the live buffer still matches its saved baseline (clean).
            geometry_slug:
              !isDirty && (baseline.source === "loaded" || baseline.source === "committed")
                ? baseline.slug
                : null,
            forbid_cloud_fallback: false,
            // FXC-9009: warm-start each campaign solve from the flowfield-NN (~2.8× fewer
            // steps) — same gate as runSolve so the demo's cold A/B toggle covers campaigns too.
            warm_start: warmStartOn
              ? {
                  mode: "nn-flowfield",
                  weights_url: `${import.meta.env.BASE_URL ?? "/"}flowfield_mlp_web.json`,
                  blow: config.operation.diskLoading ?? 0,
                }
              : null,
          },
          highlift_config: config,
        });
        setCampaignId(res.campaign_id);
      } catch (error: unknown) {
        setCampaignNote(error instanceof Error ? error.message : "campaign create failed");
      } finally {
        setCampaignBusy(false);
      }
    },
    [projectId, branch, persistedProject, config, isDirty, baseline, warmStartOn],
  );

  // Pause / resume / stop, then refetch immediately so the pill reflects the ack without
  // waiting out the 4s poll tick.
  const controlCampaign = useCallback(
    async (action: CampaignControlAction) => {
      if (!campaignId) return;
      try {
        await controlHighliftCampaign(projectId, campaignId, action, branch);
        setCampaignNote(null);
      } catch (error: unknown) {
        setCampaignNote(error instanceof Error ? error.message : `campaign ${action} failed`);
      }
      void queryClient.invalidateQueries({ queryKey: ["highlift-campaign", projectId, branch, campaignId] });
    },
    [projectId, branch, campaignId, queryClient],
  );

  // Tracks the last (config, α) handed to a WebGPU solve/resolve so the drag-to-resolve effect
  // below doesn't echo a warm re-solve for geometry that was just (cold-)solved.
  const lastResolvedRef = useRef<{ config: HighLiftAirfoil; alpha: number } | null>(null);

  // WebGPU in-browser solve (FXC-9009 P5): a client-side alternative to runSolve. It
  // does NOT POST /highlift/solve and does NOT refreshGrid — v1 is a LIVE PREVIEW. The
  // result's FlowMesh is held in `localFlowMesh` (overriding the server field) and the
  // coefficients feed the hero HUD. Now runs the COLD solve through the persisted warm-start
  // session (P3), so a subsequent control-point drag warm-starts via session.resolve. Reuses
  // the SolveProgressOverlay via onProgress: we map the WebGpuSolveProgress into the local
  // SolveProgress shape the overlay expects, and drive the CL marker off `clHistory`.
  const solveWebGpu = useCallback(
    async (alpha: number) => {
      // HARD GATE: never hand the mesher overlapping element contours - an
      // overlap does not fail loudly, it burns the mesher on an impossible
      // boundary (warm-solve exit 2, measured). Check the EXACT configuration
      // being meshed and refuse with the pair + penetration depth.
      {
        const boundary = meshBoundaryCheck(config);
        if (!boundary.ok) {
          const v = boundary.violations[0]!;
          setSolveState("error");
          setSolveNote(
            `Blocked before meshing: ${v.a} overlaps ${v.b}`
            + (v.penetrationC > 0 ? ` by ${(100 * v.penetrationC).toFixed(2)}%c` : " (contours touch)")
            + " - separate the elements (drag handles / reduce deflection) and re-solve.",
          );
          return;
        }
      }
      setSolveState("solving");
      setSolveNote(null);
      setSolveProgress(null);
      setClHistory([]);
      setClCursor(0);
      try {
        const input = assembleWebGpuInput(config, alpha);
        // COLD solve through the persisted session: full iters from freestream + stores the
        // converged field, so the subsequent drag-to-resolves warm-start from it.
        const session = await getSession();
        const result = await session.solve(input, (p) => {
          setSolveProgress({
            status: p.stage === "extracting" ? "done" : "solving",
            stage: p.stage ?? null,
            percentage: p.percentage,
            CL: p.CL ?? null,
            cl_history: p.clHistory ?? null,
          });
          if (p.clHistory) setClHistory(p.clHistory);
        });
        setLocalFlowMesh(result.flow);
        setSolvedFlowRun(null); // a WebGPU preview supersedes any focused server run
        setLastCoeff({ cl: result.CL, cd: result.CD ?? null, ld: result.L_over_D ?? null });
        if (result.clHistory && result.clHistory.length) setClHistory(result.clHistory);
        setSolveState("done");
        setSolveNote("WebGPU (this machine) · solved");
        // Record the just-solved (config, α) so the drag-to-resolve effect doesn't immediately
        // echo a warm re-solve for the geometry we just cold-solved; the next user edit triggers it.
        lastResolvedRef.current = { config, alpha };
        // FXC-9009 P6 — persist this EXPLICIT (cold) solve as a run node so its point joins the
        // ADB grid / Tailor / lineage like a RANS run. Reuses the rans_provenance run_path scheme
        // server-side (metadata.solver='webgpu', no SSH/cloud). Drag-resolve previews are NOT
        // persisted — they never reach this path (see resolveWebGpu). Best-effort: a persist
        // failure must not fail the (already-successful) in-browser solve, so it's caught and
        // surfaced in the note without flipping solveState to error.
        try {
          const res = await persistWebGpuRun(
            projectId,
            buildWebGpuRunRequest({
              config,
              alphaDeg: alpha,
              flow: { mach: flowConditions.mach, reynolds: flowConditions.reynolds },
              result,
              flowMesh: result.flow,
              geometrySlug: webgpuLineageSlug({
                isDirty,
                baselineSource: baseline.source,
                baselineSlug: baseline.slug,
              }),
              // CS-fold provenance: when the design was seeded from an aircraft control-surface
              // cut, the run carries the sparse-wide cs_* axes (absent otherwise — see csRunAxes).
              csSeed,
            }),
            branch,
          );
          // res.run_path is the node now on the grid. Do NOT focus it as the flow run: the
          // WebGPU field is already in `localFlowMesh` (which takes precedence over the server
          // field — see flowMesh below), and the persisted node carries only coefficients, no
          // fetchable slice, so focusing it would 404. Just refresh the ADB grid + run-table
          // queries so the new point appears immediately.
          void res.run_path;
          setSolveNote("WebGPU (this machine) · solved · added to grid");
          refreshGrid();
          scheduleDelayedRefresh();
        } catch (persistErr: unknown) {
          // The solve itself succeeded — keep the field/coeffs; just flag the grid-write miss.
          setSolveNote(
            `WebGPU (this machine) · solved · grid-write failed (${persistErr instanceof Error ? persistErr.message : "unknown"})`,
          );
        }
      } catch (error: unknown) {
        setSolveState("error");
        setSolveNote(error instanceof Error ? error.message : "webgpu solve failed");
      }
    },
    [
      config,
      getSession,
      assembleWebGpuInput,
      projectId,
      branch,
      flowConditions.mach,
      flowConditions.reynolds,
      isDirty,
      baseline.source,
      baseline.slug,
      csSeed,
      refreshGrid,
      scheduleDelayedRefresh,
    ],
  );

  // DEV-ONLY: "V100 dev box" solve. Like runSolve it drives the solve overlay
  // (phase + the big CL marker), but instead of forking a cloud case it serializes the
  // live geometry to the run_case.py contours JSON and POSTs the local V100 service,
  // polling the converged forces (~1–6 min). The result feeds the hero HUD; it is a
  // local preview this phase (NOT persisted to the ADB grid). Aborts on unmount /
  // backend switch via ransV100AbortRef.
  const solveRansV100Point = useCallback(
    async (alpha: number) => {
      ransV100AbortRef.current?.abort();
      // HARD GATE: never hand the mesher overlapping element contours - an
      // overlap does not fail loudly, it burns the mesher on an impossible
      // boundary (warm-solve exit 2, measured). Check the EXACT configuration
      // being meshed and refuse with the pair + penetration depth.
      {
        const boundary = meshBoundaryCheck(config);
        if (!boundary.ok) {
          const v = boundary.violations[0]!;
          setSolveState("error");
          setSolveNote(
            `Blocked before meshing: ${v.a} overlaps ${v.b}`
            + (v.penetrationC > 0 ? ` by ${(100 * v.penetrationC).toFixed(2)}%c` : " (contours touch)")
            + " - separate the elements (drag handles / reduce deflection) and re-solve.",
          );
          return;
        }
      }
      const ac = new AbortController();
      ransV100AbortRef.current = ac;
      setSolveState("solving");
      setSolveNote(null);
      setLocalFlowMesh(null); // a RANS field, not a WebGPU preview, will back the viewer
      setRansV100Result(null);
      // Drive the indeterminate phase of the overlay (no streamed CL — the box solve is opaque).
      setSolveProgress({ status: "meshing", stage: null, percentage: null, CL: null, cl_history: null });
      setClHistory([]);
      setClCursor(0);
      try {
        const sweep = buildSweepInputs(config);
        const contours = buildRansV100Contours(sweep, {
          alphaDeg: alpha,
          mach: flowConditions.mach,
          reynolds: flowConditions.reynolds,
          temperature: flowConditions.temperature,
        });
        const result = await solveRansV100(
          contours,
          { alphaDeg: alpha, mach: flowConditions.mach, reynolds: flowConditions.reynolds },
          {
            signal: ac.signal,
            onProgress: (p) => {
              if (p.status === "queued") {
                setSolveProgress({ status: "solving", stage: null, percentage: null, CL: null, cl_history: null });
                const pos = p.position != null ? ` (position ${p.position})` : "";
                setSolveNote(`V100 dev box · queued${pos}…`);
              } else if (p.status === "running") {
                // FXC-9009: drive the CL convergence bar off the box's live total_forces history.
                const hist = p.clHistory ?? null;
                const lastCl = hist && hist.length ? hist[hist.length - 1]! : null;
                if (hist && hist.length) {
                  setClHistory((prev) => (hist.length >= prev.length ? hist : prev));
                  // The box returns the FULL curve-so-far each poll (not a streamed burst), so pin the
                  // marker/spark to the latest point — show the whole convergence curve in the card now.
                  setClCursor(hist.length - 1);
                }
                setSolveProgress({ status: "solving", stage: null, percentage: null, CL: lastCl, cl_history: hist });
                setSolveNote(`V100 dev box · solving… ${Math.round(p.elapsedSec)}s${lastCl != null ? ` · CL ${lastCl.toFixed(3)}` : ""}`);
                // FXC-9009 live convergence: if the box is dumping per-step frames, show the newest
                // one as it solves (graceful no-op when frame-dumping is off — no frames listed).
                if (p.jobId) {
                  const jid = p.jobId;
                  void fetchRansV100FrameSteps(jid, { baseUrl: ransV100BaseUrl(), signal: ac.signal })
                    .then((steps) => {
                      const last = steps[steps.length - 1];
                      return last != null ? fetchRansV100Flow(jid, { baseUrl: ransV100BaseUrl(), step: last, signal: ac.signal }) : null;
                    })
                    .then((fm) => { if (fm) setLocalFlowMesh(fm); })
                    .catch(() => {});
                }
              }
            },
          },
        );
        setRansV100Result(result);
        if (result.flow) setLocalFlowMesh(result.flow); // FXC-9009: show the V100's solved field
        setLastCoeff({ cl: result.cl, cd: result.cd ?? null, ld: result.lOverD ?? null });
        setSolveProgress(null);
        setSolveState("done");
        const cd = Number.isFinite(result.cd) ? ` · CD ${result.cd.toFixed(4)}` : "";
        setSolveNote(`V100 dev box · CL ${result.cl.toFixed(4)}${cd} (step ${result.step ?? "?"})`);
        // FXC-9009 — persist this converged V100 solve as a run node so its point joins the
        // ADB grid / Tailor / lineage like a cloud-RANS or WebGPU run. Reuses the SAME
        // rans_provenance run_path scheme + grid-write endpoint as the WebGPU persist
        // (`persistWebGpuRun`), with the design/operation flattened identically so the V100
        // point dedupes onto / sits beside its WebGPU/cloud counterparts on the same axes
        // (provenance tag rans-v100 — see ransV100Persist). Best-effort: a persist failure
        // must NOT fail the (already-successful) box solve, so it's caught and surfaced in the
        // note without flipping solveState to error — exactly like the WebGPU path.
        try {
          const res = await persistWebGpuRun(
            projectId,
            buildRansV100RunRequest({
              config,
              alphaDeg: alpha,
              flow: { mach: flowConditions.mach, reynolds: flowConditions.reynolds },
              result,
              geometrySlug: ransV100LineageSlug({
                isDirty,
                baselineSource: baseline.source,
                baselineSlug: baseline.slug,
              }),
            }),
            branch,
          );
          // res.run_path is the node now on the grid. As with the WebGPU persist, don't focus
          // it as the flow run — the V100 result has no fetchable server slice here. Just
          // refresh the ADB grid + run-table queries so the new point appears immediately.
          void res.run_path;
          setSolveNote(`V100 dev box · CL ${result.cl.toFixed(4)}${cd} · added to grid`);
          refreshGrid();
          scheduleDelayedRefresh();
        } catch (persistErr: unknown) {
          // The solve itself succeeded — keep the coeffs; just flag the grid-write miss.
          setSolveNote(
            `V100 dev box · CL ${result.cl.toFixed(4)}${cd} · grid-write failed (${persistErr instanceof Error ? persistErr.message : "unknown"})`,
          );
        }
      } catch (error: unknown) {
        if (error instanceof DOMException && error.name === "AbortError") return; // cancelled — silent
        setSolveProgress(null);
        setSolveState("error");
        setSolveNote(error instanceof Error ? error.message : "rans-v100 solve failed");
      }
    },
    [
      config,
      projectId,
      branch,
      flowConditions.mach,
      flowConditions.reynolds,
      flowConditions.temperature,
      isDirty,
      baseline.source,
      baseline.slug,
      refreshGrid,
      scheduleDelayedRefresh,
    ],
  );

  // FXC-9009 P3 — LIVE drag-to-resolve. A control-point drag (FoilGeometryCanvas →
  // setHandleDescriptor) mutates `config`; sliders + α do too. While the WebGPU backend is
  // selected AND a converged field is stored (i.e. a cold solve has run), a debounced effect
  // watches `config`/`alphaDeg` and warm-starts `session.resolve` on each change — the field
  // updates live with no server round-trip. Debounced ~200ms so a continuous drag coalesces
  // into one warm re-solve when it settles. The literal drag is captured via this config-watch
  // (the canvas exposes no debounced drag-changed callback at the surface level — see report).
  const resolveInFlightRef = useRef(false);
  const resolveWebGpu = useCallback(
    async (cfg: HighLiftAirfoil, alpha: number) => {
      const session = sessionRef.current;
      if (!session) return; // no cold solve yet → nothing to warm-start from
      if (resolveInFlightRef.current) return; // coalesce: skip while a warm re-solve is running
      resolveInFlightRef.current = true;
      try {
        const input = assembleWebGpuInput(cfg, alpha);
        const result = await session.resolve(input, (p) => {
          setSolveProgress({
            status: p.stage === "extracting" ? "done" : "solving",
            stage: p.stage ?? null,
            percentage: p.percentage,
            CL: p.CL ?? null,
            cl_history: p.clHistory ?? null,
          });
          if (p.clHistory) setClHistory(p.clHistory);
        });
        setLocalFlowMesh(result.flow); // live field update
        setLastCoeff({ cl: result.CL, cd: result.CD ?? null, ld: result.L_over_D ?? null });
        setSolveNote("WebGPU (this machine) · live reshape");
      } catch (error: unknown) {
        setSolveNote(error instanceof Error ? error.message : "webgpu resolve failed");
      } finally {
        resolveInFlightRef.current = false;
      }
    },
    [assembleWebGpuInput],
  );

  // Debounce the geometry/α change → warm re-solve. Skipped entirely unless the WebGPU backend
  // is active and a session field exists, so the RANS/V100 path is untouched. The first config
  // value after a cold solve is the converged one, so the leading change is the user's first edit.
  useEffect(() => {
    if (solveBackend !== "webgpu") return;
    if (!sessionRef.current) return; // wait for the cold solve to seed the field
    // Skip the change that simply records the just-solved state (avoid an immediate echo resolve).
    if (lastResolvedRef.current && lastResolvedRef.current.config === config && lastResolvedRef.current.alpha === alphaDeg) {
      return;
    }
    const id = window.setTimeout(() => {
      lastResolvedRef.current = { config, alpha: alphaDeg };
      void resolveWebGpu(config, alphaDeg);
    }, 200);
    return () => window.clearTimeout(id);
  }, [config, alphaDeg, solveBackend, resolveWebGpu]);

  // The spine's ▶ Solve: fork the held-slice condition built from the foil controls.
  const handleSolveThisPoint = useCallback(() => {
    if (solveBackend === "webgpu") return void solveWebGpu(alphaDeg);
    if (solveBackend === "rans-v100") return void solveRansV100Point(alphaDeg);
    const point: SolvePoint = {
      alpha_deg: alphaDeg,
      mach: flowConditions.mach,
      reynolds: flowConditions.reynolds,
      temperature: flowConditions.temperature,
      flap_deploy_deg: config.operation.deployAngleDeg,
    };
    void runSolve([point], "Nothing to solve.");
  }, [
    solveBackend,
    solveWebGpu,
    solveRansV100Point,
    alphaDeg,
    flowConditions.mach,
    flowConditions.reynolds,
    flowConditions.temperature,
    config.operation.deployAngleDeg,
    runSolve,
  ]);

  // "Propose next N (Tailor)": read the composite's GP/acquisition recommendations and
  // load them into the editable queue WITH their rationale (score, purpose, reasons) —
  // the user reviews/edits/drops them and chooses N before anything is solved.
  const proposeQueue = useCallback(
    async (n: number) => {
      setQueueLoading(true);
      setSolveNote(null);
      try {
        const recs = await fetchHighliftRecommendations(projectId, branch, HIGHLIFT_QUANTITY, n);
        const fallback = {
          mach: flowConditions.mach,
          reynolds: flowConditions.reynolds,
          temperature: flowConditions.temperature,
          flap_deploy_deg: config.operation.deployAngleDeg,
        };
        const rows = (recs.recommendations ?? [])
          .filter((rec) => Number.isFinite(Number(rec.condition?.alpha_deg ?? rec.condition?.alpha)))
          .slice(0, n)
          .map((rec) => recToQueued(rec, fallback));
        setQueue(rows);
        if (!rows.length) {
          setSolveNote("Tailor has no next-run recommendations yet — solve a seed point first.");
        }
      } catch (error: unknown) {
        setSolveNote(error instanceof Error ? error.message : "recommendations unavailable");
      } finally {
        setQueueLoading(false);
      }
    },
    [projectId, branch, flowConditions.mach, flowConditions.reynolds, flowConditions.temperature, config.operation.deployAngleDeg],
  );

  const removeFromQueue = useCallback((id: string) => {
    setQueue((q) => q.filter((row) => row.id !== id));
  }, []);
  const clearQueue = useCallback(() => setQueue([]), []);
  const patchQueuePoint = useCallback(
    (id: string, field: "alpha_deg" | "flap_deploy_deg", value: number) => {
      setQueue((q) => q.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
    },
    [],
  );

  // Run the queue: one /solve batch with all queued points, then mark each row from the
  // response. Shares runSolve (recallable focus + foil sync), so a cached/completed
  // point shows its field with the foil matched to it instead of blanking/snapping.
  const runQueue = useCallback(async () => {
    if (!queue.length) return;
    setQueue((q) => q.map((row) => ({ ...row, status: "solving" as QueueStatus, note: undefined })));
    const points: SolvePoint[] = queue.map((row) => ({
      alpha_deg: row.alpha_deg,
      mach: row.mach,
      reynolds: row.reynolds,
      temperature: row.temperature,
      flap_deploy_deg: row.flap_deploy_deg,
    }));
    const res = await runSolve(points, "Nothing queued to solve.");
    setQueue((q) =>
      q.map((row) => {
        const sub = res?.submitted.find(
          (s) =>
            Math.abs(s.alpha_deg - row.alpha_deg) < 1e-6 &&
            Math.abs((s.flap_deploy_deg ?? 0) - row.flap_deploy_deg) < 1e-6,
        );
        if (!sub) return { ...row, status: "failed" as QueueStatus, note: res ? "not submitted" : "solve failed" };
        if (sub.error) return { ...row, status: "failed" as QueueStatus, note: sub.error };
        const note = sub.backend === "cache" ? "cached" : sub.CL != null ? `CL ${sub.CL.toFixed(2)}` : "forked";
        return { ...row, status: "done" as QueueStatus, note };
      }),
    );
  }, [queue, runSolve]);

  const solving = solveState === "solving";
  // The overlay lingers past "done" until the CL replay finishes sweeping. The marker
  // shows the replay cursor's value; the sparkline fills in up to the cursor.
  const showSolveOverlay = solving || clHistory.length > 0;
  const markerCl = clHistory.length ? clHistory[Math.min(clCursor, clHistory.length - 1)]! : null;
  const sparkHistory = clHistory.slice(0, clCursor + 1);

  // --- flow field --------------------------------------------------------------
  // Show the center-span RANS slice for the run in focus and feed it to
  // FlowVisLayer (the LIC field). After a solve we auto-focus the just-solved run
  // (so the field appears without a manual grid-select); a manual node selection
  // then supersedes it. 404-tolerant: runs with no slice keep the empty state.
  const [solvedFlowRun, setSolvedFlowRun] = useState<string | null>(null);
  const [pendingFlowRun, setPendingFlowRun] = useState<string | null>(null);
  useEffect(() => {
    setSolvedFlowRun(null);
  }, [selectedNodeId]);
  const selectedFlowRunId = solvedFlowRun ?? selectedNodeId;
  const flowQuery = useQuery({
    queryKey: ["highlift-flow", projectId, branch, selectedFlowRunId],
    queryFn: () => fetchHighliftFlowMesh(projectId, selectedFlowRunId!, branch),
    enabled: persistedProject && !!selectedFlowRunId,
    // Solved slices are immutable; a 404/null during cloud ingestion is not.
    staleTime: (query) => (query.state.data === null ? 0 : 5 * 60 * 1000),
    refetchInterval: pendingFlowRun !== null && pendingFlowRun === selectedFlowRunId ? 10000 : false,
    retry: false,
  });
  // A live WebGPU preview (`localFlowMesh`) takes precedence over the server flow
  // query; it's cleared on a RANS solve and on a manual grid run-select so the
  // server field takes back over.
  // NN preview (if on) overrides everything so you can flip NN vs RANS for the same point;
  // otherwise a live WebGPU preview (`localFlowMesh`) takes precedence over the server flow.
  const flowMesh = nnPreview ? nnFlowMesh : (localFlowMesh ?? flowQuery.data ?? null);
  useEffect(() => {
    // Track the SERVER slice landing (not the merged mesh) so a live WebGPU preview
    // can't prematurely clear a RANS fork's pending-flow poll.
    if (pendingFlowRun === selectedFlowRunId && (flowQuery.data || flowQuery.isError)) setPendingFlowRun(null);
  }, [flowQuery.data, flowQuery.isError, pendingFlowRun, selectedFlowRunId]);
  const flowErrorMessage = flowQuery.error instanceof Error ? flowQuery.error.message : null;
  // 404 → null data (no throw). A subtle hint differentiates "no run selected"
  // from "this run has no flow field"; non-404 failures stay actionable.
  const flowHint =
    !selectedFlowRunId ? null
    : flowQuery.isLoading ? "loading flow field…"
    : flowQuery.isError ? `flow field unavailable${flowErrorMessage ? `: ${flowErrorMessage}` : ""}`
    : flowMesh ? null
    : "no flow field for this run (solve emits one)";
  const flowHintIsError = flowQuery.isError;
  const handleExplorerSelectRun = useCallback<SurfaceProps["onSelectRun"]>((runId, nodeId) => {
    setSolvedFlowRun(null);
    setLocalFlowMesh(null); // a manual grid select hands the viewer back to the server field
    onSelectRun(runId, nodeId);
  }, [onSelectRun]);

  // ── Study Cursor seam (FXC-9009 two-rooms) ─────────────────────────────────
  // The live workbench values every panel shares (SweepPanel/FillPanel props +
  // the board's held-axis defaults). Memoized on the scalars so the board's
  // axis metadata doesn't churn identity every render.
  const sweepLive = useMemo<SweepLiveValues>(() => ({
    deploy: config.operation.deployAngleDeg,
    flaperon: config.operation.flaperonAngleDeg,
    alpha: alphaDeg,
    mach: flowConditions.mach,
    reynolds: flowConditions.reynolds,
    temperature: flowConditions.temperature,
    // Live blowing (actuator-disk thrust loading Δp/q∞, FXC-9009) — the held value
    // when blowing isn't being swept. 0 = disk off (the blow-agnostic prior slice).
    diskLoading: config.operation.diskLoading,
  }), [
    config.operation.deployAngleDeg,
    config.operation.flaperonAngleDeg,
    alphaDeg,
    flowConditions.mach,
    flowConditions.reynolds,
    flowConditions.temperature,
    config.operation.diskLoading,
  ]);
  // Initialise the shared cursor WITHOUT subscribing this heavy surface to it
  // (pin-slider drags must not re-render the whole workbench). The board and
  // its panels subscribe themselves.
  const studyInputs = useMemo(() => boardAxisSpecs(campaign, sweepLive), [campaign, sweepLive]);
  useEnsureStudyCursor(projectId, branch, studyInputs);
  // --- run table (shared) -------------------------------------------------------
  // Shares ConditionPivotTable's run-table query key ["run-table", projectId,
  // branch, "full"] so the two de-dupe (one fetch feeds the pivot table, the
  // polar, the study-point runId reconcile below, and the campaign plots'
  // run_path resolver). trustNonce is NOT in the key (it would split the cache
  // from the pivot); instead we invalidate that key in refreshGrid after a solve.
  const runTableQuery = useQuery<RunTableData>({
    queryKey: ["run-table", projectId, branch, "full"],
    queryFn: () => fetchRunTablePaginated({ projectId, branch }),
    enabled: persistedProject,
    staleTime: 30_000,
    retry: false,
  });
  // Clicking any surface point sets selectedPoint (§5.4) → re-rig the foil to
  // that condition and fetch that run's flow field, so the geometry·flow view
  // is live at the selected point. A solve writes back through the same seam.
  const selectedStudyPoint = useStudyCursorValue(projectId, branch, (c) => c?.selectedPoint ?? null);
  const lastAppliedStudyPointRef = useRef<StudySelectedPoint | null>(null);
  useEffect(() => {
    const pt = selectedStudyPoint;
    if (!pt || lastAppliedStudyPointRef.current === pt) return;
    lastAppliedStudyPointRef.current = pt;
    const deploy = Number(pt.inputs.flap_deploy_deg);
    if (Number.isFinite(deploy)) setDeployDeg(deploy);
    const alpha = Number(pt.inputs.alpha_deg);
    if (Number.isFinite(alpha)) setAlphaDeg(alpha);
    if (pt.runNodeId) {
      // The existing explorer-select seam: clears any local preview field and
      // routes the selection through the shell, so the flow slice + the rest
      // of the workbench follow the same run (the polar-scrubber precedent).
      //
      // Reconcile-proof runId (FXC-9009): the shell's stale-run guard
      // (f5ab69b6f44) validates the `run` param against rowKey(row) — the
      // row's runId/NAME, never its node uuid — and re-pins row 0 on a miss.
      // A study point carries the node uuid, so select by the matching run
      // row's name when the run table resolves it; the node id stays the
      // provenance pin. Replay points (whose runNodeId is a raw run PATH)
      // and unresolved ids fall through unchanged.
      const row = (runTableQuery.data?.rows ?? []).find((r) => r._node_id === pt.runNodeId);
      const runId = typeof row?.name === "string" && row.name ? row.name : pt.runNodeId;
      handleExplorerSelectRun(runId, pt.runNodeId);
    }
  }, [selectedStudyPoint, setDeployDeg, handleExplorerSelectRun, runTableQuery.data]);

  const registerLabel =
    registerState === "registering" ? "registering sources…"
    : registerState === "done" ? "sources registered"
    : registerState === "error" ? "sources unavailable"
    : "sources idle";

  // --- polars (Cₗ–α + drag polar) over the solved run nodes --------------------
  const polarSeries = useMemo(
    () => runTableToPolarSeries(runTableQuery.data, { rans: palette.warn, other: palette.blue }),
    [runTableQuery.data, palette.warn, palette.blue],
  );
  // Campaign run_path → run node id (FXC-9009 campaign → plots → provenance).
  // A campaign point's run_path is an ltree whose LAST label is exactly the run
  // node's name in the run table (verified live on beecbb20), and the run table
  // is already fetched here — so resolution is a client-side map lookup, no
  // extra requests. (The /api/nodes path_prefix filter is NOT usable for this:
  // the V3 read leg serves the projection, which has no ltree path.)
  const runPathToNodeId = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of runTableQuery.data?.rows ?? []) {
      const name = typeof row.name === "string" ? row.name : null;
      const nodeId = typeof row._node_id === "string" ? row._node_id : null;
      if (name && nodeId) map.set(name, nodeId);
    }
    return map;
  }, [runTableQuery.data]);
  const resolveRunPath = useCallback(
    (runPath: string): string | null => {
      const label = runPath.includes(".") ? runPath.slice(runPath.lastIndexOf(".") + 1) : runPath;
      return runPathToNodeId.get(label) ?? null;
    },
    [runPathToNodeId],
  );
  // CLmax dot + stall α — derived from the existing solved CL-α points of the first
  // (RANS) series. REAL NOW: no backend, purely the solved run data.
  const clMax = useMemo<ClMaxMarker | null>(
    () => (polarSeries.length > 0 ? deriveClMax(polarSeries, 0) : null),
    [polarSeries],
  );
  const stallAlpha = clMax?.stallAlpha ?? null;
  // (The old stall-progression scrubber retired with the dock polar tile —
  // clicking a polar point in the Explore board now picks the flow slice via
  // the Study Cursor, which is the same capability without the extra slider.)
  // HERO force arrows — per-element forces from the live /flow dict or the selected
  // run row's element_<name>_CF{x,z}/CMy scalars; null (no arrows) when absent.
  const selectedRunRow = useMemo(
    () =>
      (runTableQuery.data?.rows ?? []).find((r) => r._node_id === selectedFlowRunId) ?? null,
    [runTableQuery.data, selectedFlowRunId],
  );
  const elementForces = useMemo(
    () => deriveElementForces(flowMesh, selectedRunRow),
    [flowMesh, selectedRunRow],
  );
  // The non-axis flow conditions held fixed for the 3D trust surface slice
  // (alpha_deg × flap_deploy_deg are the two axes, so they are excluded — mirrors
  // ThreadV5AdbComposite's fixedValues filtering). Re is pinned on the schema's
  // `re_chord_millions` identity axis (in millions) — the axis the N-D residual-GP
  // conditions on — so the slice is a genuine CONDITIONAL surface at this Reynolds
  // (a slice at Re 6M differs from one at 12M), not the schema Re default (FXC-9009).
  // Blowing (`disk_thrust_loading`, FXC-9009) is pinned to the live disk loading so the
  // 2D α × deploy slice conditions on the held blowing: a slice at blow=0 differs from
  // one at blow=1.0 (the prior is blow-agnostic, so the whole blowing delta is residual).
  // A small honest empty state shared by the new read-only analysis surfaces when
  // the design has no solved data / no baseline node yet.

  // Data-mode tiles (the design's board) — reuse REAL data/components: coefficient cards from the
  // last solve + live conditions, the actual ADB explorer (points/multifidelity/trust), and a live
  // section PiP. They mount only when the TileBoard renders them (Data mode).

  // ── AC-L dock panels: Elements / Shape / Library (left) · Solve / Sweep / Analysis (right). ──────
  // Each reuses the EXISTING surface logic (collection edits, the real setters, the Tailor queue +
  // solve, the trust summary + ADB explorer, persistence). The hero (foil over the flow + HUD)
  // renders in the dock layout's centre. (`sweepLive` is memoized up by the
  // Study Cursor seam — one shared live-values object for panels + board.)
  // Buffer-lineage: a short label for the baseline ("current") the buffer is diffed against.
  const baselineLabel = baseline.name ?? (baseline.source === "seed" ? "default" : baseline.slug ?? "baseline");
  // Can we jump to this baseline's ancestry in the Lineage view? Only when it's a saved node.
  const canViewLineage = (baseline.source === "loaded" || baseline.source === "committed") && !!baseline.slug;
  const viewAncestryInLineage = useCallback(() => {
    if (!canViewLineage || !baseline.slug) return;
    // The Lineage full-surface view picks its pivot from selectedNodeId; route there + select the
    // baseline's node so its derived_from family (parent ← baseline ← variants) is centred.
    if (baseline.nodeId) onSelectRun?.(baseline.nodeId);
    onOpenView?.("lineage");
  }, [canViewLineage, baseline.slug, baseline.nodeId, onSelectRun, onOpenView]);


  // Rig stage headline instrument: one row per element with editable deflection + cove depth and
  // live gap / overlap — makes the plain-flap (aileron) + slat riggable and unifies rigging for all
  // elements. ShapePanel stays the per-element shape detail editor below it.
  const riggingTableBody = (
    <div data-tour="hlw-rigging">
      <RiggingTable
        palette={palette}
        config={config}
        elements={elements}
        setters={setters}
        gapMetrics={gapMetrics}
        motionGap={motionGap}
        selectedId={selectedId}
        onSelect={setSelectedId}
      />
    </div>
  );

  const shapePanelBody = (
    <ShapePanel
      palette={palette}
      config={config}
      setters={setters}
      selectedId={selectedId}
      controlSurface={controlSurface}
      onControlSurface={setControlSurface}
    />
  );

  const isVariantCommit = isDirty && (baseline.source === "loaded" || baseline.source === "committed") && !!baseline.slug;
  const libraryPanelBody = (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {/* Buffer↔baseline status (XFOIL current ↔ buffer). Mirrors the header chip, with the commit verb. */}
      <div
        style={{
          display: "flex", flexDirection: "column", gap: 4, padding: "7px 9px", borderRadius: 7,
          border: `1px solid ${isDirty ? palette.amber : palette.line}`, background: isDirty ? palette.primarySoft : palette.surface,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <i style={{ width: 7, height: 7, borderRadius: 7, background: isDirty ? palette.amber : palette.classOk, display: "inline-block", flex: "0 0 auto" }} />
          <span style={{ ...mono11, color: palette.ink, fontSize: 11 }}>
            {isDirty ? "buffer modified" : baseline.source === "seed" ? "buffer = default" : "buffer = baseline"}
          </span>
          <span style={{ flex: 1 }} />
          <span style={{ ...mono11, fontSize: 9, color: palette.ink4 }}>#{bufferFingerprint}</span>
        </div>
        <span style={{ ...mono11, fontSize: 10, color: palette.ink3 }}>
          {isDirty
            ? `reshaped — diverged from ${baselineLabel}; promote to a tracked variant`
            : `current: ${baselineLabel}`}
        </span>
        {csSeed ? (
          // CS-fold provenance read-out: this buffer was seeded from an aircraft CS cut section.
          <span
            style={{ ...mono11, fontSize: 10, color: palette.blue }}
            title={csSeed.note ?? `Seeded from the aircraft Control Surfaces designer (${csSeed.section.csType})`}
          >
            {`Section from Control Surfaces: ${csSeed.section.name} - ${csSeed.section.surface} eta ${csSeed.section.eta.toFixed(2)}`}
          </span>
        ) : null}
        {csSeed && csSeedParametric ? (
          // Seed-mode control (three-way): how the seeded section presents. Parametric switches
          // REBUILD the design from the stashed seed; two-body is the read-only exact view. The
          // seed itself survives every switch (cs_* provenance axes persist).
          <div style={{ display: "flex", gap: 4, alignItems: "center" }} data-testid="hlw-seed-mode">
            {(
              [
                { mode: "aileron" as const, label: "aileron", title: "As designed - the aircraft's clean section with the seed rig's plain-flap aileron (hinge x/c, deflection, slot gap). Rebuilds the design from the seed." },
                { mode: "clean" as const, label: "clean foil", title: "Bare clean section - the aircraft's uncut foil only; shape the cove + devices with the native tools. Rebuilds the design from the seed." },
                { mode: "highlift" as const, label: "high-lift", title: "The aircraft's clean section carrying the native multi-element high-lift system (cove + vane + slotted flaperon + propeller). Rebuilds from the seed." },
                { mode: "twobody" as const, label: "two-body", title: twoBody ? "Exact cut contours - read-only view of the carved wing + deflected surface with slot health measured from them; Solve keeps running the parametric modes." : "The seed carries no usable exact contours for a two-body view." },
              ]
            ).map(({ mode, label, title }) => {
              const active = csSeedMode === mode;
              const disabled = mode === "twobody" && !twoBody;
              return (
                <button
                  key={mode}
                  type="button"
                  disabled={disabled}
                  onClick={() => applyCsSeedMode(mode)}
                  title={title}
                  style={{
                    ...mono11, fontSize: 10, padding: "2px 8px", borderRadius: 999,
                    cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.45 : 1,
                    border: `1px solid ${active ? palette.primary : palette.line}`,
                    background: active ? palette.primarySoft : palette.surface2,
                    color: active ? palette.primary : palette.ink3,
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <button
          type="button"
          style={{ ...buttonBase, flex: 1, borderColor: isVariantCommit ? palette.primary : palette.line, background: isVariantCommit ? palette.primary : palette.surface2, color: isVariantCommit ? palette.onPrimary : palette.ink, fontWeight: isVariantCommit ? 600 : 400 }}
          disabled={geometryBusy}
          onClick={handleSaveGeometry}
          title={isVariantCommit
            ? `Promote the reshaped buffer as a variant derived from “${baselineLabel}” — writes a derived_from lineage edge`
            : "Save the current geometry into this project under a name"}
        >
          {geometryBusy ? "saving…" : isVariantCommit ? "Promote as variant" : "Save geometry"}
        </button>
        {canViewLineage ? (
          <button
            type="button"
            style={{ ...buttonBase, borderColor: palette.line, background: palette.surface2, color: palette.blue }}
            onClick={viewAncestryInLineage}
            title={`Open the Lineage view centred on “${baselineLabel}” to see its derived_from family tree`}
          >
            ↗ Lineage
          </button>
        ) : null}
      </div>
      <label style={{ ...muted12, display: "flex", flexDirection: "column", gap: 4 }}>
        load
        <select
          value=""
          disabled={geometryBusy || savedGeometries.length === 0}
          onChange={(event) => { if (event.target.value) handleLoadGeometry(event.target.value); }}
          style={{ ...mono11, background: palette.surface2, border: `1px solid ${palette.line}`, color: palette.ink, borderRadius: 4, padding: "4px 6px" }}
        >
          <option value="">{savedGeometries.length === 0 ? "no saved geometries" : "choose a saved geometry…"}</option>
          {savedGeometries.map((g) => (
            <option key={g.slug} value={g.slug}>{g.name}</option>
          ))}
        </select>
      </label>
      {savedGeometries.length > 0 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {savedGeometries.map((g) => {
            const isCurrent = (baseline.source === "loaded" || baseline.source === "committed") && baseline.slug === g.slug;
            const parent = g.derived_from_slug
              ? savedGeometries.find((p) => p.slug === g.derived_from_slug)
              : null;
            return (
              <div key={g.slug} onClick={() => handleLoadGeometry(g.slug)} style={{ display: "flex", flexDirection: "column", gap: 2, padding: "6px 8px", border: `1px solid ${isCurrent ? palette.primary : palette.line}`, background: isCurrent ? palette.primarySoft : "transparent", borderRadius: 7, cursor: "pointer" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {/* derived variants are indented + carry a ↳ to read the family tree at a glance */}
                  {g.derived_from_slug ? <span style={{ ...mono11, fontSize: 11, color: palette.ink4 }}>↳</span> : null}
                  <span style={{ fontSize: 12, color: palette.ink, fontFamily: FONT_STACK, flex: 1 }}>{g.name}</span>
                  {g.design_hash ? <span style={{ ...mono11, fontSize: 9, color: palette.ink4 }}>#{g.design_hash}</span> : null}
                </div>
                {g.derived_from_slug ? (
                  <span style={{ ...mono11, fontSize: 9, color: palette.ink3 }}>derived from {parent?.name ?? g.derived_from_slug}</span>
                ) : (
                  <span style={{ ...mono11, fontSize: 9, color: palette.ink4 }}>?geometry={g.slug}</span>
                )}
              </div>
            );
          })}
        </div>
      ) : null}
      {geometryNote ? <span style={{ ...mono11, color: palette.ink3 }}>{geometryNote}</span> : null}
    </div>
  );

  // FXC-9009 WYDIWYA badge ("what you draw is what you analyze"): how many deployed elements the
  // canvas RENDERS vs how many actually reach the solver. The renderer iterates the full deployed
  // collection (drawable contours), and `buildSweepInputs` now emits every drawable element + every
  // blowing source — so this should read N/N. It turns RED only if the wire ever drops an element
  // (renderCount > solveCount), which is the truncation bug this fix removes.
  const wydiwya = useMemo(() => {
    const drawable = configuration.elements.filter(
      (el) => Array.isArray(el.contour) && el.contour.length >= 3,
    );
    const sweep = buildSweepInputs(config);
    const renderDisks = blowingOf(config).length;
    const solveDisks = sweep.disks.length;
    return {
      renderCount: drawable.length,
      solveCount: sweep.elements.length,
      // Disks are only "expected" at the solver when blowing is on (loading ≠ 0); off ⇒ 0/0 is fine.
      renderDisks: config.operation.diskLoading !== 0 ? renderDisks : 0,
      solveDisks,
      dropped: drawable.length > sweep.elements.length
        || (config.operation.diskLoading !== 0 && renderDisks > solveDisks),
    };
  }, [configuration, config]);

  const solvePanelBody = (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {/* Solve-backend selector (FXC-9009 P5): RANS/V100 (default) vs WebGPU in-browser.
          WebGPU only enables when an adapter is present. */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <label style={{ ...muted12, flex: "0 0 60px" }}>backend</label>
        <div style={{ display: "flex", flex: 1, border: `1px solid ${palette.line}`, borderRadius: 6, overflow: "hidden" }}>
          {([
            { id: "rans" as const, label: "Flow360 (cloud)", enabled: true, tip: "Managed Flow360 cloud RANS — forks a case (needs thread-api)" },
            { id: "webgpu" as const, label: "WebGPU (this machine)", enabled: webgpuAvailable, tip: webgpuAvailable ? "rustfoil WASM RANS on this machine's GPU — instant, fully local" : "No WebGPU adapter available in this browser" },
            // DEV-ONLY: only present when VITE_RANS_V100_URL is set (direct to a V100 dev box over the SSH tunnel).
            ...(ransV100On
              ? [{ id: "rans-v100" as const, label: "V100 dev box", enabled: true, tip: "Direct 2D RANS on a remote V100 dev box over the hook (dev only; ~1–6 min + your round-trip)" }]
              : []),
          ]).map((opt, i) => {
            const active = solveBackend === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                title={opt.tip}
                disabled={!opt.enabled || solving}
                onClick={() => setSolveBackend(opt.id)}
                style={{
                  ...mono11,
                  flex: 1,
                  padding: "4px 6px",
                  cursor: opt.enabled && !solving ? "pointer" : "not-allowed",
                  border: "none",
                  borderLeft: i === 0 ? "none" : `1px solid ${palette.line}`,
                  background: active ? palette.primary : palette.surface2,
                  color: active ? palette.onPrimary : opt.enabled ? palette.ink : palette.ink4,
                  fontWeight: active ? 600 : 400,
                  opacity: opt.enabled ? 1 : 0.6,
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
      {solveBackend === "rans-v100" && ransV100Result && (
        <div style={{ ...muted12, paddingLeft: 68, color: palette.ink3, lineHeight: 1.3, fontFamily: MONO_STACK }}>
          {`per-element CL — ${(
            // Full N-element breakdown when present (FXC-9009 N-element wire), else the legacy
            // first-three aliases for older box hosts.
            ransV100Result.elements && ransV100Result.elements.length
              ? ransV100Result.elements.map((e) =>
                  e.cl != null ? `${e.name} ${e.cl.toFixed(3)}` : null,
                )
              : [
                  ransV100Result.clMain != null ? `main ${ransV100Result.clMain.toFixed(3)}` : null,
                  ransV100Result.clVane != null ? `vane ${ransV100Result.clVane.toFixed(3)}` : null,
                  ransV100Result.clFlap != null ? `flap ${ransV100Result.clFlap.toFixed(3)}` : null,
                ]
          )
            .filter(Boolean)
            .join(" · ")}`}
        </div>
      )}
      {solveBackend === "webgpu" && (
        <div
          style={{
            ...muted12,
            paddingLeft: 68,
            fontStyle: "italic",
            color: palette.ink3,
            lineHeight: 1.3,
          }}
        >
          ⚠ Preview fidelity (f32, fast): Cₗ is indicative only. CD and tight-slot / multi-element
          loads are not reliable here — use Flow360 (cloud) for trustworthy results.
        </div>
      )}
      {/* FXC-9009 WYDIWYA badge — a truncation-BUG detector, so it renders ONLY
          when red (some drawn element/disk is not reaching the solver). The
          healthy N/N state is the invariant, not information (§3.6 dev chrome). */}
      {wydiwya.dropped ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8, paddingLeft: 68 }}>
          <span
            title="Some drawn elements/disks are NOT reaching the solver — the analysis ≠ what you drew."
            style={{
              ...mono11,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "2px 8px",
              borderRadius: 999,
              border: `1px solid ${palette.warn}`,
              background: `${palette.warn}22`,
              color: palette.warn,
              fontWeight: 600,
            }}
          >
            ⚠ solving {wydiwya.solveCount}/{wydiwya.renderCount} elements
            {wydiwya.renderDisks > 0 ? ` · ${wydiwya.solveDisks}/${wydiwya.renderDisks} disks` : ""}
          </span>
        </div>
      ) : null}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <label style={{ ...muted12, flex: "0 0 60px" }}>α (deg)</label>
        <input
          type="range"
          min={-4}
          max={20}
          step={0.5}
          value={alphaDeg}
          onChange={(event) => setAlphaDeg(Number(event.target.value))}
          style={{ flex: 1, accentColor: palette.primary, minWidth: 0 }}
        />
        <span style={{ ...mono11, flex: "0 0 40px", textAlign: "right", color: palette.ink }}>{alphaDeg.toFixed(1)}</span>
      </div>
      {/* Solve-fidelity toggles, relocated from the hero HUD (§5.1): the NN
          preview / warm-start / convergence-replay + the dev-gated 2nd-order
          refine are solve concerns, so they live with the solve. The spine's
          ▶ Solve is THE one solve control (acceptance 3). */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={() => setNnPreview((v) => !v)}
          title={`NN-predicted flowfield (client-side coordinate-MLP, instant on geometry/condition edits) vs the RANS/solved field — with instant NN CL/CD by surface-Cp integration${nnCheckpoint ? ` · checkpoint schema ${nnCheckpoint.schemaVersion} (${nnCheckpoint.rawFeatureCount} raw features)` : ""}${nnPreview && nnForces ? ` · live Cₗ ${nnForces.CL.toFixed(2)} Cd ${nnForces.CD.toFixed(3)}` : ""}`}
          style={{ ...mono11, cursor: "pointer", color: nnPreview ? palette.onPrimary : palette.ink3, background: nnPreview ? palette.blue : palette.surface, border: `1px solid ${nnPreview ? palette.blue : palette.line}`, borderRadius: 6, padding: "3px 10px" }}
        >
          {nnPreview ? "NN field ✓" : "NN field"}
        </button>
        <button
          type="button"
          onClick={() => setWarmStartOn((v) => !v)}
          title="NN warm-start the solver: seed each RANS solve from the flowfield-NN's predicted field instead of freestream — ~2.5-3× fewer iterations to the same CL. Toggle OFF to run cold and compare."
          style={{ ...mono11, cursor: "pointer", color: warmStartOn ? palette.onPrimary : palette.ink3, background: warmStartOn ? palette.blue : palette.surface, border: `1px solid ${warmStartOn ? palette.blue : palette.line}`, borderRadius: 6, padding: "3px 10px" }}
        >
          {warmStartOn ? "NN warm-start ✓" : "NN warm-start"}
        </button>
        <button
          type="button"
          onClick={() => setConvPlay((v) => !v)}
          title="Watch a V100 RANS solve converge — from the NN warm-start guess to the converged field, with the CL trace"
          style={{ ...mono11, cursor: "pointer", color: convPlay ? palette.onPrimary : palette.ink3, background: convPlay ? palette.blue : palette.surface, border: `1px solid ${convPlay ? palette.blue : palette.line}`, borderRadius: 6, padding: "3px 10px" }}
        >
          Watch converge
        </button>
        {refineOn ? (
          <button
            type="button"
            onClick={handleRefine}
            disabled={refineBusy || !localFlowMesh}
            title={localFlowMesh ? "Refine this WebGPU result with the CPU-f64 2nd-order JFNK solver (~10 min, faithful)" : "Solve a WebGPU point first"}
            style={{ ...mono11, cursor: refineBusy || !localFlowMesh ? "not-allowed" : "pointer", opacity: refineBusy || !localFlowMesh ? 0.5 : 1, color: palette.blue, background: palette.primarySoft, border: `1px solid ${palette.blue}`, borderRadius: 6, padding: "3px 10px" }}
          >
            {refineBusy ? "refining…" : refineResult ? `2nd-order Cₗ ${refineResult.cl.toFixed(3)}` : "↑ Refine 2nd-order (CPU)"}
          </button>
        ) : null}
      </div>
      {refineOn && refineNote ? <span style={{ ...mono11, color: palette.ink3 }}>{refineNote}</span> : null}
      <div style={{ display: "flex", gap: 6 }}>
        <button
          type="button"
          style={{ ...buttonBase, flex: 1, borderColor: palette.line, background: palette.surface2, color: palette.ink }}
          disabled={solving || queueLoading}
          onClick={() => void proposeQueue(queueN)}
          title="Ask Tailor which points to run next, and why — review the proposals under Targets"
        >
          {queueLoading ? "…" : `Propose next ${queueN}`}
        </button>
        <span style={{ ...mono11, color: palette.ink3, display: "inline-flex", alignItems: "center", gap: 4 }}>
          N
          <button type="button" style={stepBtn} disabled={queueLoading || queueN <= QUEUE_N_MIN} onClick={() => setQueueN((n) => Math.max(QUEUE_N_MIN, n - 1))}>−</button>
          <span style={{ minWidth: 14, textAlign: "center" }}>{queueN}</span>
          <button type="button" style={stepBtn} disabled={queueLoading || queueN >= QUEUE_N_MAX} onClick={() => setQueueN((n) => Math.min(QUEUE_N_MAX, n + 1))}>+</button>
        </span>
      </div>
      {solveNote ? <span style={{ ...mono11, color: solveState === "error" ? palette.warn : palette.ink3 }}>{solveNote}</span> : null}
      {queue.length > 0 ? (
        // The EDITABLE queue lives once, under Targets — this used to be a
        // full duplicate (same state, different chrome, ~26 extra controls in
        // the busiest inspector). One line + a jump is the whole affordance.
        <span style={{ ...mono11, color: palette.ink3 }}>
          Tailor queue ({queue.length}) — review under Targets below
        </span>
      ) : null}
    </div>
  );

  // ONE merged axis table (FXC-9009 phase 5, acceptance 13): six axes, each
  // hold | sweep(steps) | range(min,max). The CTA switches Grid-batch vs
  // Tailor-campaign by whether any axis is "range" — the structurally-identical
  // SweepPanel/FillPanel twins (and their Grid|Tailor-fill toggle) are gone.
  // Always mounted, CSS-gated by the inspector's stage rule (draft preservation).
  const axisTableBody = (
    <AxisTable
      palette={palette}
      live={sweepLive}
      busy={solving || campaignBusy}
      localSolveAvailable={localSolveAvailable}
      campaign={campaign}
      note={campaignNote ?? campaignFetchNote}
      onSubmitGrid={(points) => void runSolve(points, "Nothing to sweep.")}
      onStartCampaign={(spec) => void startCampaign(spec)}
      onControlCampaign={(action) => void controlCampaign(action)}
    />
  );


  // 3D trust surface panel — the fitted, confidence-weighted surface over the
  // selected axes (ghostly where extrapolating/in conflict). Reuses TrustSurfacePanel;
  // its own /composite/surface query refetches when projectId/branch/axes/fixed change,
  // and key=trustNonce forces a refetch after a solve. Quantity is lower-cased to the
  // canonical schema name ('cl'): unlike the highlift trust-map/recs wrappers,
  // fetchSurface does NOT lowercase, so passing "CL" returns an empty surface.
  const trust3dPanelBody = persistedProject ? (
    <div style={{ height: 420 }}>
      <TrustSurfacePanel
        refreshToken={trustNonce}
        projectId={projectId}
        branch={branch}
        quantity={HIGHLIFT_QUANTITY.toLowerCase()}
        axisX={trustAxisX}
        axisY={trustAxisY}
        fixed={debouncedTrustFixed}
        axisOptions={TRUST_AXIS_OPTIONS}
        onAxisXChange={setTrustAxisX}
        onAxisYChange={setTrustAxisY}
        fill
      />
    </div>
  ) : (
    <div style={{ ...muted12, padding: "14px 12px", fontFamily: MONO_STACK, fontSize: 11 }}>
      trust surface available once the project is saved
    </div>
  );

  // Geometry lineage panel — seeded on the live baseline geometry node so its
  // derived_from variant family (parent ← baseline → variants) is the centred
  // family, not the run-spine. Compact (no fullSurface) for the ~316px right dock.

  // Polars panel — Cₗ–α lift curve + drag polar over the solved run nodes.


  // The hero (foil over the LIC flow + HUD + drag handles) — the dock layout's centre in Design mode.
  const hero = (
    <div
      ref={stageWrapRef}
      style={{ position: "relative", flex: 1, minHeight: 0, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", padding: 12 }}
    >
      {/* The flow field and the foil share ONE responsive stage so their identical world→pixel
          mappings overlay pixel-for-pixel: LIC field behind, the transparent geometry canvas on top,
          both framed to FOIL_VIEW via the foilView letterbox. */}
      <div style={{ position: "relative", width: stageW, height: stageH }}>
        {/* Two-body (exact) seed mode: the parametric canvas + its flow field dim to a ghost and
            go pointer-inert (dragging invisible handles would silently edit the parametric buffer)
            while the read-only exact contours render on top in the SAME letterboxed frame. */}
        <div style={{ position: "absolute", inset: 0, clipPath: fieldClip ?? undefined, WebkitClipPath: fieldClip ?? undefined, opacity: twoBodyActive ? 0.15 : 1 }}>
          <FlowVisLayer flow={flowMesh} width={stageW} height={stageH} view={foilView} />
        </div>
        <div style={{ position: "relative", zIndex: 1, opacity: twoBodyActive ? 0.15 : 1, pointerEvents: twoBodyActive ? "none" : undefined }}>
          <FoilGeometryCanvas
            ref={heroCanvasRef}
            config={config}
            configuration={configuration}
            showAnnotations={showAnnotations}
            showLabels={showLabels}
            view={foilView}
            onViewChange={setFoilView}
            onSetControlPoint={setHandleDescriptor}
            selectedElementId={selectedId}
            onSelectElement={setSelectedId}
            onHoverHandle={onHoverHandle}
            onFieldClip={setFieldClip}
            width={stageW}
            height={stageH}
          />
        </div>
        {/* Per-element force arrows — additive overlay in the SAME foilView frame.
            Renders nothing until per-element forces are present (honest absent). */}
        <HighliftForceArrows forces={elementForces} view={foilView} width={stageW} height={stageH} />
        {/* Two-body (exact) seed mode: the seed's exact cut contours + slot health, read-only. */}
        {twoBodyActive && twoBody && csSeed ? (
          <CsTwoBodyOverlay body={twoBody} section={csSeed.section} view={foilView} width={stageW} height={stageH} palette={palette} />
        ) : null}
        {convPlay ? (
          <div style={{ position: "absolute", inset: 0, zIndex: 5 }}>
            <ConvergencePlayer width={stageW} height={stageH} />
            <button type="button" onClick={() => setConvPlay(false)} title="Close convergence player"
              style={{ ...mono11, position: "absolute", top: 8, left: "50%", transform: "translateX(-50%)", zIndex: 6, cursor: "pointer", color: palette.ink, background: `color-mix(in srgb, ${palette.surface} 85%, transparent)`, border: `1px solid ${palette.line}`, borderRadius: 6, padding: "3px 10px" }}>✕ close convergence</button>
          </div>
        ) : null}
        {/* HUD cluster 1 (top-left): ONE view kebab — fill / zoom-reset /
            annotations / labels / hover-readout fold inside (design principle 4:
            one control per action, power one click away). The NN-field,
            watch-converge and warm-start toggles moved to the SOLVE panel —
            they are solve-fidelity concerns, not view concerns (§5.1). */}
        <details className="hlw-viewkebab" data-testid="hlw-view-kebab" style={{ position: "absolute", top: 12, left: 12, zIndex: 2 }}>
          <summary title="View options" aria-label="View options" style={{ ...mono11 }}>⋮ view</summary>
          <div className="hlw-overflow-menu" style={{ left: 0, right: "auto" }}>
            <button type="button" className="hlw-overflow-row" onClick={fillView} title="Frame + centre the foil">⤢ fill view</button>
            <button type="button" className="hlw-overflow-row" disabled={!viewMoved} onClick={() => setFoilView(FOIL_VIEW)} title="Reset zoom + pan">
              {viewMoved ? `${zoomLevel(foilView).toFixed(zoomLevel(foilView) < 1 ? 2 : 1)}× · reset zoom` : "zoom at home"}
            </button>
            <button type="button" className="hlw-overflow-row" onClick={() => setShowAnnotations((v) => !v)} title="Show/hide annotations (grid, control nets, hinges, gap overlay, handles)">
              {showAnnotations ? "✓ annotations" : "annotations"}
            </button>
            <button type="button" className="hlw-overflow-row" disabled={!showAnnotations} onClick={() => setShowLabels((v) => !v)} title="Show/hide text labels (control-point ids, gap metrics, axes, hint)">
              {showLabels ? "✓ labels" : "labels"}
            </button>
            <button type="button" className="hlw-overflow-row" onClick={() => setShowHoverReadout((v) => !v)} title="Show the hovered control point's coordinates in a corner pill">
              {showHoverReadout ? "✓ hover read-out" : "hover read-out"}
            </button>
          </div>
        </details>
        {/* HUD: top-right live coefficient cards */}
        <div style={{ position: "absolute", top: 12, right: 12, zIndex: 2, display: "flex", gap: 7, pointerEvents: "none" }}>
          {[
            { label: "Cₗ", value: markerCl != null ? markerCl.toFixed(3) : lastCoeff ? lastCoeff.cl.toFixed(3) : "—", accent: palette.primary },
            { label: "L / D", value: lastCoeff?.ld != null ? lastCoeff.ld.toFixed(1) : "—", accent: palette.ink },
            { label: "Cₔ", value: lastCoeff?.cd != null ? lastCoeff.cd.toFixed(4) : "—", accent: palette.ink },
          ].map((c) => (
            <div key={c.label} style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", background: palette.surface, border: `1px solid ${palette.line}`, borderRadius: 8, padding: "6px 11px" }}>
              <span style={{ fontFamily: MONO_STACK, fontSize: 19, fontWeight: 500, lineHeight: 1, color: c.accent }}>{c.value}</span>
              <span style={{ fontSize: 9, letterSpacing: "0.08em", textTransform: "uppercase", color: palette.ink4 }}>{c.label}</span>
            </div>
          ))}
          {/* (The dev-only 2nd-order refine card moved into the SOLVE panel —
              a solve-fidelity concern, not hero chrome; §5.1 two-cluster rule.) */}
        </div>
        {/* Optional bottom-left "what am I dragging" read-out (AC-H) — opt-in
            via the view kebab; hidden by default (two-cluster hero, §5.1). */}
        {showHoverReadout ? (
          <div style={{ position: "absolute", bottom: 12, left: 12, zIndex: 2, display: "flex", alignItems: "center", gap: 8, background: palette.surface, border: `1px solid ${palette.line}`, borderRadius: 999, padding: "5px 13px", pointerEvents: "none" }}>
            <span style={{ color: palette.blue }}>✛</span>
            <span style={{ fontFamily: MONO_STACK, fontSize: 11, color: palette.ink2 }}>{hoverReadout}</span>
          </div>
        ) : null}
        {showSolveOverlay ? <SolveProgressOverlay progress={solveProgress} markerCl={markerCl} sparkHistory={sparkHistory} palette={palette} /> : null}
        {replayingFill && replayEntry ? (
          <FillReplayOverlay
            step={replayEntry.step}
            index={replayEntry.index}
            total={replayEntry.total}
            warm={replayWarmOn}
            palette={palette}
          />
        ) : null}
        {/* (The N-D fill map moved to its steady-state home — the Explore
            board's campaign lane — so a LIVE campaign shows it too, not just a
            manual replay; the replay cursor still drives it there. One FillMap
            render site total, per acceptance 3.) */}
        {flowHint ? (
          <div style={{ position: "absolute", left: 8, bottom: 48, zIndex: 2, ...mono11, color: flowHintIsError ? palette.warn : palette.ink3, background: palette.surface2, border: `1px solid ${flowHintIsError ? palette.warn : palette.line}`, borderRadius: 6, padding: "3px 8px", pointerEvents: "none" }}>
            {flowHint}
          </div>
        ) : null}
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // REDESIGN IA render tree (stage spine). All zones are fed by the scaffold
  // The retired dock/board data definitions (dataTiles / designPanels /
  // panelHomes / BOARD_KEY) are DELETED (layout restructure step 1): they were
  // dead-by-render but still executed their bodies every render.
  void projectName; // destructure shape preserved; header dropped in the redesign IA

  // SPINE: the conditions chips (read-only from the live flow conditions + α + Cμ).
  const cmu = blowingOf(config).length ? config.operation.diskLoading : 0;
  const spine = (
    <div className="hlw-spine" data-tour="hlw-spine">
      <div className="hlw-spine-steps">
        {HLW_STAGES.map((s, i) => (
          <span key={s.id} style={{ display: "inline-flex", alignItems: "center" }}>
            {i > 0 ? <span className="hlw-step-sep">›</span> : null}
            <button type="button" className="hlw-step" data-on={stage === s.id ? "1" : "0"} onClick={() => setStage(s.id)} title={`${s.label} — ${s.sub}`}>
              <span className="ico"><StepIcon paths={s.icon} /></span>
              {s.label}
              <span className="hlw-step-sub">{s.sub}</span>
            </button>
          </span>
        ))}
      </div>
      <div className="hlw-spine-cond">
        {/* FXC-9009: always-visible Solve (primary action) — tinker + check from ANY stage, not just
            the Flow inspector. Leads the condition row so it's never clipped. Warm-started per the toggle. */}
        <button
          type="button"
          data-tour="hlw-solve"
          onClick={handleSolveThisPoint}
          disabled={solving || !persistedProject}
          title={!persistedProject ? "Open a saved project to solve" : warmStartOn ? "Solve this configuration (NN warm-started, ~2.5–3× fewer steps)" : "Solve this configuration (cold)"}
          style={{ marginRight: 8, padding: "4px 14px", borderRadius: 7, fontWeight: 600, fontSize: 12, whiteSpace: "nowrap", cursor: solving || !persistedProject ? "default" : "pointer", border: `1px solid ${palette.primary}`, background: solving ? palette.surface : palette.primary, color: solving ? palette.ink3 : palette.onPrimary, opacity: !persistedProject ? 0.5 : 1 }}
        >
          {solving ? "solving…" : "▶ Solve"}
        </button>
        {/* (The single replay-fill control lives on the Explore board header —
            §5.7: one replay control total, beside the story it replays.) */}
        {/* Mobile toolbar (≤900px only, §5.9): summon the rail / inspector
            sheets. Glyph labels (review fix: 'panel' pushed the toolbar half
            off-screen at 390px). */}
        <button type="button" className="hlw-mtool" data-testid="hlw-mtool-rail" onClick={() => openMobileSheet("rail")} title="Section elements" aria-label="Open the section rail">
          ☰
        </button>
        <button type="button" className="hlw-mtool" data-testid="hlw-mtool-insp" onClick={() => openMobileSheet("insp")} title="Inspector panel" aria-label="Open the inspector">
          ▤
        </button>
        <div className="hlw-cond"><b>α</b><span className="v">{alphaDeg.toFixed(1)}°</span></div>
        <div className="hlw-cond"><b>M</b><span className="v">{flowConditions.mach.toFixed(2)}</span></div>
        <div className="hlw-cond"><b>Re</b><span className="v">{(flowConditions.reynolds / 1e6).toFixed(1)}e6</span></div>
        <div className="hlw-cond"><b>Cμ</b><span className="v">{cmu.toFixed(3)}</span></div>
        {/* modified-dot: the ONLY always-visible buffer signal (the full XFOIL
            buffer↔baseline status lives in the ⋯ menu + the Library fold). */}
        <span
          className="hlw-moddot"
          data-dirty={isDirty ? "1" : "0"}
          title={isDirty ? `Buffer reshaped — diverged from ${baselineLabel} (see ⋯ or Library).` : baseline.source === "seed" ? "Buffer matches the default reference geometry." : `Buffer matches “${baselineLabel}”.`}
          aria-label={isDirty ? "geometry modified" : "geometry unmodified"}
        />
        {/* ⋯ overflow — every utility that used to be its own spine chip
            (design principle 2: power one click away, never always-visible). */}
        <details className="hlw-overflow" data-testid="hlw-spine-overflow">
          <summary aria-label="Workbench utilities" title="Workbench utilities">⋯</summary>
          <div className="hlw-overflow-menu">
            {/* Narrow screens hide the spine condition chips — the summary moves
                in here (§5.9: the spine stays one row on mobile). */}
            <div className="hlw-overflow-row hlw-overflow-cond" data-static="1">
              α {alphaDeg.toFixed(1)}° · M {flowConditions.mach.toFixed(2)} · Re {(flowConditions.reynolds / 1e6).toFixed(1)}e6 · Cμ {cmu.toFixed(3)}
              {isDirty ? " · modified" : ""}
            </div>
            {/* Exactly three Explore presets + last-used (§5.2 / acceptance 8);
                the current lanes persist as last-used automatically. */}
            <div className="hlw-overflow-row" data-static="1" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: ".07em" }}>
              Explore presets
            </div>
            {EXPLORE_PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                className="hlw-overflow-row"
                data-testid={`hlw-preset-${p.id}`}
                onClick={(e) => {
                  handleExploreLanes([...p.lanes]);
                  setStage("explore");
                  // a preset is a navigation — close the menu behind it
                  const menu = (e.currentTarget as HTMLElement).closest("details");
                  if (menu) (menu as HTMLDetailsElement).open = false;
                }}
              >
                {p.label}
              </button>
            ))}
            <div className="hlw-overflow-row" data-static="1" style={{ borderTop: `1px solid ${palette.line}`, borderRadius: 0, marginTop: 2, paddingTop: 8 }} />
            <div className="hlw-overflow-row" data-static="1" title={isDirty ? `reshaped — diverged from ${baselineLabel}` : `current: ${baselineLabel}`}>
              <i style={{ width: 7, height: 7, borderRadius: 7, background: isDirty ? palette.amber : palette.classOk, display: "inline-block", flex: "0 0 auto" }} />
              {isDirty ? `modified · #${bufferFingerprint}` : `${baseline.source === "seed" ? "default" : baselineLabel} · #${bufferFingerprint}`}
            </div>
            <button type="button" className="hlw-overflow-row" onClick={() => setLocked((v) => !v)} title="Freeze the canvas drag">
              {locked ? "🔒 locked — unlock canvas" : "🔓 lock canvas drag"}
            </button>
            <button
              type="button"
              className="hlw-overflow-row"
              onClick={toggleTheme}
              aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            >
              {theme === "dark" ? "☀ switch to light mode" : "☾ switch to dark mode"}
            </button>
            <div className="hlw-overflow-row" data-static="1" title={registerNote ?? undefined} style={{ color: registerState === "error" ? palette.warn : undefined }}>
              {registerLabel}
            </div>
            {/* Tutorial launch — drives the self-contained driver.js tour (see ./highlift/tour). */}
            <TutorialButton onStart={startTutorial} />
          </div>
        </details>
      </div>
    </div>
  );

  // RAIL: the model tree (rig/flow/push/trust) OR the span ruler (span). The tree
  // reuses the existing elements/blowing collection logic from elementsPanelBody.
  // The Aircraft/Stations placeholder scaffold is DELETED (acceptance 13): no
  // spanwise station model exists yet, so the rail shows only what is real —
  // the section's elements, one add-element menu, and the Replace-foil picker.
  const railTree = (
    <div className="hlw-rail-tree">
      <div className="hlw-rail-h">Section elements</div>
      {elements.map((el) => (
        <div key={el.id} className="hlw-tnode" data-on={selectedId === el.id ? "1" : "0"} onClick={() => setSelectedId(el.id)}>
          <span className="dot" style={{ background: elementSwatch(el.kind, palette) }} />
          <span style={{ flex: 1 }}>{el.label}</span>
          {isDefaultCollectionId(el.id) ? null : (
            <button type="button" style={ghostBtn} title="remove element" onClick={(e) => { e.stopPropagation(); if (selectedId === el.id) setSelectedId("main"); removeCollectionItem(el.id); }}>×</button>
          )}
        </div>
      ))}
      {blowing.map((b) => (
        <div key={b.id} className="hlw-tnode" data-on={selectedId === b.id ? "1" : "0"} onClick={() => setSelectedId(b.id)}>
          <span className="dot" style={{ borderRadius: 8, background: palette.blue }} />
          <span style={{ flex: 1 }}>{b.label}</span>
          {isDefaultCollectionId(b.id) ? null : (
            <button type="button" style={ghostBtn} title="remove blowing source" onClick={(e) => { e.stopPropagation(); if (selectedId === b.id) setSelectedId("main"); removeCollectionItem(b.id); }}>×</button>
          )}
        </div>
      ))}
      {/* Replace the MAIN element's base airfoil — search the bundled Flexfoil/UIUC catalog (~1665),
          enter a NACA 4-digit code, or upload a .dat. The whole section re-derives off the new foil.
          Uses the csSeed-clearing setters: a replaced foil is no longer the aircraft CS cut. */}
      <FoilPicker config={config} setters={foilPickerSetters} palette={palette} />
      {/* ONE "+ add element" menu replaces the 5-button row (§5.1: the rail is
          Section elements + one add menu + Replace-foil). Save/Trace-lineage
          live in the Library fold only (acceptance 3: one lineage entry). */}
      <details className="hlw-addel-menu" data-tour="hlw-add-cove">
        <summary className="hlw-addel" style={{ marginTop: 6 }}>+ add element</summary>
        <div className="hlw-overflow-menu" style={{ position: "static", marginTop: 4 }}>
          <button type="button" className="hlw-overflow-row" onClick={addVane}>+ foil (vane)</button>
          <button type="button" className="hlw-overflow-row" onClick={addPlainFlap}>+ flap</button>
          <button type="button" className="hlw-overflow-row" onClick={addSingleSlottedFlap}>+ slotted flap (simple, no vane)</button>
          <button type="button" className="hlw-overflow-row" onClick={addSlottedFlap}>+ slotted flap (vane + flaperon)</button>
          <button type="button" className="hlw-overflow-row" onClick={addSlat}>+ slat</button>
          <button type="button" className="hlw-overflow-row" onClick={addKrueger}>+ Krueger</button>
          <button type="button" className="hlw-overflow-row" onClick={addBlowing}>+ blowing</button>
        </div>
      </details>
    </div>
  );
  // SPAN RAIL ruler (NET-NEW read-only scaffold over the single-section geometry —
  // no spanwise station model exists yet; the η stations are derived root/sel/tip
  // entries and the insert buttons are placeholders this phase).
  // TODO(highlift-studio): wire to a real spanwise station model when one exists.

  // HERO Cp strip (NET-NEW; honest empty state when the flow mesh carries no Cp).
  const cpStrip = deriveCpStrip(flowMesh);

  // INSPECTOR header helper.
  const ipHeader = (t: string, s: string) => (
    <div className="hlw-ip-h"><div className="t">{t}</div><div className="s">{s}</div></div>
  );

  // The Tailor queue block: the ONE review/edit home for proposed points.
  // Proposing lives on the Solve panel (acceptance 3: one propose-queue) —
  // this fold only reviews, edits and runs what was proposed.
  const tailorQueueBody = (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div className="hlw-goal">
        Review/edit Tailor's proposed points, then solve the queue. Propose from
        the Solve panel above.
      </div>
      {queue.length > 0 ? (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 220, overflow: "auto" }}>
            {queue.map((row) => (
              <div key={row.id} className="hlw-qrow" style={{ marginBottom: 0 }}>
                <span className="gh" style={{ border: row.status === "done" ? "none" : undefined, background: row.status === "done" ? palette.primary : undefined }} />
                <label style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>α<input type="number" step="0.5" value={row.alpha_deg} disabled={solving} onChange={(e) => patchQueuePoint(row.id, "alpha_deg", Number(e.target.value))} style={qInput} /></label>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>flap<input type="number" step="1" value={row.flap_deploy_deg} disabled={solving} onChange={(e) => patchQueuePoint(row.id, "flap_deploy_deg", Number(e.target.value))} style={qInput} /></label>
                <span style={{ color: palette.ink3, fontSize: 9 }}>{row.score.toFixed(2)}</span>
                <span className="x" title={row.reasons.join(" · ")}>{row.note ? row.note : row.purpose}</span>
                <button type="button" style={ghostBtn} disabled={solving} title="remove" onClick={() => removeFromQueue(row.id)}>×</button>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button type="button" className="hlw-btn full" style={{ flex: 1 }} disabled={solving || queueLoading} onClick={() => void runQueue()}>
              {solving ? "solving…" : `Solve queue (${queue.length})`}
            </button>
            <button type="button" style={ghostBtn} disabled={solving} onClick={clearQueue}>clear</button>
          </div>
        </>
      ) : null}
    </div>
  );

  // INSPECTOR: per-stage right panel (reuses the existing memoized panel bodies).
  // FXC-9009 phase 2: per-knob fixed/free + limits. Free knobs are the Sweep axes + Inverse
  // variables; fixed knobs are held. applyAxes enforces these (drops fixed, clamps bounded).
  const knobConstraintsBody = (
    <div>
      <div style={{ ...mono11, color: palette.ink3, marginBottom: 6 }}>
        Hold a knob fixed or leave it free, and set its limits. Free knobs become the Sweep axes + Inverse variables.
      </div>
      {/* Pure-geometry flap-deployment optimizer (FXC-9009): no NN/backend, minimises clearance
          across the full range of motion + first-principles slot-shape criteria (converging slot,
          reattachment-favorable nose) over whichever Operation/Geometry knobs above are free. */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "2px 0 10px" }}>
        <button
          type="button"
          style={{ ...stepBtn, background: geomOptRunning ? palette.surface2 : palette.primarySoft, color: palette.primary, flex: "0 0 auto" }}
          disabled={geomOptRunning}
          title="Optimize the free deploy/flaperon/hinge/nose/cove knobs above for slot clearance + shape (pure geometry, no flow solve)"
          onClick={onOptimizeGeometry}
        >
          {geomOptRunning ? "optimizing…" : "⚙ optimize deployment"}
        </button>
        {geomOptNote ? <span style={{ ...mono11, color: palette.ink3, overflow: "hidden", textOverflow: "ellipsis" }}>{geomOptNote}</span> : null}
      </div>
      {KNOB_GROUPS.map((g) => {
        const defs = KNOB_REGISTRY.filter((k) => k.group === g);
        if (!defs.length) return null;
        return (
          <div key={g} style={{ marginBottom: 8 }}>
            <div style={{ ...mono11, color: palette.ink, fontWeight: 600, margin: "6px 0 3px" }}>{g}</div>
            {defs.map((def) => {
              const c = config.constraints?.[def.key];
              const fixed = c?.fixed ?? false;
              const [lo, hi] = knobBounds(def, c);
              const raw = def.read({ alphaDeg, flowConditions, config });
              const isRe = def.key === "reynolds";
              const scale = isRe ? 1e6 : 1;
              const shown = isRe ? `${(raw / 1e6).toFixed(1)}e6` : `${raw.toFixed(def.decimals)}${def.unit && def.unit !== "Re" ? def.unit : ""}`;
              return (
                <div key={def.key} style={{ marginBottom: 7 }}>
                  <div style={{ ...mono11, color: palette.ink, display: "flex", justifyContent: "space-between", gap: 8 }} title={def.key}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{def.label}</span>
                    <span style={{ color: palette.ink3, flexShrink: 0 }}>{shown}{isRe ? "" : ""}</span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "auto minmax(0,1fr) minmax(0,1fr)", gap: 5, alignItems: "center", marginTop: 2 }}>
                    <button type="button" onClick={() => setKnobConstraint(def.key, { fixed: !fixed })}
                      title={fixed ? "Held — click to free" : "Free — click to hold"}
                      style={{ ...mono11, cursor: "pointer", padding: "2px 10px", borderRadius: 5, border: `1px solid ${fixed ? palette.amber : palette.primary}`, background: fixed ? palette.surface : palette.primarySoft, color: fixed ? palette.amber : palette.primary }}>
                      {fixed ? "fixed" : "free"}
                    </button>
                    <input type="number" value={lo / scale} step={isRe ? 1 : def.step} disabled={fixed}
                      onChange={(e) => setKnobConstraint(def.key, { min: parseKnobLimit(e.target.value, scale) })}
                      title={`min${isRe ? " (×10⁶)" : ""}`} style={{ ...mono11, opacity: fixed ? 0.4 : 1 }} />
                    <input type="number" value={hi / scale} step={isRe ? 1 : def.step} disabled={fixed}
                      onChange={(e) => setKnobConstraint(def.key, { max: parseKnobLimit(e.target.value, scale) })}
                      title={`max${isRe ? " (×10⁶)" : ""}`} style={{ ...mono11, opacity: fixed ? 0.4 : 1 }} />
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );

  const inspector = (
    <div className="hlw-insp">
      {/* collapse / re-expand the inspector (persisted). Absolutely pinned so it
          rides whichever stage panel header is showing. */}
      <button
        type="button"
        className="hlw-collapse hlw-collapse-insp"
        onClick={() => setLayout((cur) => ({ ...cur, inspCollapsed: !cur.inspCollapsed }))}
        title={layout.inspCollapsed ? "Expand the inspector" : "Collapse the inspector"}
        aria-label={layout.inspCollapsed ? "Expand the inspector" : "Collapse the inspector"}
      >
        {layout.inspCollapsed ? "‹" : "›"}
      </button>
      {/* ALWAYS mounted, CSS-gated — including across inspector COLLAPSE
          (review fix: collapse used to unmount AxisTable/inverse-design/
          targets, wiping in-progress drafts despite the workbench's own
          draft-preservation rule). data-insp-collapsed hides .hlw-insp-body. */}
      <div className="hlw-insp-body">
      {/* Always mounted, CSS-gated (draft preservation): the AxisTable /
          InverseDesignSection hold unsubmitted drafts in local state — a stage
          hop must not wipe a half-configured campaign box. The heavy panels
          that motivated per-stage mounting left with the retired stages. */}
      <div className="hlw-ip hlw-ip-rig">
        {ipHeader("Section · 2D editor", `η 0.78 · ${isDirty ? "modified" : baseline.source === "seed" ? "default" : baselineLabel}`)}
        <div className="hlw-sec">
          <div className="hlw-sec-t">Rigging</div>
          {riggingTableBody}
        </div>
        {/* Progressive disclosure (layout quick-win): Rigging is the primary
            instrument; the rest opens on demand — the inspector stops being a
            wall of every control at once. Native <details>, no state. */}
        <details className="hlw-sec hlw-sec-fold">
          <summary className="hlw-sec-t">Knobs · fixed / free / limits</summary>
          {knobConstraintsBody}
        </details>
        <details className="hlw-sec hlw-sec-fold">
          <summary className="hlw-sec-t">Shape · selected element</summary>
          {/* Droop-nose LE morph — a shape concern, so it lives in the Shape
              fold (the slim rail is elements + add + Replace-foil only, §5.1). */}
          <DroopNoseControl config={config} setters={setters} palette={palette} mono={mono11} />
          {shapePanelBody}
        </details>
        <details className="hlw-sec hlw-sec-fold">
          <summary className="hlw-sec-t">Library · save / load / lineage</summary>
          {libraryPanelBody}
        </details>
      </div>
      <div className="hlw-ip hlw-ip-explore">
        {ipHeader("Explore the flow", `α ${alphaDeg.toFixed(1)}° · M ${flowConditions.mach.toFixed(2)} · RANS`)}
        <div className="hlw-sec">{solvePanelBody}</div>
        <div className="hlw-sec">
          <div className="hlw-sec-t">Axes · hold | sweep | range</div>
          {axisTableBody}
        </div>
        {/* Geometry-breadth trust surface (FXC-9009): the fitted, confidence-
            weighted 3D surface with the x/y axis SELECTOR — any canonical
            composite axis, incl. a per-element geometry shape DOF, can be put
            on an axis to re-project the surface (and, via trustAxisX/Y, the
            header trust chips' map). Folded like its siblings; always mounted,
            CSS-gated (the persistent WebGL renderer must not remount). */}
        <details className="hlw-sec hlw-sec-fold">
          <summary className="hlw-sec-t">Trust surface · 3D{trustAxisX !== "alpha_deg" || trustAxisY !== "flap_deploy_deg" ? ` (${trustAxisX} × ${trustAxisY})` : ""}</summary>
          {trust3dPanelBody}
        </details>
        {/* The old Push stage, folded in: the queue and the inverse-design
            targets always drove the same solve backend this section fronts. */}
        <details className="hlw-sec hlw-sec-fold">
          <summary className="hlw-sec-t">Targets · Tailor queue{queue.length > 0 ? ` (${queue.length})` : ""}</summary>
          {tailorQueueBody}
        </details>
        <details className="hlw-sec hlw-sec-fold">
          <summary className="hlw-sec-t">Inverse design · target Cp + ranges (NN-Newton)</summary>
          {/* DESIGN §8: drive the design-opt spine from a target-Cp curve / scalar objective over the
              ranged DOFs. surfaceCp + the loops no-op honestly until an NN is bound (setNN seam). */}
          <InverseDesignSection
            config={config}
            ranges={ranges}
            setRange={setRange}
            clearRange={clearRange}
            surfaceCp={surfaceCp}
            hasNN={hasNN}
            runInverseDesign={runInverseDesign}
            runOptimize={runOptimize}
            runGapTarget={runGapTarget}
            gapMetricOutputs={gapMetricOutputs}
          />
        </details>
      </div>
      </div>
    </div>
  );

  // TRUST STRIP RETIRED (acceptance 13): the class-mix chips + last-CL fold
  // into the Explore board header (the hero gets the 66px row back); the
  // "database →" escape hatch became the board's "open full ADB →" and the
  // per-point provenance handoff is the header's "lineage →".
  const ladderCl = (() => {
    const ranss = polarSeries.find((s) => s.label === "RANS")?.points;
    const cl = ranss && ranss.length ? ranss[ranss.length - 1]!.CL : (lastCoeff?.cl ?? null);
    return cl != null ? cl.toFixed(2) : "—";
  })();
  const trustHeaderExtra = (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }} data-testid="hlw-trust-chips">
      {total > 0
        ? CLASS_ORDER.filter((cls) => mix[cls] > 0).map((cls) => (
            <span key={cls} className="hlw-fchip" style={{ borderColor: classColorMap[cls], color: classColorMap[cls] }} title={`${mix[cls]} of ${total} trust-map cells`}>
              <span className="hlw-fdot" style={{ background: classColorMap[cls] }} />
              {CLASS_LABELS[cls]} {Math.round((mix[cls] / total) * 100)}%
            </span>
          ))
        : trustError
          ? <span style={{ ...mono11, color: palette.warn }} title={trustError}>trust map unavailable</span>
          : <span style={{ ...mono11, color: palette.ink4 }}>{trustLoading ? "sampling trust…" : "no trust cells yet"}</span>}
      <span style={{ ...mono11, color: palette.ink3 }} title={solveNote ?? undefined}>
        last CL {ladderCl}
      </span>
      <button
        type="button"
        className="hlw-cond"
        style={{ cursor: "pointer" }}
        onClick={() => onOpenView("lineage")}
        title="Trace this point's provenance in the Trust surface"
      >
        lineage →
      </button>
    </span>
  );
  // The board header's full extra row: THE one replay-fill control (§5.7) + the
  // autoplay/replay progress readout (point k/N · coverage) + the trust chips.
  const exploreHeaderExtra = (
    <>
      {replayingFill || replaySteps.length > 0 ? (
        <button
          type="button"
          className="hlw-cond"
          data-testid="hlw-replay-fill"
          onClick={onReplayButtonClick}
          title="Replay the Tailor fill campaign chronologically — watch the sampler explore the box point by point"
          style={{ cursor: "pointer", borderColor: replayingFill ? palette.primary : undefined, color: replayingFill ? palette.primary : undefined }}
        >
          {replayingFill ? "■ stop replay" : "▶ replay fill"}
        </button>
      ) : null}
      {replayingFill && replayEntry ? (
        <span
          className="hlw-cond"
          data-testid="hlw-autoplay-progress"
          style={{ borderColor: palette.primary, color: palette.primary }}
        >
          point {replayEntry.index + 1}/{replayEntry.total}
          {replayCoverage != null ? ` · coverage ${(replayCoverage * 100).toFixed(0)}%` : ""}
          {replayEntry.step.CL != null ? ` · CL ${replayEntry.step.CL.toFixed(2)}` : ""}
        </span>
      ) : null}
      {trustHeaderExtra}
    </>
  );
  // The autoplay caption ticker (§5.7): ONE overlay narrating each step from
  // the data the queue rows already carry — reasons/purpose VERBATIM, never
  // invented. Renders only while the armed autoplay is actually replaying;
  // any interaction dismisses it (the capture-phase listener above).
  const autoplayTicker =
    autoplayOn && replayingFill && replayEntry ? (
      <div className="hlw-xb-ticker" data-testid="hlw-autoplay-ticker" aria-live="polite">
        <b>
          Tailor picked α {replayEntry.step.alphaDeg.toFixed(1)}°, flap {replayEntry.step.deployDeg.toFixed(0)}°
        </b>
        {replayEntry.step.reason ? <span className="why">{replayEntry.step.reason}</span> : null}
        <span className="out">
          {replayEntry.step.failed
            ? "failed — budget spent honestly"
            : replayEntry.step.CL != null
              ? `solved: CL ${replayEntry.step.CL.toFixed(2)} — surface re-fit${replayEntry.step.cached ? " · cached, free" : ""}`
              : "solving…"}
        </span>
      </div>
    ) : null;

  // ── The "why" terminal (FXC-9009): the LLM-narrated running log, the richer
  // sibling of the caption ticker. The active point is the replay step while a
  // replay narrates, else the study cursor's selected point mapped back to its
  // campaign ReplayStep (so live clicks + solves narrate too).
  const [whyOpen, setWhyOpen] = useState(false);
  const whyMobile = useMediaQuery("(max-width: 560px)");
  // Autoplay auto-opens the terminal so the story has its log; a manual toggle
  // by the user afterwards wins (we only force-open on the autoplay transition).
  const wasReplayingRef = useRef(false);
  useEffect(() => {
    if (replayingFill && autoplayOn && !wasReplayingRef.current) setWhyOpen(true);
    wasReplayingRef.current = replayingFill;
  }, [replayingFill, autoplayOn]);
  const activeWhyPoint = useMemo<WhyPoint | null>(() => {
    // Prior iteration's coverage_after = this point's coverage-before.
    const coverageBefore = (step: ReplayStep): number | null => {
      const its = campaign?.state?.iterations ?? [];
      let prev: number | null = null;
      for (const it of its) {
        if (it.index >= step.iterationIndex) break;
        if (typeof it.coverage_after === "number" && Number.isFinite(it.coverage_after)) {
          prev = it.coverage_after;
        }
      }
      return prev;
    };
    if (replayingFill && replayEntry) {
      return { step: replayEntry.step, coverageBefore: coverageBefore(replayEntry.step) };
    }
    // Live: map the selected study point back to a campaign step by condition.
    const sel = selectedStudyPoint;
    if (!sel) return null;
    const near = (a: number | undefined, b: number, tol = 1e-3) =>
      typeof a === "number" && Math.abs(a - b) <= tol;
    const match = replaySteps.find(
      (s) =>
        near(sel.inputs.alpha_deg, s.alphaDeg, 0.05) &&
        near(sel.inputs.flap_deploy_deg, s.deployDeg, 0.05),
    );
    if (!match) return null;
    return { step: match, coverageBefore: coverageBefore(match) };
  }, [replayingFill, replayEntry, selectedStudyPoint, replaySteps, campaign]);
  const whyTerminal =
    replaySteps.length > 0 || activeWhyPoint ? (
      <WhyTerminal
        active={activeWhyPoint}
        open={whyOpen}
        onToggle={setWhyOpen}
        compact={whyMobile}
      />
    ) : null;

  // HERO: persistent geometry-over-LIC (the existing `hero`) for rig/flow/push/trust,
  // the REAL section loft for span, the contextual dock row, and the corner loft preview.
  // When the Explore board has the geometry card promoted, the hero renders inside
  // its LANE instead of here — exactly one canvas instance ever exists, so the
  // imperative previewDeploy channel always targets the visible canvas.
  const geometryInExplore = stage === "explore" && exploreLanes.includes("geometry");
  const heroZone = (
    <div className="hlw-hero">
      {/* the live 2D section (geometry over flow + HUD) — shown rig/flow/push/trust via CSS */}
      <div className="hlw-big hlw-big-2d">
        <div className="hlw-big-h">
          Section geometry · η 0.78
          {stage === "rig" ? (
            <span style={{ display: "inline-flex", gap: 4, marginLeft: 10 }}>
              {(["2d", "3d"] as const).map((v) => (
                <button key={v} type="button" onClick={() => setHeroView(v)} style={{ fontSize: 10, padding: "1px 8px", borderRadius: 999, cursor: "pointer", border: `1px solid ${heroView === v ? palette.primary : palette.line}`, background: heroView === v ? palette.primarySoft : palette.surface2, color: heroView === v ? palette.primary : palette.ink3 }}>
                  {v.toUpperCase()}
                </button>
              ))}
            </span>
          ) : null}
        </div>
        <div className="hlw-big-body">{geometryInExplore ? null : hero}</div>
      </div>
      {/* the span/loft 3D preview — the REAL loft of the current section
          (root == tip, straight-extruded via buildWingShell + manifold boolean). */}
      {stage === "rig" && heroView === "3d" ? (
      <div className="hlw-big hlw-big-loft">
        <div className="hlw-big-h">Lofted wing · on aircraft<span className="r">current section · constant-section loft</span></div>
        <div className="hlw-big-body" style={{ display: "flex", alignItems: "stretch", justifyContent: "stretch" }}>
          <HlwSectionLoft config={config} palette={palette} />
        </div>
      </div>
      ) : null}
      {/* horizontal splitter between the big canvas and the dock row (var-driven
          --hlw-hero-big). Hidden in the span stage where the dock row is gone. */}
      <button
        type="button"
        className="hlw-rsz hlw-rsz-hero v5-resize-bar is-horizontal"
        aria-label="Resize the canvas and the dock row"
        title="Drag to resize the canvas and dock row · double-click to reset"
        onPointerDown={(e) => beginHighliftResize(e, "hero")}
        onDoubleClick={() => resetHighliftResize("hero")}
      />
      {/* contextual bottom dock row (Cp / slot / polar / trust) + corner loft preview */}
      <div className="hlw-dockrow">
        <div className="hlw-dock">
          {/* RIG: slot health — LIVE per-slot gap off gapMetrics.slots (was hardcoded placeholder
              bars/percentages that never reflected the actual geometry; FXC-9009). The bar shows the
              slot's FULL RANGE OF MOTION envelope (motionGap's per-slot min/max gap swept across the
              deploy + flaperon limits — debounced, so it lags a fast drag by ~250ms rather than
              re-sweeping every frame) as a translucent span, plus a solid marker at the slot's CURRENT
              gap. Colour flags range-of-motion health from motionGap (collision/choked), matching the
              Rigging table's own slot-health colouring so the two views agree. Hover a bar for exact
              min/current/max. Visual span is a fixed 0-8%c window (typical high-lift throats). */}
          <div className="hlw-dock-slot">
            {/* Calm header: no hint caption, no replay duplicate — the SPINE owns
                replay (acceptance 3); "animate deploy" lives in the tile BODY. */}
            <div className="hlw-dock-h">Slot health</div>
            <div className="hlw-dock-b" style={{ padding: "14px 16px" }}>
              {gapMetrics.slots.length === 0 ? (
                <div style={{ fontSize: 11.5, color: palette.ink4 }}>No slots — add a device to form one.</div>
              ) : (
                gapMetrics.slots.map((slot) => {
                  const label = elements.find((e) => e.id === slot.downstreamId)?.label ?? slot.downstreamId;
                  const gapPct = slot.metrics.gap * 100;
                  const pctOf = (g: number): number => Math.max(0, Math.min(100, (g / 0.08) * 100));
                  const curPct = pctOf(slot.metrics.gap);
                  const mSlot = motionGap.slots.find((s) => s.downstreamId === slot.downstreamId);
                  const color = mSlot?.collides ? palette.warn : mSlot?.choked ? palette.amber : palette.primary;
                  const minPct = mSlot ? pctOf(mSlot.minGap) : curPct;
                  const maxPct = mSlot ? pctOf(mSlot.maxGap) : curPct;
                  const title = mSlot
                    ? `current ${gapPct.toFixed(2)}%c · range ${(mSlot.minGap * 100).toFixed(2)}–${(mSlot.maxGap * 100).toFixed(2)}%c across deploy ${motionGap.deployRange[0].toFixed(0)}–${motionGap.deployRange[1].toFixed(0)}° / flaperon ${motionGap.flaperonRange[0].toFixed(0)}–${motionGap.flaperonRange[1].toFixed(0)}°`
                    : `current ${gapPct.toFixed(2)}%c`;
                  return (
                    <div
                      key={slot.downstreamId}
                      ref={(node) => {
                        if (node) slotHealthRowRefs.current.set(slot.downstreamId, node);
                        else slotHealthRowRefs.current.delete(slot.downstreamId);
                      }}
                      className="hlw-barrow"
                      title={title}
                    >
                      <div className="nm"><span className="dot" style={{ background: color }} />{label}</div>
                      <div className="hlw-bartrk">
                        <div
                          className="hlw-barfill"
                          style={{ left: `${minPct}%`, width: `${Math.max(0, maxPct - minPct)}%`, background: color, opacity: 0.4 }}
                        />
                        <div className="hlw-barmark" style={{ left: `${curPct}%`, background: palette.ink }} />
                      </div>
                      <div className="pv">{gapPct.toFixed(1)}%</div>
                    </div>
                  );
                })
              )}
              <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 10 }}>
                <button
                  type="button"
                  style={{
                    ...stepBtn,
                    background: animatingDeploy ? palette.primarySoft : palette.surface2,
                    color: animatingDeploy ? palette.primary : palette.ink,
                  }}
                  title={`Sweep ${motionAxis.axis === "flaperon" ? "flaperon deflection" : "flap deploy"} across ${motionAxis.range[0].toFixed(0)}–${motionAxis.range[1].toFixed(0)}° so the range bars visibly move`}
                  onClick={toggleAnimateDeploy}
                >
                  {animatingDeploy ? "■ stop" : "▶ animate deploy"}
                </button>
                <span style={{ fontSize: 11.5, color: palette.ink4 }}>
                  shaded span = full range of motion · marker = current (drag the canvas handles).
                </span>
              </div>
            </div>
          </div>
        </div>
        {/* (The always-on corner 3D-loft preview is RETIRED — review fix: the
            Design default is EXACTLY four elements, and the loft already has a
            first-class home one click away behind the hero's 2D|3D toggle.) */}
      </div>
      {/* ── Explore room: focus + filmstrip (FXC-9009 two-rooms hero inversion).
          The response surface board is the Explore hero, UNGATED from campaigns —
          it renders from /composite/surface whenever the project has data; a
          running campaign animates it. The old Cp/Projection/Polar dock tiles
          are filmstrip cards now; the live section canvas above stays mounted
          (hidden) so drag/replay state survives, and returns as the hero when
          the locked geometry card is promoted. */}
      {stage === "explore" ? (
        <ExploreBoard
          projectId={projectId}
          branch={branch}
          persistedProject={persistedProject}
          palette={palette}
          campaign={campaign}
          replaySteps={replaySteps}
          live={sweepLive}
          queue={queue}
          polarSeries={polarSeries}
          clMax={clMax}
          stallAlpha={stallAlpha}
          cpStrip={cpStrip}
          configuration={configuration}
          lanes={exploreLanes}
          onLanesChange={handleExploreLanes}
          geometryHero={geometryInExplore ? hero : undefined}
          onOpenAdb={() => onOpenView("tailor")}
          headerExtra={exploreHeaderExtra}
          overlay={autoplayTicker}
          whyTerminal={whyTerminal}
          pulseKey={replayingFill && replayEntry ? replayEntry.index : campaign?.state?.completed ?? null}
          replayCursor={replayingFill && replayEntry ? replayEntry.index : null}
          resolveRunPath={resolveRunPath}
        />
      ) : null}
    </div>
  );

  return (
    <main
      className="v5-main"
      style={{ display: "flex", flexDirection: "column", minHeight: 0, height: "100%" }}
    >
      <style>{`
        .v5-main input[type=range]{-webkit-appearance:none;appearance:none;height:3px;border-radius:3px;background:${palette.lineStrong};outline:none}
        .v5-main input[type=range]::-webkit-slider-thumb{-webkit-appearance:none;width:13px;height:13px;border-radius:50%;background:${palette.primary};cursor:pointer;border:2px solid ${palette.surface}}
        .v5-main input[type=range]::-moz-range-thumb{width:13px;height:13px;border-radius:50%;background:${palette.primary};cursor:pointer;border:none}
      `}</style>
      {/* data-stage drives the CSS stage machine (which big/dock/inspector shows);
          data-layout fixed to "a"; data-theme follows the global V5 theme. */}
      <div
        ref={wbRef}
        className="hlw-wb"
        data-stage={stage}
        data-explore-hero={exploreLanes[0] ?? "cl"}
        data-explore-lanes={exploreLanes.length}
        data-hero-view={heroView}
        data-layout="a"
        data-theme={theme}
        data-rail-collapsed={layout.railCollapsed ? "1" : "0"}
        data-insp-collapsed={layout.inspCollapsed ? "1" : "0"}
        data-mobile-sheet={mobileSheet ?? "none"}
        style={layoutStyle}
      >
        {spine}
        {/* mobile sheet backdrop (≤900px): tap outside to close (§5.9) */}
        {mobileSheet ? (
          <button
            type="button"
            className="hlw-sheet-backdrop"
            aria-label="Close the panel"
            data-testid="hlw-sheet-backdrop"
            onClick={() => setMobileSheet(null)}
          />
        ) : null}
        {/* trust strip row RETIRED — its chips live on the Explore board header */}
        <div className="hlw-rail">
          {/* collapse / re-expand the left rail (persisted) */}
          <button
            type="button"
            className="hlw-collapse hlw-collapse-rail"
            onClick={() => setLayout((cur) => ({ ...cur, railCollapsed: !cur.railCollapsed }))}
            title={layout.railCollapsed ? "Expand the model rail" : "Collapse the model rail"}
            aria-label={layout.railCollapsed ? "Expand the model rail" : "Collapse the model rail"}
          >
            {layout.railCollapsed ? "›" : "‹"}
          </button>
          {layout.railCollapsed ? null : railTree}
        </div>
        {heroZone}
        {inspector}
        {/* Pointer-driven column splitters — absolutely positioned over the grid
            seams (left:var(--hlw-rail-w) / right:var(--hlw-insp-w)), spanning the
            middle row. Hidden for a collapsed column. Reuses the .v5-resize-bar look. */}
        {layout.railCollapsed ? null : (
          <button
            type="button"
            className="hlw-rsz hlw-rsz-rail v5-resize-bar is-vertical"
            style={{ left: "var(--hlw-rail-w)" }}
            aria-label="Resize the model rail"
            title="Drag to resize the model rail · double-click to reset"
            onPointerDown={(e) => beginHighliftResize(e, "rail")}
            onDoubleClick={() => resetHighliftResize("rail")}
          />
        )}
        {layout.inspCollapsed ? null : (
          <button
            type="button"
            className="hlw-rsz hlw-rsz-insp v5-resize-bar is-vertical"
            style={{ right: "var(--hlw-insp-w)" }}
            aria-label="Resize the inspector"
            title="Drag to resize the inspector · double-click to reset"
            onPointerDown={(e) => beginHighliftResize(e, "insp")}
            onDoubleClick={() => resetHighliftResize("insp")}
          />
        )}
      </div>
    </main>
  );
}
